import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import RichEditor from './RichEditor';
import ToolBar from './ToolBar';
import ReadOnlyToolBar from './ReadOnlyToolBar';
import EditorPlaceholder from './EditorPlaceholder';
import { loadDocContent, saveDocContent, updateDocMetadata } from '../../helpers/docHelpers';
import { computeDocFingerprint } from '../../api/docs';
import { createContentSignature, getVersions, getVersionContent, createVersion } from '../../helpers/versionHelpers';
import { createTab } from '../../helpers/tabHelpers';
import config from '../../config';
import { removeChunkAttributes } from '../../helpers/chunkHelpers';
import { handleEditorDrop as helperHandleEditorDrop, clearInsertIndicators, restoreHiddenIndicators } from '../../helpers/dragHelpers';
import { debug } from '../../helpers/debug';
import { isEditorContentEmpty } from '../../helpers/contentGuards';
import { isValidEditorJson, cacheDocState, normalizeEditorContent, loadDocForEditor } from '../../helpers/editorHelpers';
import '../../styles/global.css';
import { attachEditorDomHandlers, detachEditorDomHandlers } from '../../helpers/editorHelpers';
import { createImage } from '../../helpers/imageHelpers';
import { apiGetImagesForDoc, apiMarkImageDeleted } from '../../api/images';
import { apiGetNextRequestKeyForDoc, apiInsertChatRequest, apiInsertChatRequestAttempt, apiSetChatRequestFeedback } from '../../api/chatRequests';
import { apiAiChatCompletions } from '../../api/ai';
import { computeDocumentStats, computeOffsetFromStats, normalizeDocumentStats } from '../../helpers/statHelpers';
import { notifyToast } from '../../helpers/notificationHelpers';
import { getPreference } from '../../helpers/preferencesHelpers';
import { findItem } from '../../helpers/mapKey';
import { renameDoc } from '../../helpers/docHelpers';
import { renameChat } from '../../helpers/chatHelpers';
import Icon from '../Icon';
import {
  buildAiStageCompletionSummary,
  buildAiStageStatusText,
  buildChatComposerNextJson,
  buildLinkedDocChipOptions,
  countChatMessagesInDocJson,
  replaceChatMessageTextByTimestamp,
  resolveLinkedDocOptionCandidate,
  shouldPinChatToBottom
} from '../../helpers/chatHelpers';
import { applyAiWriteOperationsToDocJson, applyAiWriteSelectionFallbackToDocJson } from '../../helpers/aiWriteHelpers';
import { generateAssistantResponse } from '../../helpers/ai';
import { getAiModelById, getAiTaskKeyForChatMode, getChatOrchestrationConfig, getDefaultModelForTask, getFallbackModelsForTask, getResolvedModelForTask } from '../../helpers/ai/aiConfigRuntimeHelpers';
import { extractAssistantTextFromOpenRouter } from '../../helpers/ai/aiResponseHelpers';
import {
  buildChatLinkedDocIds,
  buildChatSystemPrompt,
  buildStageSummaryFromEvent,
  classifyAiChatError,
  extractExploreArtifact,
  getWriteModeFallbackAssistantText,
  normalizeChatMode,
  parseAiWriteOperationsEnvelope,
  requestChatRenameSuggestion
} from '../../helpers/ai/aiChatHelpers';
import {
  extractHeadingRenameCandidate,
  getDefaultRenameMode,
  getRenameModePreferenceKey,
  isUntitledDefaultName,
  normalizeRenameMode,
  sanitizeSuggestedRename,
  shouldApplySuggestedRename
} from '../../helpers/renameHelpers';
import {
  createSuggestionIdentity,
  createSuggestionMarkAttrs,
  resolveSuggestionByAnnotationId,
  resolveSuggestionByBatchId
} from '../../helpers/suggestionHelpers';
import useTableContextMenu from '../Overlays/useTableContextMenu';
import ContextMenuTables from '../Overlays/contextMenuTables';
import ChatComposer from './ChatComposer';

const LAST_SELECTION_CONTEXT_WINDOW_KEY = '__ghostLastSelectionContext';
const DOC_CONTENT_UPDATED_EVENT = 'ghost-doc-content-updated';

const normalizeDiffModeValue = (input) => {
  const normalized = String(input || '').trim().toLowerCase();
  return normalized === 'suggest' ? 'suggest' : 'write';
};

const getDefaultDiffModeValue = () => {
  try {
    return normalizeDiffModeValue(getPreference(null, 'editorDiffMode', 'write'));
  } catch (err) {
    return 'write';
  }
};

export const shouldSelectSuggestionRange = (focusMode) => String(focusMode || '').trim().toLowerCase() === 'range';

export const getInlineSuggestionAnnotationIdsToResolve = (inlineBar = {}) => {
  return Array.from(new Set([
    String(inlineBar?.annotationId || '').trim(),
    ...(Array.isArray(inlineBar?.linkedAnnotationIds)
      ? inlineBar.linkedAnnotationIds.map((id) => String(id || '').trim())
      : [])
  ].filter(Boolean)));
};

export const buildWriteFallbackReplacementContent = (writeOps = []) => {
  const candidates = (Array.isArray(writeOps) ? writeOps : [])
    .map((op) => String(op?.new_content || '').trim())
    .filter(Boolean);

  if (candidates.length === 0) return '';
  if (candidates.length === 1) return candidates[0];
  return candidates.join('\n\n').trim();
};

const SELECTION_QUICK_ACTIONS = {
  rephrase: {
    label: 'Rephrase',
    maxTokens: 220,
    temperature: 0.35,
    useSurroundingContext: true,
    minRatio: null,
    maxRatio: null,
    targetRatio: null
  },
  shorten: {
    label: 'Shorten',
    maxTokens: 180,
    temperature: 0.35,
    useSurroundingContext: false,
    minRatio: null,
    maxRatio: null,
    targetRatio: 0.75
  },
  elaborate: {
    label: 'Expand',
    maxTokens: 280,
    temperature: 0.3,
    useSurroundingContext: true,
    minRatio: null,
    maxRatio: 2.6,
    targetRatio: 2.4
  },
  spelling: {
    label: 'Spelling',
    maxTokens: 170,
    temperature: 0.1,
    useSurroundingContext: false,
    minRatio: null,
    maxRatio: null,
    targetRatio: null
  }
};

export const getSelectionQuickActionConfig = (action = 'rephrase') => {
  const normalized = String(action || '').trim().toLowerCase();
  return SELECTION_QUICK_ACTIONS[normalized] || SELECTION_QUICK_ACTIONS.rephrase;
};

const FALLBACK_SELECTION_QUICK_ACTION_MODEL = 'openai/gpt-4o-mini';
const isOpenAiModelId = (modelId = '') => String(modelId || '').trim().toLowerCase().startsWith('openai/');

export const resolveSelectionQuickActionOutputModel = () => {
  const candidates = [
    getDefaultModelForTask('chat.write', 'response'),
    getDefaultModelForTask('chat.explore', 'response'),
    getDefaultModelForTask('chat.write'),
    getDefaultModelForTask('chat.explore'),
    FALLBACK_SELECTION_QUICK_ACTION_MODEL
  ]
    .map((id) => String(id || '').trim())
    .filter(Boolean)
    .map((id) => getAiModelById(id)?.id || id);

  const preferredOpenAi = candidates.find((id) => isOpenAiModelId(id));
  if (preferredOpenAi) return preferredOpenAi;
  return candidates[0] || FALLBACK_SELECTION_QUICK_ACTION_MODEL;
};

export const buildSelectionQuickActionPrompt = ({ action = 'rephrase', selectedText = '', surroundingContext = '' } = {}) => {
  const normalizedAction = String(action || '').trim().toLowerCase();
  const selected = String(selectedText || '').trim();
  const context = String(surroundingContext || '').trim();
  const config = getSelectionQuickActionConfig(normalizedAction);

  const baseInstructionByAction = {
    rephrase: 'Rewrite the selected text for clarity while preserving meaning, tone, and formatting intent. Preserve the same voice/persona. Replace the selected text entirely.',
    shorten: 'Produce a much shorter rewrite of the selected text while preserving the original meaning, tone, and key facts. Keep the same voice/persona and punch. Replace the selected text entirely; do not append.',
    elaborate: 'Produce a much longer rewrite of the selected text with richer detail and continuity, but do not add unrelated facts. Keep the same voice/persona. Replace the selected text entirely; do not append.',
    spelling: 'Correct spelling and obvious grammar mistakes in the selected text without changing intent.'
  };

  const lines = [
    baseInstructionByAction[normalizedAction] || baseInstructionByAction.rephrase,
    'Return ONLY the replacement text for the selected sample.',
    'Start directly with revised text.',
    'Do not include explanations, labels/headings, JSON, markdown fences, or quotation wrappers.',
    'Never start with lead-ins such as "Here is", "Here\'s", or "Concise version:".',
    'Preserve the original tone and persona. If the original is edgy or punchy, keep that edge.'
  ];

  const selectedLength = Math.max(1, selected.length);
  if (Number.isFinite(Number(config.targetRatio)) && Number(config.targetRatio) > 0) {
    const targetChars = Math.max(1, Math.round(selectedLength * Number(config.targetRatio)));
    lines.push(`Target length: around ${targetChars} characters (${Math.round(Number(config.targetRatio) * 100)}% of selected length).`);
  }
  if (Number.isFinite(Number(config.maxRatio)) && Number(config.maxRatio) > 0) {
    const maxChars = Math.max(1, Math.round(selectedLength * Number(config.maxRatio)));
    lines.push(`Hard max length: ${maxChars} characters (${Math.round(Number(config.maxRatio) * 100)}% of selected length).`);
  }
  if (Number.isFinite(Number(config.minRatio)) && Number(config.minRatio) > 0) {
    const minChars = Math.max(1, Math.round(selectedLength * Number(config.minRatio)));
    lines.push(`Minimum length: ${minChars} characters (${Math.round(Number(config.minRatio) * 100)}% of selected length).`);
  }

  if (config.useSurroundingContext && context) {
    lines.push('', `Surrounding context (for continuity only):\n${context}`);
  }

  lines.push('', `Selected text:\n${selected}`);
  return lines.join('\n').trim();
};

export const normalizeSelectionQuickActionResponse = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const fencedMatch = raw.match(/```(?:text|markdown)?\s*([\s\S]*?)```/i);
  const unfenced = fencedMatch?.[1] ? String(fencedMatch[1]).trim() : raw;
  const singleWrapped = unfenced.match(/^"([\s\S]+)"$/);
  const unquoted = singleWrapped?.[1] ? String(singleWrapped[1]).trim() : unfenced;

  const lines = unquoted
    .split(/\r?\n/)
    .map((line) => String(line || '').trimEnd());

  while (lines.length > 1) {
    const first = String(lines[0] || '').trim();
    if (!first) {
      lines.shift();
      continue;
    }
    const leadInPattern = /^(?:here(?:'s| is)\b|(?:a\s+)?(?:concise|shorter|longer|expanded|elaborated|rephrased|revised|corrected)\s+version\b|(?:quick\s+)?(?:rewrite|revision|correction)\b)/i;
    const leadInWithColonPattern = /(?:version|rewrite|revision|correction)\s*:\s*$/i;
    if (!leadInPattern.test(first) && !leadInWithColonPattern.test(first)) {
      break;
    }
    lines.shift();
  }

  return lines.join('\n').trim();
};

const isSelectionQuickActionNoOp = ({ selectedText = '', replacementText = '' } = {}) => {
  const selected = String(selectedText || '').trim();
  const replacement = String(replacementText || '').trim();
  if (!selected || !replacement) return true;
  return selected === replacement;
};

export const trimTextToWordBoundary = (value = '', maxChars = 0) => {
  const text = String(value || '').trim();
  const cap = Math.max(1, Number(maxChars || 0) || 0);
  if (!text) return '';
  if (text.length <= cap) return text;

  const sliced = text.slice(0, cap).trimEnd();
  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > 24) {
    return sliced.slice(0, lastSpace).trimEnd();
  }
  return sliced;
};

export const trimTextToSentenceBoundary = (value = '', maxChars = 0, minChars = 0) => {
  const text = String(value || '').trim();
  const cap = Math.max(1, Number(maxChars || 0) || 0);
  const minCap = Math.max(0, Number(minChars || 0) || 0);
  if (!text) return '';
  if (text.length <= cap) return text;

  const sliced = text.slice(0, cap).trimEnd();
  let lastSentenceEnd = -1;
  const sentenceRegex = /[.!?](?=(?:["')\]]|\s|$))/g;
  let match = sentenceRegex.exec(sliced);
  while (match) {
    const endIndex = Number(match.index || 0) + 1;
    if (endIndex >= minCap) {
      lastSentenceEnd = endIndex;
    }
    match = sentenceRegex.exec(sliced);
  }

  if (lastSentenceEnd > 0) {
    return sliced.slice(0, lastSentenceEnd).trimEnd();
  }

  return trimTextToWordBoundary(sliced, cap);
};

export const cleanTrailingFragment = (value = '') => {
  let text = String(value || '').trim();
  if (!text) return '';

  const danglingWords = new Set([
    'and', 'or', 'but', 'so', 'because', 'that', 'which', 'who', 'whom', 'whose',
    'to', 'for', 'with', 'of', 'in', 'on', 'at', 'by', 'from', 'as', 'if', 'when',
    'while', 'than', 'then'
  ]);

  while (text) {
    const lastWordMatch = text.match(/([A-Za-z]+)\W*$/);
    const lastWord = String(lastWordMatch?.[1] || '').trim().toLowerCase();
    if (!lastWord || !danglingWords.has(lastWord)) break;
    text = text.replace(/\s*[A-Za-z]+\W*$/, '').trimEnd();
  }

  text = text.replace(/[\s,:;-]+$/, '').trimEnd();
  if (!text) return '';

  if (!/[.!?](?:["')\]]+)?$/.test(text)) {
    text = `${text}.`;
  }

  return text;
};

export const shouldTreatSelectionAsWholeParent = ({
  from = 0,
  to = 0,
  parentStart = 0,
  parentEnd = 0,
  selectedText = '',
  parentText = '',
  sameParentSelection = false
} = {}) => {
  if (!sameParentSelection) return false;

  const exactBoundaryMatch = Number(from || 0) === Number(parentStart || 0)
    && Number(to || 0) === Number(parentEnd || 0);
  if (exactBoundaryMatch) return true;

  const normalizedSelectedText = String(selectedText || '').replace(/\s+/g, ' ').trim();
  const normalizedParentText = String(parentText || '').replace(/\s+/g, ' ').trim();
  return Boolean(normalizedSelectedText) && Boolean(normalizedParentText) && normalizedSelectedText === normalizedParentText;
};

export const computeSuggestionScrollTopForViewportRatio = ({
  currentScrollTop = 0,
  containerTop = 0,
  containerHeight = 0,
  nodeTop = 0,
  ratio = 0.25
} = {}) => {
  const normalizedRatio = Math.max(0.05, Math.min(0.95, Number(ratio || 0.25) || 0.25));
  const desiredTop = Number(containerTop || 0) + (Number(containerHeight || 0) * normalizedRatio);
  const delta = Number(nodeTop || 0) - desiredTop;
  if (!Number.isFinite(delta)) return Number(currentScrollTop || 0) || 0;
  return (Number(currentScrollTop || 0) || 0) + delta;
};

export const shouldAutoFocusChatSuggestion = ({ preferredAnnotationId = null, entries = [] } = {}) => {
  const annotationId = String(preferredAnnotationId || '').trim();
  if (!annotationId) return true;

  const rows = Array.isArray(entries) ? entries : [];
  return rows.some((entry) => String(entry?.annotationId || '').trim() === annotationId);
};

export const buildMarkedInlineNodes = ({ schema, mark, text = '' } = {}) => {
  const normalized = String(text || '').replace(/\r\n?/g, '\n');
  const lines = normalized.split('\n');
  const hardBreakType = schema?.nodes?.hardBreak;
  const nodes = [];

  lines.forEach((line, index) => {
    const chunk = String(line || '');
    if (chunk) {
      nodes.push(schema.text(chunk, [mark]));
    }
    if (index < lines.length - 1) {
      if (hardBreakType) {
        nodes.push(hardBreakType.create());
      } else {
        nodes.push(schema.text(' ', [mark]));
      }
    }
  });

  if (nodes.length === 0) {
    nodes.push(schema.text(' ', [mark]));
  }

  return nodes;
};

export const insertMarkedReplacement = ({ tr, state, from, to, replacementText = '', markType, markAttrs } = {}) => {
  const replacement = String(replacementText || '').trim();
  const mark = markType.create(markAttrs || {});
  const paragraphType = state?.schema?.nodes?.paragraph;
  const headingType = state?.schema?.nodes?.heading;
  const selectionFrom = state?.selection?.$from || null;
  const selectionTo = state?.selection?.$to || null;
  const selectionSpansBlocks = Boolean(selectionFrom && selectionTo)
    && selectionFrom.parent !== selectionTo.parent;
  const sameParentSelection = Boolean(selectionFrom && selectionTo)
    && selectionFrom.sameParent(selectionTo);
  const parentNode = sameParentSelection ? selectionFrom.parent : null;
  const selectsWholeParentNode = shouldTreatSelectionAsWholeParent({
    from,
    to,
    parentStart: Number(selectionFrom?.start?.() || 0),
    parentEnd: Number(selectionFrom?.end?.() || 0),
    selectedText: String(state?.doc?.textBetween?.(from, to, ' ', ' ') || ''),
    parentText: String(parentNode?.textContent || ''),
    sameParentSelection
  });
  const insertBelowHeading = Boolean(paragraphType)
    && selectsWholeParentNode
    && String(parentNode?.type?.name || '').trim().toLowerCase() === 'heading';
  const blocks = replacement
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((chunk) => String(chunk || '').trim())
    .filter(Boolean);

  const rawInsertPos = insertBelowHeading
    ? Number(selectionFrom?.after?.(selectionFrom?.depth) || to || 0)
    : Number(to || 0);
  const insertPos = tr.mapping.map(rawInsertPos, 1);
  if (paragraphType && (insertBelowHeading || (selectionSpansBlocks && blocks.length > 1))) {
    let nextPos = insertPos;
    blocks.forEach((block) => {
      const inlineNodes = buildMarkedInlineNodes({
        schema: state.schema,
        mark,
        text: block
      });
      const nextNode = insertBelowHeading && headingType
        ? headingType.create(parentNode?.attrs || null, inlineNodes)
        : paragraphType.create(null, inlineNodes);
      tr.insert(nextPos, nextNode);
      nextPos += nextNode.nodeSize;
    });
    return;
  }

  const replacementNode = state.schema.text(replacement, [mark]);
  tr.insert(insertPos, replacementNode);
};

export const coerceSelectionQuickActionLength = ({ action = '', selectedText = '', replacementText = '' } = {}) => {
  const config = getSelectionQuickActionConfig(action);
  const normalizedAction = String(action || '').trim().toLowerCase();
  const selectedLen = Math.max(1, String(selectedText || '').trim().length);
  let replacement = String(replacementText || '').trim();
  let replacementLen = Math.max(0, replacement.length);
  const ratio = replacementLen / selectedLen;

  if (normalizedAction === 'shorten' && replacementLen >= selectedLen) {
    const targetChars = Math.max(1, Math.floor(selectedLen * 0.75));
    const shortened = trimTextToWordBoundary(replacement, targetChars);
    if (shortened && shortened.length < selectedLen) {
      return {
        ok: true,
        replacementText: shortened,
        toast: ''
      };
    }
    return {
      ok: false,
      replacementText: replacement,
      toast: 'Unable to apply this edit. Try again.'
    };
  }

  if (normalizedAction === 'elaborate' && replacementLen <= selectedLen) {
    return {
      ok: false,
      replacementText: replacement,
      toast: 'Unable to apply this edit. Try again.'
    };
  }

  const maxRatio = Number(config?.maxRatio);
  if (normalizedAction === 'elaborate' && Number.isFinite(maxRatio) && maxRatio > 0 && ratio > maxRatio) {
    const maxChars = Math.max(selectedLen + 1, Math.floor(selectedLen * maxRatio));
    const minChars = Math.max(selectedLen + 1, Math.floor(selectedLen * 1.2));
    const trimmed = cleanTrailingFragment(trimTextToSentenceBoundary(replacement, maxChars, minChars));
    if (trimmed && trimmed.length > selectedLen) {
      return {
        ok: true,
        replacementText: trimmed,
        toast: ''
      };
    }
    return {
      ok: false,
      replacementText: replacement,
      toast: 'Unable to apply this edit. Try again.'
    };
  }

  return { ok: true, replacementText: replacement, toast: '' };
};

const buildQuickActionUsageMetrics = (response = null) => {
  const usagePayload = response?.usage && typeof response.usage === 'object' ? response.usage : {};
  const promptTokens = Number(usagePayload?.prompt_tokens || usagePayload?.promptTokens || 0) || 0;
  const completionTokens = Number(usagePayload?.completion_tokens || usagePayload?.completionTokens || 0) || 0;
  const totalTokens = Number(usagePayload?.total_tokens || usagePayload?.totalTokens || (promptTokens + completionTokens)) || 0;
  return {
    promptTokens,
    completionTokens,
    totalTokens
  };
};

const persistSelectionQuickActionRequestLog = async ({
  docId,
  requestKey,
  model,
  response,
  durationMs,
  projectId,
  userId,
  action,
  status,
  errorMessage = null
} = {}) => {
  const normalizedDocId = Number(docId || 0);
  const normalizedRequestKey = String(requestKey || '').trim();
  if (!normalizedDocId || !normalizedRequestKey) return;

  const normalizedStatus = String(status || 'success').trim().toLowerCase() || 'success';
  const attemptKey = `${normalizedRequestKey}_a1`;
  const responseModel = String(response?.model || model || '').trim() || null;
  const usage = buildQuickActionUsageMetrics(response);

  let requestLogged = false;
  try {
    await apiInsertChatRequest({
      chatDocId: normalizedDocId,
      requestKey: normalizedRequestKey,
      chatMode: 'write',
      intentKey: `selection_${String(action || '').trim().toLowerCase() || 'rephrase'}`,
      requestedModel: String(model || '').trim() || null,
      responseModel,
      usage,
      activeProjectId: Number(projectId || 0) || null,
      initiatorUserId: userId || null,
      attemptCount: 1,
      failedAttemptCount: normalizedStatus === 'success' ? 0 : 1,
      timeoutAttemptCount: 0,
      totalDurationMs: Math.max(0, Number(durationMs || 0) || 0),
      attemptSummary: {
        stats: {
          totalCalls: 1,
          successCalls: normalizedStatus === 'success' ? 1 : 0,
          failedCalls: normalizedStatus === 'success' ? 0 : 1,
          timeoutCalls: 0
        },
        attempts: [
          {
            attemptIndex: 1,
            stageName: 'selection_quick_action',
            status: normalizedStatus,
            candidateModel: String(model || '').trim() || null,
            responseModel,
            durationMs: Math.max(0, Number(durationMs || 0) || 0),
            totalTokens: Number(usage?.totalTokens || 0) || 0
          }
        ]
      },
      status: normalizedStatus,
      errorMessage: normalizedStatus === 'success' ? null : String(errorMessage || 'Selection quick action request failed').trim()
    });
    requestLogged = true;
  } catch (error) {
    debug('Selection quick action request log failed', error);
  }

  if (!requestLogged) return;

  try {
    await apiInsertChatRequestAttempt({
      requestHash: normalizedRequestKey,
      attemptHash: attemptKey,
      chatDocId: normalizedDocId,
      activeProjectId: Number(projectId || 0) || null,
      initiatorUserId: userId || null,
      attemptIndex: 1,
      stageName: 'selection_quick_action',
      callKind: 'response',
      providerName: 'openrouter',
      providerRequestId: String(response?.id || '').trim() || null,
      candidateModel: String(model || '').trim() || null,
      responseModel,
      transport: 'single',
      status: normalizedStatus,
      isTimeout: false,
      durationMs: Math.max(0, Number(durationMs || 0) || 0),
      usage,
      errorCode: normalizedStatus === 'success' ? null : 'SELECTION_QUICK_ACTION_FAILED',
      errorMessage: normalizedStatus === 'success' ? null : String(errorMessage || 'Selection quick action request failed').trim()
    });
  } catch (error) {
    debug('Selection quick action attempt log failed', error);
  }
};

const EditorPanel = forwardRef((props, ref) => {
  const CHAT_RESPONSE_TIMEOUT_MS = 60000;
  const {
    pane = 'left',
    className = '',
    tabs = [],
    allTabs = [],
    activeTabId = null,
    setActiveTabId = () => {},
    selectedDocId = null,
    tabsRestored = false,
    itemsMap = {},
    setTabs = () => {},
    setSplitScreen = () => {},
    setLeftActiveTabId = () => {},
    setRightActiveTabId = () => {},
    leftActiveTabId = null,
    rightActiveTabId = null,
    setActivePane = () => {},
    setItemsMap = () => {},
    draggedId = null,
    
    editorRef = null,
    loadTabsForEditor = null,
    currentProject = null,
    currentUserId = null,
    isProjectReadOnly = false,
    
    onEditorChange = () => {},
    dropInEditor = null,
    onOpenVersion,
    onRestoreVersion,
    onRestoreVersionCopy,
    onVersionCreated,
    onSaved
  } = props;

  const editorInstances = useRef({});
  const editorDomHandlers = useRef({});
  const editorImageHandlers = useRef({});
  const loadedDocsRef = useRef(new Set());
  const touchInfo = useRef({});
  const touchHandledAt = useRef({});
  const currentProjectIdRef = useRef(currentProject?.id ?? null);
  const editorScrollRef = useRef(null);
  const diffSuggestionFocusedKeyRef = useRef(null);
  const diffSuggestionNextFocusModeRef = useRef('none');
  const diffSuggestionInlineHideTimeoutRef = useRef(null);
  const diffSuggestionInlineHoverRef = useRef(false);

  const isPaneLoading = !tabsRestored;
  const setEditorInstanceFns = useRef({});
  const [, forceUpdate] = useState({});
  const [editorStates, setEditorStates] = useState({});
  const [chatRetryPayload, setChatRetryPayload] = useState(null);
  const [isRetryingChat, setIsRetryingChat] = useState(false);
  const [docRenameMode, setDocRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey(null), getDefaultRenameMode(null)), null);
    } catch (err) {
      return getDefaultRenameMode(null);
    }
  });
  const [chatRenameMode, setChatRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey('chat'), getDefaultRenameMode('chat')), 'chat');
    } catch (err) {
      return getDefaultRenameMode('chat');
    }
  });
  const [selectionActionBar, setSelectionActionBar] = useState({
    visible: false,
    left: 0,
    top: 0
  });
  const [selectionRegionIndicator, setSelectionRegionIndicator] = useState({
    visible: false,
    docId: null,
    left: 0,
    top: 0,
    height: 0
  });
  const [diffSuggestionDock, setDiffSuggestionDock] = useState({
    visible: false,
    docId: null,
    currentIndex: 0,
    entries: []
  });
  const [diffSuggestionInlineBar, setDiffSuggestionInlineBar] = useState({
    visible: false,
    left: 0,
    top: 0,
    docId: null,
    annotationId: null,
    batchId: null,
    linkedAnnotationIds: []
  });
  const [chatComposerOrchestration, setChatComposerOrchestration] = useState({});
  const {
    VERSIONING: {
      AUTO_VERSION_ON_IMAGE_CHANGE = false,
      AUTO_VERSION_MIN_OFFSET = 0,
      AUTO_VERSION_SAVE_INTERVAL = 1,
      AUTO_VERSION_TIME_INTERVAL_MS = 0
    } = {}
  } = config || {};
  const autoSaveTimeouts = useRef({});
  const metadataSaveTimeouts = useRef({});
  const savingInProgress = useRef({}); // Track in-progress saves per docId
  const lastSaveTime = useRef({}); // Track last save time per docId
  const previousActiveTabId = useRef(null);
  const previousTabRefreshId = useRef(null);
  const suppressNextEditorChangeByDoc = useRef({});
  const lastUserInteractionByDoc = useRef({});
  const lastEditInteractionMatchByDoc = useRef({});
  const lastVersionSignatureByDoc = useRef({});
  const autoVersionSaveChecksByDoc = useRef({});
  const autoVersionAnchorAtByDoc = useRef({});
  const autoVersionBaselineStatsByDoc = useRef({});
  const versionSaveInProgressByDoc = useRef({});
  const hasExistingVersionByDoc = useRef({});
  const lastSavedSignatureByDoc = useRef({});
  const versionExistenceCheckedByDoc = useRef({});
  const versionExistenceInFlightByDoc = useRef({});
  const activeVersionLoadInFlight = useRef({});
  const initialChatScrollDoneByDoc = useRef(new Set());
  const pendingLoadVerificationByDoc = useRef({});
  const lastSelectionContextRef = useRef(null);
  const activeLoadingToastRef = useRef(null);
  const lastChatMessageTimestampRef = useRef(0);
  const chatRequestInFlightByDoc = useRef({});
  const writeSummaryByRequestKeyRef = useRef({});
  const writeSummaryByTimestampRef = useRef({});
  const onOpenProjectDocRef = useRef(null);
  const activeRenamePromptDocIdRef = useRef(null);
  const dismissedRenameSuggestionsByDocRef = useRef(new Map());
  const activeTabObj = tabs.find(t => t.id === activeTabId);
  const activeEditorInstance = activeTabObj?.docId ? editorInstances.current[activeTabObj.docId] : null;
  const activeDocState = activeTabObj?.docId
    ? (editorStates[activeTabObj.docId] || editorStates[Number(activeTabObj.docId)])
    : null;
  const activeDiffMode = normalizeDiffModeValue(activeDocState?.diffMode || getDefaultDiffModeValue());
  const isActiveChatTab = activeTabObj?.docType === 'chat';
  const isActiveTabReadOnly = Boolean(activeTabObj?.readOnly) || isProjectReadOnly || String(activeTabObj?.docId || '').startsWith('version-');
  
  // Generate unique IDs based on pane
  const editorPanelId = `editorPanel${pane.charAt(0).toUpperCase() + pane.slice(1)}`;
  const toolbarId = `toolbar${pane.charAt(0).toUpperCase() + pane.slice(1)}`;
  const notifyPaneToast = useCallback((input, options = {}) => {
    const panePlacement = pane === 'right' ? 'right' : 'left';
    const baseWhere = options?.where && typeof options.where === 'object' ? options.where : {};
    const activeDocId = baseWhere.docId ?? activeTabObj?.docId ?? null;
    notifyToast(input, {
      ...options,
      where: {
        ...baseWhere,
        ...(activeDocId != null ? { docId: activeDocId, scope: baseWhere.scope || 'doc' } : {}),
        pane: baseWhere.pane || panePlacement
      }
    });
  }, [activeTabObj?.docId, pane]);

  const markUserInteraction = useCallback((rawDocId, interaction = {}) => {
    const docId = String(rawDocId || '');
    if (!docId) return;
    const at = Number(interaction?.at || Date.now());
    lastUserInteractionByDoc.current[docId] = {
      at: Number.isFinite(at) ? at : Date.now(),
      type: String(interaction?.type || 'unknown').trim() || 'unknown',
      key: String(interaction?.key || '').trim() || null,
      hasModifier: Boolean(interaction?.hasModifier)
    };
  }, []);

  const wasUserInteractionDetectedForDoc = useCallback((rawDocId) => {
    const docId = String(rawDocId || '');
    const interactionMatch = lastEditInteractionMatchByDoc.current[docId] || null;
    if (!interactionMatch) return true;
    return Boolean(interactionMatch.matched);
  }, []);

  const wasRecentEditInteractionMatchedForDoc = useCallback((rawDocId) => {
    const docId = String(rawDocId || '');
    const interactionMatch = lastEditInteractionMatchByDoc.current[docId] || null;
    return Boolean(interactionMatch?.matched);
  }, []);

  const getLoadingToastId = useCallback((docId) => `editor-load-${pane}-${String(docId || 'unknown')}`, [pane]);

  const showLoadingToastForDoc = useCallback((docId) => {
    if (!docId) return;
    const toastId = getLoadingToastId(docId);
    activeLoadingToastRef.current = { toastId, docId: String(docId) };
    notifyPaneToast({
      message: 'Loading...',
      level: 'info',
      timeoutMs: 0,
      toastId,
      where: {
        pane,
        docId
      }
    });
  }, [getLoadingToastId, notifyPaneToast, pane]);

  const dismissLoadingToastForDoc = useCallback((docId = null) => {
    const active = activeLoadingToastRef.current;
    const targetDocId = String(docId || active?.docId || '').trim();
    const toastId = targetDocId ? getLoadingToastId(targetDocId) : active?.toastId;
    if (!toastId) return;
    notifyPaneToast({ dismiss: true, toastId });
    if (!docId || String(active?.docId || '') === targetDocId) {
      activeLoadingToastRef.current = null;
    }
  }, [getLoadingToastId, notifyPaneToast]);

  // Table context menu hook (single instance per EditorPanel)
  const tableMenu = useTableContextMenu();
  const chatOrchestrationEnabled = Boolean(config?.AI?.CHAT_ORCHESTRATION?.ENABLED);
  const maxLinkedDocs = Number(
    config?.AI?.CHAT_ORCHESTRATION?.MAX_LINKED_DOCS
    || getChatOrchestrationConfig().retrieval?.limits?.maxLinkedDocs
    || 6
  );

  const linkedDocChipOptions = useMemo(() => {
    const sourceTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
    const oppositePaneActiveTabId = pane === 'right' ? leftActiveTabId : rightActiveTabId;
    return buildLinkedDocChipOptions({
      isActiveChatTab,
      activeDocId: activeTabObj?.docId,
      selectedDocId,
      sourceTabs,
      oppositePaneActiveTabId,
      itemsMap,
      maxLinkedDocs
    });
  }, [activeTabObj?.docId, allTabs, isActiveChatTab, itemsMap, leftActiveTabId, maxLinkedDocs, pane, rightActiveTabId, selectedDocId, tabs]);

  const hasTwoActiveChatTabs = useMemo(() => {
    const sourceTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
    if (!Array.isArray(sourceTabs) || sourceTabs.length === 0) return false;

    const leftActiveTab = sourceTabs.find((entry) => String(entry?.id || '') === String(leftActiveTabId || '')) || null;
    const rightActiveTab = sourceTabs.find((entry) => String(entry?.id || '') === String(rightActiveTabId || '')) || null;

    const activeChats = [leftActiveTab, rightActiveTab].filter((entry) => String(entry?.docType || '').trim().toLowerCase() === 'chat');
    return activeChats.length === 2;
  }, [allTabs, leftActiveTabId, rightActiveTabId, tabs]);

  const shouldShowInactiveChatChrome = isActiveChatTab && !hasTwoActiveChatTabs;
  const panelClassName = `editorPanel ${className} ${shouldShowInactiveChatChrome ? 'showInactiveChatChrome' : ''}`.trim();

  const resolveLinkedDocOption = useCallback((candidateId) => {
    const sourceTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
    return resolveLinkedDocOptionCandidate({
      candidateId,
      activeDocId: activeTabObj?.docId,
      sourceTabs,
      itemsMap
    });
  }, [activeTabObj?.docId, allTabs, itemsMap, tabs]);

  useEffect(() => {
    const handleRenameModeChanged = (event) => {
      const docType = String(event?.detail?.docType || '').trim().toLowerCase();
      if (docType === 'chat') {
        setChatRenameMode(normalizeRenameMode(event?.detail?.mode, 'chat'));
        return;
      }
      setDocRenameMode(normalizeRenameMode(event?.detail?.mode, null));
    };

    window.addEventListener('rename-mode-changed', handleRenameModeChanged);
    return () => window.removeEventListener('rename-mode-changed', handleRenameModeChanged);
  }, []);

  const getRenamePromptToastId = useCallback((docId) => `rename-prompt-${pane}-${String(docId || 'unknown')}`, [pane]);

  const resolveDocDisplayName = useCallback((docId) => {
    const normalizedDocId = String(docId || '').trim();
    if (!normalizedDocId) return '';

    const editorState = editorStates[normalizedDocId] || editorStates[Number(normalizedDocId)];
    const tab = tabs.find(entry => String(entry?.docId) === normalizedDocId);
    const item = findItem(itemsMap, normalizedDocId, 'doc');
    const normalizedDocType = String(item?.docType || tab?.docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null;
    const editorName = String(editorState?.name || '').trim();
    const tabLabel = String(tab?.label || '').trim();
    const itemName = String(item?.name || '').trim();

    // Prefer any non-default/manual title to avoid stale untitled labels
    // accidentally re-triggering rename suggestions on autosave.
    const canonicalCandidates = [itemName, editorName, tabLabel].filter(Boolean);
    const manualName = canonicalCandidates.find((name) => !isUntitledDefaultName(name, normalizedDocType));
    if (manualName) return manualName;

    return editorName || itemName || tabLabel || '';
  }, [editorStates, itemsMap, tabs]);

  const dismissRenamePrompt = useCallback((docId) => {
    if (!docId) return;
    notifyPaneToast({ dismiss: true, toastId: getRenamePromptToastId(docId) }, {
      where: {
        docId,
        scope: 'doc'
      }
    });
    if (String(activeRenamePromptDocIdRef.current || '') === String(docId)) {
      activeRenamePromptDocIdRef.current = null;
    }
  }, [getRenamePromptToastId, notifyPaneToast]);

  const markRenameSuggestionDismissed = useCallback((docId, suggestedName) => {
    const docKey = String(docId || '').trim();
    const sanitizedSuggestion = sanitizeSuggestedRename(suggestedName);
    if (!docKey || !sanitizedSuggestion) return;

    const current = dismissedRenameSuggestionsByDocRef.current;
    const existing = current.get(docKey);
    if (existing instanceof Set) {
      existing.add(sanitizedSuggestion);
      current.set(docKey, existing);
      return;
    }

    current.set(docKey, new Set([sanitizedSuggestion]));
  }, []);

  const wasRenameSuggestionDismissed = useCallback((docId, suggestedName) => {
    const docKey = String(docId || '').trim();
    const sanitizedSuggestion = sanitizeSuggestedRename(suggestedName);
    if (!docKey || !sanitizedSuggestion) return false;
    const existing = dismissedRenameSuggestionsByDocRef.current.get(docKey);
    return existing instanceof Set && existing.has(sanitizedSuggestion);
  }, []);

  const clearDismissedRenameSuggestions = useCallback((docId) => {
    const docKey = String(docId || '').trim();
    if (!docKey) return;
    dismissedRenameSuggestionsByDocRef.current.delete(docKey);
  }, []);

  const applyResolvedRename = useCallback(async ({ docId, nextName, docType = null } = {}) => {
    const trimmedName = sanitizeSuggestedRename(nextName);
    if (!docId || !trimmedName) return false;

    const normalizedDocType = String(docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null;
    let renamed = null;

    if (normalizedDocType === 'chat') {
      renamed = await renameChat({
        chat: { id: docId, docType: 'chat' },
        nextName: trimmedName,
        itemsMap,
        setItemsMap,
        tabs,
        setTabs,
        projectId: currentProject?.id,
        userId: currentUserId
      });
    } else {
      renamed = await renameDoc(docId, trimmedName, itemsMap, setItemsMap, currentProject?.id, currentUserId);
      if (renamed) {
        setTabs(prev => (Array.isArray(prev)
          ? prev.map(tab => (String(tab?.docId) === String(docId) ? { ...tab, label: trimmedName } : tab))
          : prev));
      }
    }

    if (!renamed) return false;

    clearDismissedRenameSuggestions(docId);

    setEditorStates(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        name: trimmedName
      }
    }));

    return true;
  }, [clearDismissedRenameSuggestions, currentProject?.id, currentUserId, itemsMap, setItemsMap, setTabs, tabs]);

  const showAutoRenameToast = useCallback(({ docId, nextName, previousName, docType = null } = {}) => {
    if (!docId || !nextName || !previousName) return;

    notifyPaneToast({
      message: 'Renamed doc to',
      suggestedName: nextName,
      toastId: `rename-auto-${pane}-${String(docId)}-${Date.now()}`,
      actions: [
        {
          id: 'undo',
          iconClass: 'arrow-u-up-left',
          label: 'Undo',
          onClick: () => {
            const currentName = resolveDocDisplayName(docId);
            if (String(currentName || '').trim() !== String(nextName || '').trim()) return;
            void applyResolvedRename({ docId, nextName: previousName, docType });
          }
        }
      ]
    }, {
      where: {
        docId,
        scope: 'doc'
      }
    });
  }, [applyResolvedRename, notifyPaneToast, pane, resolveDocDisplayName]);

  const showRenamePrompt = useCallback(({ docId, suggestedName, docType = null } = {}) => {
    const sanitizedSuggestion = sanitizeSuggestedRename(suggestedName);
    if (!docId || !sanitizedSuggestion) return false;
    if (wasRenameSuggestionDismissed(docId, sanitizedSuggestion)) return false;

    activeRenamePromptDocIdRef.current = String(docId);
    notifyPaneToast({
      message: 'Rename to',
      suggestedName: sanitizedSuggestion,
      isPrompt: true,
      toastId: getRenamePromptToastId(docId),
      persistent: true,
      actions: [
        {
          id: 'confirm',
          iconClass: 'check',
          label: 'Rename',
          onClick: () => {
            void applyResolvedRename({ docId, nextName: sanitizedSuggestion, docType });
          }
        },
        {
          id: 'dismiss',
          iconClass: 'x',
          label: 'Dismiss',
          onClick: () => {
            markRenameSuggestionDismissed(docId, sanitizedSuggestion);
          }
        }
      ]
    }, {
      where: {
        docId,
        scope: 'doc'
      }
    });
    return true;
  }, [applyResolvedRename, getRenamePromptToastId, markRenameSuggestionDismissed, notifyPaneToast, wasRenameSuggestionDismissed]);

  const maybeHandleDocumentRename = useCallback(async ({ docId, content, docType = null, trigger = 'autosave' } = {}) => {
    const normalizedDocType = String(docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null;
    if (!docId || normalizedDocType === 'chat' || trigger !== 'autosave') return false;

    const currentName = resolveDocDisplayName(docId);
    const mode = normalizeRenameMode(docRenameMode, null);
    const suggestedName = extractHeadingRenameCandidate(content, { maxLength: 120 });

    if (!shouldApplySuggestedRename({
      currentName,
      docType: normalizedDocType,
      renameMode: mode,
      suggestedName
    })) {
      return false;
    }

    if (mode === 'auto') {
      const renamed = await applyResolvedRename({ docId, nextName: suggestedName, docType: normalizedDocType });
      if (renamed) {
        showAutoRenameToast({
          docId,
          nextName: sanitizeSuggestedRename(suggestedName),
          previousName: currentName,
          docType: normalizedDocType
        });
      }
      return renamed;
    }

    if (mode === 'prompt') {
      const sanitized = sanitizeSuggestedRename(suggestedName);
      if (sanitized) {
        return showRenamePrompt({ docId, suggestedName, docType: normalizedDocType });
      }
      return false;
    }

    return false;
  }, [applyResolvedRename, docRenameMode, resolveDocDisplayName, showAutoRenameToast, showRenamePrompt]);

  useEffect(() => {
    const activeDocId = activeTabObj?.docId ? String(activeTabObj.docId) : null;
    const promptDocId = String(activeRenamePromptDocIdRef.current || '').trim() || null;
    if (promptDocId && promptDocId !== activeDocId) {
      dismissRenamePrompt(promptDocId);
      return;
    }

    if (promptDocId && activeDocId === promptDocId) {
      const currentName = resolveDocDisplayName(promptDocId);
      const activeDocType = String(activeTabObj?.docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null;
      if (!isUntitledDefaultName(currentName, activeDocType)) {
        dismissRenamePrompt(promptDocId);
      }
    }
  }, [activeTabObj?.docId, activeTabObj?.docType, dismissRenamePrompt, resolveDocDisplayName, tabs, itemsMap, editorStates]);

  const fallbackCopyText = useCallback((text) => {
    const normalized = String(text || '');
    if (!normalized) return;
    const textarea = document.createElement('textarea');
    textarea.value = normalized;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      // no-op
    } finally {
      document.body.removeChild(textarea);
    }
  }, []);

  const fallbackCopyRichText = useCallback(({ text, html }) => {
    const normalizedText = String(text || '').trim();
    const normalizedHtml = String(html || '').trim();
    if (!normalizedText && !normalizedHtml) return false;

    const holder = document.createElement('div');
    holder.contentEditable = 'true';
    holder.style.position = 'fixed';
    holder.style.opacity = '0';
    holder.style.pointerEvents = 'none';
    holder.style.left = '-9999px';
    holder.style.top = '0';
    holder.innerHTML = normalizedHtml || normalizedText;
    document.body.appendChild(holder);

    const selection = window.getSelection?.();
    const range = document.createRange();
    range.selectNodeContents(holder);
    selection?.removeAllRanges();
    selection?.addRange(range);

    let copied = false;
    const copyHandler = (event) => {
      if (!event?.clipboardData) return;
      event.clipboardData.setData('text/plain', normalizedText || holder.textContent || '');
      if (normalizedHtml) {
        event.clipboardData.setData('text/html', normalizedHtml);
      }
      event.preventDefault();
      copied = true;
    };

    try {
      document.addEventListener('copy', copyHandler, true);
      document.execCommand('copy');
      return copied;
    } catch (err) {
      return false;
    } finally {
      document.removeEventListener('copy', copyHandler, true);
      selection?.removeAllRanges();
      document.body.removeChild(holder);
    }
  }, []);

  const stripChunkAttributesFromHtml = useCallback((html) => {
    const rawHtml = String(html || '').trim();
    if (!rawHtml) return '';
    try {
      const template = document.createElement('template');
      template.innerHTML = rawHtml;
      template.content.querySelectorAll('[data-chunk]').forEach((node) => {
        node.removeAttribute('data-chunk');
      });
      return String(template.innerHTML || '').trim();
    } catch (err) {
      return rawHtml.replace(/\sdata-chunk=("[^"]*"|'[^']*')/gi, '');
    }
  }, []);

  const copyToClipboard = useCallback(async ({ text, html }, successMessage = 'Copied') => {
    const normalizedText = String(text || '').trim();
    const normalizedHtml = stripChunkAttributesFromHtml(html);
    if (!normalizedText && !normalizedHtml) return false;

    try {
      const ClipboardItemCtor = typeof window !== 'undefined' ? window.ClipboardItem : null;
      if (navigator?.clipboard?.write && ClipboardItemCtor && normalizedHtml && normalizedText) {
        const payload = new ClipboardItemCtor({
          'text/plain': new Blob([normalizedText], { type: 'text/plain' }),
          'text/html': new Blob([normalizedHtml], { type: 'text/html' })
        });
        await navigator.clipboard.write([payload]);
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedText || normalizedHtml);
      } else {
        const copied = fallbackCopyRichText({ text: normalizedText, html: normalizedHtml });
        if (!copied) fallbackCopyText(normalizedText || normalizedHtml);
      }
      notifyPaneToast(successMessage);
      return true;
    } catch (err) {
      const copied = fallbackCopyRichText({ text: normalizedText, html: normalizedHtml });
      if (!copied) fallbackCopyText(normalizedText || normalizedHtml);
      notifyPaneToast(successMessage);
      return true;
    }
  }, [fallbackCopyRichText, fallbackCopyText, notifyPaneToast, stripChunkAttributesFromHtml]);

  const handleChatComposerOrchestrationChange = useCallback((value) => {
    if (!value || typeof value !== 'object') {
      setChatComposerOrchestration({});
      return;
    }
    setChatComposerOrchestration({ ...value });
  }, []);

  const applySelectionRegionIndicator = useCallback((value) => {
    const normalizedDocId = Number(value?.docId || 0);
    const normalizedLeft = Number(value?.left || 0);
    const normalizedTop = Number(value?.top || 0);
    const normalizedHeight = Number(value?.height || 0);
    if (!normalizedDocId || !Number.isFinite(normalizedLeft) || !Number.isFinite(normalizedTop) || !Number.isFinite(normalizedHeight) || normalizedHeight <= 0) {
      setSelectionRegionIndicator((prev) => (prev.visible ? { visible: false, docId: null, left: 0, top: 0, height: 0 } : prev));
      return;
    }
    const next = {
      visible: true,
      docId: normalizedDocId,
      left: Math.round(normalizedLeft),
      top: Math.round(normalizedTop),
      height: Math.max(16, Math.round(normalizedHeight))
    };
    setSelectionRegionIndicator((prev) => {
      if (
        prev.visible
        && prev.docId === next.docId
        && prev.left === next.left
        && prev.top === next.top
        && prev.height === next.height
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const publishSelectionContext = useCallback((payload) => {
    const activeDocId = Number(activeTabObj?.docId || 0);
    if (!activeDocId) return;
    if (String(activeTabObj?.docType || '').trim().toLowerCase() === 'chat') return;

    const selectedText = String(payload?.text || '').trim();
    if (!selectedText) return;

    const context = {
      docId: activeDocId,
      label: String(resolveDocDisplayName(activeDocId) || `Doc ${activeDocId}`).trim() || `Doc ${activeDocId}`,
      selectedText,
      selectedHtml: String(payload?.html || '').trim() || null,
      indicator: payload?.indicator && typeof payload.indicator === 'object'
        ? {
          docId: activeDocId,
          left: Number(payload.indicator.left || 0),
          top: Number(payload.indicator.top || 0),
          height: Number(payload.indicator.height || 0)
        }
        : null,
      updatedAt: Date.now(),
      key: `${activeDocId}:${selectedText.slice(0, 120)}`
    };

    lastSelectionContextRef.current = context;
    applySelectionRegionIndicator(context?.indicator || null);
    if (typeof window !== 'undefined') {
      try {
        window[LAST_SELECTION_CONTEXT_WINDOW_KEY] = context;
        window.dispatchEvent(new CustomEvent('ghost-selection-context-updated', { detail: context }));
      } catch (err) {}
    }
  }, [activeTabObj?.docId, activeTabObj?.docType, applySelectionRegionIndicator, resolveDocDisplayName]);

  const clearSelectionContextForDoc = useCallback((docId, options = {}) => {
    const normalizedDocId = Number(docId || 0);
    if (!normalizedDocId) return;

    const preserveIndicator = options?.preserveIndicator === true;
    const current = lastSelectionContextRef.current;
    if (!current || Number(current?.docId || 0) !== normalizedDocId) return;

    const nextIndicator = preserveIndicator && current?.indicator ? { ...current.indicator, docId: normalizedDocId } : null;
    const clearedContext = {
      docId: normalizedDocId,
      label: String(resolveDocDisplayName(normalizedDocId) || `Doc ${normalizedDocId}`).trim() || `Doc ${normalizedDocId}`,
      selectedText: null,
      selectedHtml: null,
      indicator: nextIndicator,
      updatedAt: Date.now(),
      key: `${normalizedDocId}:cleared`,
      cleared: true
    };

    lastSelectionContextRef.current = clearedContext;
    applySelectionRegionIndicator(nextIndicator);

    if (typeof window !== 'undefined') {
      try {
        window[LAST_SELECTION_CONTEXT_WINDOW_KEY] = clearedContext;
        window.dispatchEvent(new CustomEvent('ghost-selection-context-updated', { detail: clearedContext }));
      } catch (err) {}
    }
  }, [applySelectionRegionIndicator, resolveDocDisplayName]);

  const clearSelectionContextForActiveDoc = useCallback((options = {}) => {
    const activeDocId = Number(activeTabObj?.docId || 0);
    if (!activeDocId) return;
    if (String(activeTabObj?.docType || '').trim().toLowerCase() === 'chat') return;
    clearSelectionContextForDoc(activeDocId, options);
  }, [activeTabObj?.docId, activeTabObj?.docType, clearSelectionContextForDoc]);

  const resolveSelectionReferenceForComposer = useCallback(() => {
    if (typeof window === 'undefined') return null;

    const fromRef = lastSelectionContextRef.current;
    const fromWindow = window[LAST_SELECTION_CONTEXT_WINDOW_KEY] || null;
    const candidate = fromRef && Number(fromRef?.updatedAt || 0) >= Number(fromWindow?.updatedAt || 0)
      ? fromRef
      : fromWindow;
    if (!candidate) return null;

    const candidateDocId = Number(candidate?.docId || 0);
    if (!candidateDocId) return null;
    if (String(activeTabObj?.docId || '') === String(candidateDocId)) {
      return null;
    }

    const ageMs = Date.now() - Number(candidate?.updatedAt || 0);
    if (!Number.isFinite(ageMs) || ageMs > 15 * 60 * 1000) {
      return null;
    }

    return {
      id: candidateDocId,
      label: String(candidate?.label || `Doc ${candidateDocId}`).trim() || `Doc ${candidateDocId}`,
      selectedText: String(candidate?.selectedText || '').trim() || null,
      key: String(candidate?.key || `${candidateDocId}`).trim(),
      updatedAt: Number(candidate?.updatedAt || 0) || Date.now()
    };
  }, [activeTabObj?.docId]);

  const getSelectionPayload = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const activeDocId = activeTabObj?.docId;
    if (!activeDocId) return null;

    const editor = editorInstances.current[activeDocId];
    const editorDom = editor?.view?.dom;
    if (!editorDom) return null;

    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;

    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    if (!anchorNode || !focusNode) return null;
    if (!editorDom.contains(anchorNode) || !editorDom.contains(focusNode)) return null;

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (!rect.width && !rect.height)) return null;

    const text = String(selection.toString() || '').trim();
    const fragment = range.cloneContents();
    const container = document.createElement('div');
    container.appendChild(fragment);
    const html = String(container.innerHTML || '').trim();
    const ancestorText = String(range.commonAncestorContainer?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();

    let surroundingContext = '';
    if (ancestorText) {
      if (!text) {
        surroundingContext = ancestorText.slice(0, 420).trim();
      } else {
        const selectedIndex = ancestorText.toLowerCase().indexOf(text.toLowerCase());
        if (selectedIndex >= 0) {
          const start = Math.max(0, selectedIndex - 120);
          const end = Math.min(ancestorText.length, selectedIndex + text.length + 120);
          surroundingContext = ancestorText.slice(start, end).trim();
        } else {
          surroundingContext = ancestorText.slice(0, 420).trim();
        }
      }
    }

    if (!text && !html) return null;

    return {
      text,
      html,
      surroundingContext,
      left: Math.max(10, Math.round(rect.left)),
      top: Math.round(rect.bottom + 8),
      indicator: {
        left: Math.round((editorDom.closest('.document')?.getBoundingClientRect?.().right || editorDom.getBoundingClientRect().right) + 8),
        top: Math.round(rect.top),
        height: Math.max(16, Math.round(rect.height || 16))
      }
    };
  }, [activeTabObj?.docId]);

  const refreshSelectionActionBar = useCallback(() => {
    const payload = getSelectionPayload();
    if (!payload) {
      setSelectionActionBar((prev) => (prev.visible ? { visible: false, left: 0, top: 0 } : prev));
      return null;
    }

    publishSelectionContext(payload);
    setSelectionActionBar({ visible: true, left: payload.left, top: payload.top });
    return payload;
  }, [getSelectionPayload, publishSelectionContext]);

  const focusActiveEditor = useCallback(() => {
    const activeDocId = activeTabObj?.docId;
    if (!activeDocId) return;
    const editor = editorInstances.current[activeDocId];
    try {
      if (editor?.view?.focus) {
        editor.view.focus();
        return;
      }
      if (editor?.commands?.focus) {
        editor.commands.focus();
      }
    } catch (err) {}
  }, [activeTabObj?.docId]);

  const hideSelectionActionBar = useCallback(() => {
    setSelectionActionBar((prev) => (prev.visible ? { visible: false, left: 0, top: 0 } : prev));
  }, []);

  const clearFocusedSuggestionClass = useCallback(() => {
    Object.values(editorInstances.current || {}).forEach((editor) => {
      const editorDom = editor?.view?.dom;
      if (!editorDom) return;
      const focusedNodes = Array.from(editorDom.querySelectorAll('[data-diff-op].focused'));
      focusedNodes.forEach((node) => {
        try {
          node.classList.remove('focused');
        } catch (err) {}
      });
    });
    diffSuggestionFocusedKeyRef.current = null;
  }, []);

  const hideDiffSuggestionInlineBar = useCallback(() => {
    if (diffSuggestionInlineHideTimeoutRef.current) {
      clearTimeout(diffSuggestionInlineHideTimeoutRef.current);
      diffSuggestionInlineHideTimeoutRef.current = null;
    }
    diffSuggestionInlineHoverRef.current = false;
    setDiffSuggestionInlineBar((prev) => (prev.visible
      ? { visible: false, left: 0, top: 0, docId: null, annotationId: null, batchId: null, linkedAnnotationIds: [] }
      : prev));
  }, []);

  const getDiffSuggestionInlinePayload = useCallback((event, docId) => {
    if (!event || !docId) return null;
    const targetNode = event?.target?.closest?.('[data-diff-op]');
    if (!targetNode) return null;

    const editor = editorInstances.current[docId];
    const editorDom = editor?.view?.dom;
    if (!editorDom || !editorDom.contains(targetNode)) return null;

    const annotationId = String(targetNode.getAttribute('data-diff-id') || '').trim() || null;
    const batchId = String(targetNode.getAttribute('data-diff-batch-id') || '').trim() || null;
    const op = String(targetNode.getAttribute('data-diff-op') || '').trim() || 'add';

    const sectionNodes = Array.from(editorDom.querySelectorAll('[data-diff-op]')).filter((node) => {
      const nodeOp = String(node.getAttribute('data-diff-op') || '').trim() || 'add';
      if (nodeOp !== op) return false;

      const nodeAnnotationId = String(node.getAttribute('data-diff-id') || '').trim() || null;
      const nodeBatchId = String(node.getAttribute('data-diff-batch-id') || '').trim() || null;

      if (annotationId) {
        return nodeAnnotationId === annotationId;
      }

      if (batchId) {
        return nodeBatchId === batchId;
      }

      return node === targetNode;
    });

    const anchorNode = sectionNodes.length > 0
      ? sectionNodes.reduce((topNode, node) => {
        if (!topNode) return node;
        const topRect = topNode.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        if (nodeRect.top < topRect.top) return node;
        if (Math.abs(nodeRect.top - topRect.top) <= 1 && nodeRect.left < topRect.left) return node;
        return topNode;
      }, null)
      : targetNode;

    let linkedAnnotationIds = [];
    if (batchId) {
      const sectionRects = sectionNodes
        .map((node) => node?.getBoundingClientRect?.())
        .filter((nodeRect) => nodeRect && (nodeRect.width || nodeRect.height));
      const sectionTop = sectionRects.length > 0
        ? Math.min(...sectionRects.map((nodeRect) => Number(nodeRect.top || 0)))
        : Number(anchorNode?.getBoundingClientRect?.()?.top || 0);
      const sectionBottom = sectionRects.length > 0
        ? Math.max(...sectionRects.map((nodeRect) => Number(nodeRect.bottom || 0)))
        : Number(anchorNode?.getBoundingClientRect?.()?.bottom || sectionTop);

      const oppositeOp = op === 'add' ? 'remove' : 'add';
      const linkedIds = new Set();
      const candidateNodes = Array.from(editorDom.querySelectorAll('[data-diff-op]'));
      candidateNodes.forEach((node) => {
        const nodeOp = String(node.getAttribute('data-diff-op') || '').trim() || 'add';
        if (nodeOp !== oppositeOp) return;

        const nodeBatchId = String(node.getAttribute('data-diff-batch-id') || '').trim() || null;
        if (!nodeBatchId || nodeBatchId !== batchId) return;

        const nodeAnnotationId = String(node.getAttribute('data-diff-id') || '').trim() || null;
        if (!nodeAnnotationId || (annotationId && nodeAnnotationId === annotationId)) return;

        const nodeRect = node.getBoundingClientRect();
        if (!nodeRect || (!nodeRect.width && !nodeRect.height)) return;

        const overlap = Math.min(sectionBottom, Number(nodeRect.bottom || 0)) - Math.max(sectionTop, Number(nodeRect.top || 0));
        const isAligned = overlap >= -2;
        if (!isAligned) return;

        linkedIds.add(nodeAnnotationId);
      });

      linkedAnnotationIds = Array.from(linkedIds);
    }

    const rect = anchorNode?.getBoundingClientRect?.();
    if (!rect || (!rect.width && !rect.height)) return null;
    const editorRect = editorDom.getBoundingClientRect();
    if (!editorRect || (!editorRect.width && !editorRect.height)) return null;

    const BAR_WIDTH = 66;
    const BAR_HEIGHT = 30;
    const EDGE_PAD = 6;

    let left = Math.round(rect.left);
    let top = Math.round(rect.top - BAR_HEIGHT - EDGE_PAD);

    const minLeft = Math.round(editorRect.left + EDGE_PAD);
    const maxLeft = Math.round(editorRect.right - BAR_WIDTH - EDGE_PAD);
    if (maxLeft >= minLeft) {
      left = Math.max(minLeft, Math.min(left, maxLeft));
    } else {
      left = minLeft;
    }

    const minTop = Math.round(editorRect.top + EDGE_PAD);
    if (top < minTop) {
      top = Math.round(rect.bottom + EDGE_PAD);
    }

    const maxTop = Math.round(editorRect.bottom - BAR_HEIGHT - EDGE_PAD);
    if (maxTop >= minTop) {
      top = Math.max(minTop, Math.min(top, maxTop));
    } else {
      top = minTop;
    }

    return {
      docId,
      annotationId,
      batchId,
      linkedAnnotationIds,
      left,
      top
    };
  }, []);

  const refreshDiffSuggestionInlineBar = useCallback((event, docId, readOnly = false) => {
    if (readOnly) {
      hideDiffSuggestionInlineBar();
      return;
    }

    const payload = getDiffSuggestionInlinePayload(event, docId);
    if (!payload) {
      if (diffSuggestionInlineHoverRef.current) return;
      if (diffSuggestionInlineHideTimeoutRef.current) {
        clearTimeout(diffSuggestionInlineHideTimeoutRef.current);
      }
      diffSuggestionInlineHideTimeoutRef.current = setTimeout(() => {
        diffSuggestionInlineHideTimeoutRef.current = null;
        if (!diffSuggestionInlineHoverRef.current) {
          hideDiffSuggestionInlineBar();
        }
      }, 180);
      return;
    }

    if (diffSuggestionInlineHideTimeoutRef.current) {
      clearTimeout(diffSuggestionInlineHideTimeoutRef.current);
      diffSuggestionInlineHideTimeoutRef.current = null;
    }

    setDiffSuggestionInlineBar((prev) => {
      const unchanged = prev.visible
        && prev.docId === payload.docId
        && prev.annotationId === payload.annotationId
        && prev.batchId === payload.batchId
        && JSON.stringify(prev.linkedAnnotationIds || []) === JSON.stringify(payload.linkedAnnotationIds || [])
        && Math.abs(prev.left - payload.left) <= 1
        && Math.abs(prev.top - payload.top) <= 1;
      if (unchanged) return prev;
      return {
        visible: true,
        left: payload.left,
        top: payload.top,
        docId: payload.docId,
        annotationId: payload.annotationId,
        batchId: payload.batchId,
        linkedAnnotationIds: Array.isArray(payload.linkedAnnotationIds) ? payload.linkedAnnotationIds : []
      };
    });
  }, [getDiffSuggestionInlinePayload, hideDiffSuggestionInlineBar]);

  const collectDiffSuggestionEntries = useCallback((docId) => {
    const editor = docId ? editorInstances.current[docId] : null;
    const editorDom = editor?.view?.dom;
    if (!editorDom) return [];

    const nodes = Array.from(editorDom.querySelectorAll('[data-diff-op]'));
    const grouped = new Map();

    nodes.forEach((node, index) => {
      const annotationId = String(node.getAttribute('data-diff-id') || '').trim() || null;
      const batchId = String(node.getAttribute('data-diff-batch-id') || '').trim() || null;
      const op = String(node.getAttribute('data-diff-op') || '').trim() || 'add';
      // Annotation id represents a logical suggestion pair (add/remove).
      const key = annotationId || `node-${index}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.nodeIndexes.push(index);
        existing.ops.add(op);
        return;
      }

      grouped.set(key, {
        key,
        annotationId,
        batchId,
        nodeIndexes: [index],
        ops: new Set([op]),
        firstNodeIndex: index
      });
    });

    return Array.from(grouped.values())
      .sort((left, right) => left.firstNodeIndex - right.firstNodeIndex)
      .map((entry) => ({
        ...entry,
        ops: Array.from(entry.ops)
      }));
  }, []);

  const focusSuggestionEntry = useCallback((docId, entry, options = {}) => {
    const editor = docId ? editorInstances.current[docId] : null;
    const editorDom = editor?.view?.dom;
    if (!editorDom) return;
    const selectRange = options?.selectRange !== false;
    const allowScroll = options?.scrollIntoView !== false;
    const targetViewportRatio = Number.isFinite(Number(options?.targetViewportRatio))
      ? Number(options.targetViewportRatio)
      : 0.25;

    const allSuggestionNodes = Array.from(editorDom.querySelectorAll('[data-diff-op]'));
    const targetIndexes = Array.isArray(entry?.nodeIndexes) ? entry.nodeIndexes : [];
    const targetNodes = targetIndexes
      .map((nodeIndex) => allSuggestionNodes[Math.max(0, Math.min(Number(nodeIndex || 0), allSuggestionNodes.length - 1))])
      .filter(Boolean);

    allSuggestionNodes.forEach((node) => {
      try {
        node.classList.remove('focused');
      } catch (err) {}
    });

    targetNodes.forEach((node) => {
      try {
        node.classList.add('focused');
      } catch (err) {}
    });

    if (allowScroll && targetNodes[0]) {
      const targetNode = targetNodes[0];
      const scrollContainer = editorScrollRef.current
        || (typeof document !== 'undefined' ? document.getElementById(editorPanelId) : null);

      if (scrollContainer?.getBoundingClientRect) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const nodeRect = targetNode.getBoundingClientRect();
        const nextScrollTop = computeSuggestionScrollTopForViewportRatio({
          currentScrollTop: Number(scrollContainer.scrollTop || 0),
          containerTop: Number(containerRect.top || 0),
          containerHeight: Number(containerRect.height || 0),
          nodeTop: Number(nodeRect.top || 0),
          ratio: targetViewportRatio
        });
        if (Number.isFinite(nextScrollTop) && Math.abs(nextScrollTop - Number(scrollContainer.scrollTop || 0)) > 1) {
          scrollContainer.scrollTop = nextScrollTop;
        }
      } else if (targetNode.scrollIntoView) {
        targetNode.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    }

    // For keyboard/dock navigation we select the range; for direct clicks,
    // keep native caret placement to avoid replacing suggestion text on type.
    try {
      const view = editor?.view;
      const firstNode = targetNodes[0];
      const lastNode = targetNodes[targetNodes.length - 1] || firstNode;
      if (selectRange && view && firstNode && lastNode && typeof view.posAtDOM === 'function') {
        const from = view.posAtDOM(firstNode, 0);
        const to = view.posAtDOM(lastNode, lastNode.childNodes?.length || 0);
        if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
          if (editor?.commands?.setTextSelection) {
            editor.commands.setTextSelection({ from, to });
          }
        }
      }
    } catch (err) {
      // Ignore selection focus failures; scroll focus still applies.
    }

    diffSuggestionFocusedKeyRef.current = entry?.key || null;
  }, [editorPanelId]);

  const focusSuggestionEntryByIndex = useCallback((docId, targetIndex, options = {}) => {
    const entries = collectDiffSuggestionEntries(docId);
    if (!Array.isArray(entries) || entries.length === 0) {
      clearFocusedSuggestionClass();
      return { entries: [], index: 0 };
    }

    const safeIndex = Math.max(0, Math.min(Number(targetIndex || 0), entries.length - 1));
    const entry = entries[safeIndex] || null;
    if (!entry) {
      clearFocusedSuggestionClass();
      return { entries, index: safeIndex };
    }

    focusSuggestionEntry(docId, entry, options);
    return { entries, index: safeIndex };
  }, [clearFocusedSuggestionClass, collectDiffSuggestionEntries, focusSuggestionEntry]);

  const refreshDiffSuggestionDock = useCallback((docId, readOnly = false, options = {}) => {
    if (!docId || readOnly) {
      clearFocusedSuggestionClass();
      setDiffSuggestionDock({ visible: false, docId: null, currentIndex: 0, entries: [] });
      return;
    }

    const entries = collectDiffSuggestionEntries(docId);
    if (entries.length === 0) {
      clearFocusedSuggestionClass();
      setDiffSuggestionDock({ visible: false, docId, currentIndex: 0, entries: [] });
      return;
    }

    setDiffSuggestionDock((prev) => {
      let nextIndex = -1;
      const requestedIndex = Number(options?.preferredIndex);
      if (Number.isFinite(requestedIndex)) {
        nextIndex = Math.max(0, Math.min(Math.floor(requestedIndex), entries.length - 1));
      }

      if (nextIndex < 0) {
        const preferredAnnotationId = String(options?.preferredAnnotationId || '').trim();
        if (preferredAnnotationId) {
          const annotationIndex = entries.findIndex((entry) => String(entry?.annotationId || '').trim() === preferredAnnotationId);
          if (annotationIndex >= 0) {
            nextIndex = annotationIndex;
          }
        }
      }

      if (nextIndex < 0) {
        const previousKey = prev?.entries?.[prev.currentIndex]?.key || diffSuggestionFocusedKeyRef.current;
        nextIndex = entries.findIndex((entry) => entry.key === previousKey);
      }

      if (nextIndex < 0) nextIndex = 0;
      return {
        visible: true,
        docId,
        currentIndex: nextIndex,
        entries
      };
    });
  }, [clearFocusedSuggestionClass, collectDiffSuggestionEntries]);

  const handleDiffSuggestionSelectFromEvent = useCallback((event, docId, readOnly = false) => {
    if (readOnly || !docId || !event) return;
    markUserInteraction(docId, { type: 'click', key: 'suggestion-select' });
    const targetNode = event?.target?.closest?.('[data-diff-op]');
    if (!targetNode) return;

    const editor = editorInstances.current[docId];
    const editorDom = editor?.view?.dom;
    if (!editorDom) return;

    const allSuggestionNodes = Array.from(editorDom.querySelectorAll('[data-diff-op]'));
    const clickedIndex = allSuggestionNodes.indexOf(targetNode);
    if (clickedIndex < 0) return;

    const entries = collectDiffSuggestionEntries(docId);
    if (entries.length === 0) return;

    let targetIndex = entries.findIndex((entry) => Array.isArray(entry.nodeIndexes) && entry.nodeIndexes.includes(clickedIndex));
    if (targetIndex < 0) {
      targetIndex = Math.max(0, Math.min(clickedIndex, entries.length - 1));
    }

    diffSuggestionNextFocusModeRef.current = 'none';

    setDiffSuggestionDock({
      visible: true,
      docId,
      currentIndex: targetIndex,
      entries
    });
  }, [collectDiffSuggestionEntries, markUserInteraction]);

  const handleDiffSuggestionActionKeep = useCallback(() => {
    if (!diffSuggestionInlineBar?.visible || !diffSuggestionInlineBar?.docId) return;
    markUserInteraction(diffSuggestionInlineBar.docId, { type: 'click', key: 'suggestion-keep-inline' });
    const editor = editorInstances.current[diffSuggestionInlineBar.docId];
    const annotationIds = getInlineSuggestionAnnotationIdsToResolve(diffSuggestionInlineBar);

    let changed = false;
    annotationIds.forEach((annotationId) => {
      const result = resolveSuggestionByAnnotationId(editor, {
        annotationId,
        action: 'keep'
      });
      if (result?.changed) changed = true;
    });

    if (!changed && diffSuggestionInlineBar.batchId) {
      const batchResult = resolveSuggestionByBatchId(editor, {
        batchId: diffSuggestionInlineBar.batchId,
        action: 'keep'
      });
      changed = Boolean(batchResult?.changed);
    }

    if (!changed) {
      notifyPaneToast('Unable to keep suggestion');
      return;
    }

    notifyPaneToast('Suggestion kept');
    clearSelectionContextForActiveDoc();
    hideDiffSuggestionInlineBar();
    refreshDiffSuggestionDock(diffSuggestionInlineBar.docId, false);
  }, [clearSelectionContextForActiveDoc, diffSuggestionInlineBar, hideDiffSuggestionInlineBar, markUserInteraction, notifyPaneToast, refreshDiffSuggestionDock]);

  const handleDiffSuggestionActionUndo = useCallback(() => {
    if (!diffSuggestionInlineBar?.visible || !diffSuggestionInlineBar?.docId) return;
    markUserInteraction(diffSuggestionInlineBar.docId, { type: 'click', key: 'suggestion-undo-inline' });
    const editor = editorInstances.current[diffSuggestionInlineBar.docId];
    const annotationIds = getInlineSuggestionAnnotationIdsToResolve(diffSuggestionInlineBar);

    let changed = false;
    annotationIds.forEach((annotationId) => {
      const result = resolveSuggestionByAnnotationId(editor, {
        annotationId,
        action: 'undo'
      });
      if (result?.changed) changed = true;
    });

    if (!changed && diffSuggestionInlineBar.batchId) {
      const batchResult = resolveSuggestionByBatchId(editor, {
        batchId: diffSuggestionInlineBar.batchId,
        action: 'undo'
      });
      changed = Boolean(batchResult?.changed);
    }

    if (!changed) {
      notifyPaneToast('Unable to undo suggestion');
      return;
    }

    notifyPaneToast('Suggestion undone');
    clearSelectionContextForActiveDoc();
    hideDiffSuggestionInlineBar();
    refreshDiffSuggestionDock(diffSuggestionInlineBar.docId, false);
  }, [clearSelectionContextForActiveDoc, diffSuggestionInlineBar, hideDiffSuggestionInlineBar, markUserInteraction, notifyPaneToast, refreshDiffSuggestionDock]);

  const handleDiffSuggestionDockAction = useCallback((action = 'keep') => {
    if (!diffSuggestionDock?.visible || !diffSuggestionDock?.docId || !Array.isArray(diffSuggestionDock.entries) || diffSuggestionDock.entries.length === 0) return;

    const requestedAction = String(action || '').trim().toLowerCase() === 'undo' ? 'undo' : 'keep';
    markUserInteraction(diffSuggestionDock.docId, {
      type: 'click',
      key: requestedAction === 'undo' ? 'suggestion-undo-dock' : 'suggestion-keep-dock'
    });
    const targetIndex = Math.max(0, Math.min(Number(diffSuggestionDock.currentIndex || 0), diffSuggestionDock.entries.length - 1));
    const entry = diffSuggestionDock.entries[targetIndex];
    const focusedKey = String(diffSuggestionFocusedKeyRef.current || '').trim();
    if (!entry || !focusedKey || focusedKey !== String(entry?.key || '').trim()) return;

    const editor = editorInstances.current[diffSuggestionDock.docId];
    let result = entry.annotationId
      ? resolveSuggestionByAnnotationId(editor, {
        annotationId: entry.annotationId,
        action: requestedAction
      })
      : resolveSuggestionByBatchId(editor, {
        batchId: entry.batchId,
        action: requestedAction
      });

    if (!result?.changed && entry.annotationId && entry.batchId) {
      result = resolveSuggestionByBatchId(editor, {
        batchId: entry.batchId,
        action: requestedAction
      });
    }

    if (!result?.changed) {
      notifyPaneToast(requestedAction === 'undo' ? 'Unable to undo suggestion' : 'Unable to keep suggestion');
      return;
    }

    hideDiffSuggestionInlineBar();
    notifyPaneToast(requestedAction === 'undo' ? 'Suggestion undone' : 'Suggestion kept');
    clearSelectionContextForActiveDoc();
    diffSuggestionNextFocusModeRef.current = 'range';
    refreshDiffSuggestionDock(diffSuggestionDock.docId, false, { preferredIndex: targetIndex });
  }, [clearSelectionContextForActiveDoc, diffSuggestionDock, hideDiffSuggestionInlineBar, markUserInteraction, notifyPaneToast, refreshDiffSuggestionDock]);

  const handleDiffSuggestionActionKeepFromDock = useCallback(() => {
    handleDiffSuggestionDockAction('keep');
  }, [handleDiffSuggestionDockAction]);

  const handleDiffSuggestionActionUndoFromDock = useCallback(() => {
    handleDiffSuggestionDockAction('undo');
  }, [handleDiffSuggestionDockAction]);

  const resolveAllSuggestionsInDoc = useCallback((docId, action) => {
    const editor = editorInstances.current[docId];
    if (!editor) return { changed: false };
    const entries = collectDiffSuggestionEntries(docId);
    if (entries.length === 0) return { changed: false };

    let changed = false;
    const processedBatchIds = new Set();
    const processedAnnotationIds = new Set();

    entries.forEach((entry) => {
      const batchId = String(entry?.batchId || '').trim();
      const annotationId = String(entry?.annotationId || '').trim();

      if (batchId && !processedBatchIds.has(batchId)) {
        processedBatchIds.add(batchId);
        const result = resolveSuggestionByBatchId(editor, { batchId, action });
        if (result?.changed) changed = true;
        return;
      }

      if (annotationId && !processedAnnotationIds.has(annotationId)) {
        processedAnnotationIds.add(annotationId);
        const result = resolveSuggestionByAnnotationId(editor, { annotationId, action });
        if (result?.changed) changed = true;
      }
    });

    return { changed };
  }, [collectDiffSuggestionEntries]);

  const handleDiffSuggestionActionKeepAll = useCallback(() => {
    if (!diffSuggestionDock?.visible || !diffSuggestionDock?.docId) return;
    markUserInteraction(diffSuggestionDock.docId, { type: 'click', key: 'suggestion-keep-all' });
    const result = resolveAllSuggestionsInDoc(diffSuggestionDock.docId, 'keep');

    if (!result?.changed) {
      notifyPaneToast('Unable to keep all suggestions');
      return;
    }

    notifyPaneToast('All suggestions kept');
    clearSelectionContextForActiveDoc();
    refreshDiffSuggestionDock(diffSuggestionDock.docId, false);
  }, [clearSelectionContextForActiveDoc, diffSuggestionDock, markUserInteraction, notifyPaneToast, refreshDiffSuggestionDock, resolveAllSuggestionsInDoc]);

  const handleDiffSuggestionActionUndoAll = useCallback(() => {
    if (!diffSuggestionDock?.visible || !diffSuggestionDock?.docId) return;
    markUserInteraction(diffSuggestionDock.docId, { type: 'click', key: 'suggestion-undo-all' });
    const result = resolveAllSuggestionsInDoc(diffSuggestionDock.docId, 'undo');

    if (!result?.changed) {
      notifyPaneToast('Unable to undo all suggestions');
      return;
    }

    notifyPaneToast('All suggestions undone');
    clearSelectionContextForActiveDoc();
    refreshDiffSuggestionDock(diffSuggestionDock.docId, false);
  }, [clearSelectionContextForActiveDoc, diffSuggestionDock, markUserInteraction, notifyPaneToast, refreshDiffSuggestionDock, resolveAllSuggestionsInDoc]);

  const handleDiffSuggestionCycleUp = useCallback(() => {
    diffSuggestionNextFocusModeRef.current = 'range';
    setDiffSuggestionDock((prev) => {
      if (!prev?.visible || !prev?.docId) return prev;
      const entries = collectDiffSuggestionEntries(prev.docId);
      if (!Array.isArray(entries) || entries.length === 0) return prev;
      const nextIndex = (Number(prev.currentIndex || 0) - 1 + entries.length) % entries.length;
      return { ...prev, entries, currentIndex: nextIndex };
    });
  }, [collectDiffSuggestionEntries]);

  const handleDiffSuggestionCycleDown = useCallback(() => {
    diffSuggestionNextFocusModeRef.current = 'range';
    setDiffSuggestionDock((prev) => {
      if (!prev?.visible || !prev?.docId) return prev;
      const entries = collectDiffSuggestionEntries(prev.docId);
      if (!Array.isArray(entries) || entries.length === 0) return prev;
      const nextIndex = (Number(prev.currentIndex || 0) + 1) % entries.length;
      return { ...prev, entries, currentIndex: nextIndex };
    });
  }, [collectDiffSuggestionEntries]);

  useEffect(() => {
    const activeDocId = activeTabObj?.docId;
    const activeTab = tabs.find(t => t.id === activeTabId);
    const isChat = activeTab?.docType === 'chat';
    const docReadOnly = Boolean(activeTab?.readOnly) || isProjectReadOnly;
    if (!activeDocId || isChat) {
      clearFocusedSuggestionClass();
      hideDiffSuggestionInlineBar();
      setDiffSuggestionDock({ visible: false, docId: null, currentIndex: 0, entries: [] });
      return;
    }
    refreshDiffSuggestionDock(activeDocId, docReadOnly);
  }, [activeTabId, activeTabObj?.docId, clearFocusedSuggestionClass, editorStates, hideDiffSuggestionInlineBar, isProjectReadOnly, refreshDiffSuggestionDock, tabs]);

  useEffect(() => {
    if (!diffSuggestionDock?.visible || !diffSuggestionDock?.docId || !Array.isArray(diffSuggestionDock.entries) || diffSuggestionDock.entries.length === 0) {
      clearFocusedSuggestionClass();
      return;
    }
    const selectRange = shouldSelectSuggestionRange(diffSuggestionNextFocusModeRef.current);
    const focused = focusSuggestionEntryByIndex(diffSuggestionDock.docId, diffSuggestionDock.currentIndex, {
      selectRange,
      scrollIntoView: true,
      targetViewportRatio: 0.25
    });
    diffSuggestionNextFocusModeRef.current = 'none';
    if (!Array.isArray(focused.entries) || focused.entries.length === 0) return;

    if (focused.entries.length !== diffSuggestionDock.entries.length || focused.index !== diffSuggestionDock.currentIndex) {
      setDiffSuggestionDock((prev) => ({
        ...prev,
        entries: focused.entries,
        currentIndex: focused.index
      }));
    }
  }, [clearFocusedSuggestionClass, diffSuggestionDock, focusSuggestionEntryByIndex]);

  const dockFocusedEntry = (
    diffSuggestionDock?.visible
    && Array.isArray(diffSuggestionDock?.entries)
    && diffSuggestionDock.entries.length > 0
  )
    ? diffSuggestionDock.entries[Math.max(0, Math.min(Number(diffSuggestionDock.currentIndex || 0), diffSuggestionDock.entries.length - 1))]
    : null;
  const canResolveFocusedDockSuggestion = Boolean(
    dockFocusedEntry
    && String(diffSuggestionFocusedKeyRef.current || '').trim()
    && String(dockFocusedEntry?.key || '').trim() === String(diffSuggestionFocusedKeyRef.current || '').trim()
  );
  const handleSelectionActionCopy = useCallback(async () => {
    const payload = getSelectionPayload();
    if (!payload) return;

    // Prefer native copy so clipboard HTML matches Ctrl+C behavior
    // (preserves block wrappers like headings when available).
    try {
      focusActiveEditor();
      const nativeCopied = document.execCommand('copy');
      if (nativeCopied) {
        notifyPaneToast('Copied selection');
        refreshSelectionActionBar();
        return;
      }
    } catch (err) {}

    await copyToClipboard({ text: payload.text, html: payload.html }, 'Copied selection');
    refreshSelectionActionBar();
  }, [copyToClipboard, focusActiveEditor, getSelectionPayload, notifyPaneToast, refreshSelectionActionBar]);

  const handleSelectionActionCut = useCallback(async () => {
    if (isActiveTabReadOnly) return;
    const payload = getSelectionPayload();
    if (!payload) return;
    await copyToClipboard({ text: payload.text, html: payload.html }, 'Cut selection');

    const activeDocId = activeTabObj?.docId;
    const editor = activeDocId ? editorInstances.current[activeDocId] : null;
    try {
      if (editor?.chain) {
        editor.chain().focus().deleteSelection().run();
      } else {
        document.execCommand('cut');
      }
    } catch (err) {
      try { document.execCommand('cut'); } catch (error) {}
    }

    hideSelectionActionBar();
  }, [activeTabObj?.docId, copyToClipboard, getSelectionPayload, hideSelectionActionBar, isActiveTabReadOnly]);

  const handleSelectionActionPaste = useCallback(async () => {
    if (isActiveTabReadOnly) return;
    focusActiveEditor();

    const activeDocId = activeTabObj?.docId;
    const editor = activeDocId ? editorInstances.current[activeDocId] : null;

    const pasteRichSlice = ({ html, text }) => {
      if (!editor?.view?.state?.tr) return false;
      const view = editor.view;
      const state = view.state;
      const htmlText = String(html || '').trim();
      const plainText = String(text || '');
      let slice = null;

      const trySmartBoundaryInsert = (inputSlice) => {
        if (!inputSlice?.content || !state?.selection?.empty) return false;
        const $from = state.selection.$from;
        if (!$from?.parent?.isTextblock) return false;

        let hasBlockNode = false;
        for (let i = 0; i < inputSlice.content.childCount; i += 1) {
          const node = inputSlice.content.child(i);
          if (node?.isBlock) {
            hasBlockNode = true;
            break;
          }
        }
        if (!hasBlockNode) return false;

        const atStart = $from.parentOffset === 0;
        const atEnd = $from.parentOffset === $from.parent.content.size;
        if (!atStart && !atEnd) return false;

        const depth = $from.depth;
        if (depth < 1) return false;

        const insertPos = atStart ? $from.before(depth) : $from.after(depth);
        if (!Number.isFinite(insertPos)) return false;

        try {
          const tr = state.tr.replaceRange(insertPos, insertPos, inputSlice).scrollIntoView();
          view.dispatch(tr);
          return true;
        } catch (err) {
          return false;
        }
      };

      if (htmlText) {
        try {
          const container = document.createElement('div');
          container.innerHTML = htmlText;
          view.someProp('clipboardParser', (parser) => {
            if (!parser || slice) return;
            slice = parser.parseSlice(container, {
              preserveWhitespace: true,
              context: state.selection.$from
            });
          });
        } catch (err) {}
      }

      if (!slice && plainText) {
        try {
          view.someProp('clipboardTextParser', (parser) => {
            if (!parser || slice) return;
            slice = parser(plainText, state.selection.$from, true, view);
          });
        } catch (err) {}
      }

      if (!slice) return false;

      try {
        if (trySmartBoundaryInsert(slice)) return true;
        const tr = state.tr.replaceSelection(slice).scrollIntoView();
        view.dispatch(tr);
        return true;
      } catch (err) {
        return false;
      }
    };

    try {
      // Native paste preserves structured content (for example headings/lists)
      // when browser clipboard permissions and context allow it.
      try {
        const nativePasted = document.execCommand('paste');
        if (nativePasted) {
          notifyPaneToast('Pasted');
          hideSelectionActionBar();
          return;
        }
      } catch (nativeErr) {}

      let clipHtml = '';
      let clipText = '';

      const readItems = await navigator?.clipboard?.read?.();
      if (Array.isArray(readItems) && readItems.length > 0) {
        for (const item of readItems) {
          if (!clipHtml && item?.types?.includes?.('text/html')) {
            try {
              const htmlBlob = await item.getType('text/html');
              clipHtml = await htmlBlob.text();
            } catch (err) {}
          }
          if (!clipText && item?.types?.includes?.('text/plain')) {
            try {
              const textBlob = await item.getType('text/plain');
              clipText = await textBlob.text();
            } catch (err) {}
          }
          if (clipHtml && clipText) break;
        }
      }

      if (!clipText) {
        clipText = await navigator?.clipboard?.readText?.() || '';
      }

      if (!clipHtml && !clipText) {
        notifyPaneToast('Clipboard is empty');
        return;
      }

      if (editor?.chain) {
        const pasted = pasteRichSlice({ html: clipHtml, text: clipText });
        if (!pasted) {
          if (clipHtml) {
            editor.chain().focus().insertContent(String(clipHtml)).run();
          } else {
            editor.chain().focus().insertContent(String(clipText)).run();
          }
        }
      } else {
        document.execCommand('paste');
      }
      notifyPaneToast('Pasted');
      hideSelectionActionBar();
    } catch (err) {
      try {
        document.execCommand('paste');
        notifyPaneToast('Pasted');
        hideSelectionActionBar();
      } catch (error) {
        notifyPaneToast('Unable to paste');
      }
    }
  }, [activeTabObj?.docId, focusActiveEditor, hideSelectionActionBar, isActiveTabReadOnly, notifyPaneToast]);

  const handleSelectionActionPasteWithoutFormatting = useCallback(async () => {
    if (isActiveTabReadOnly) return;
    focusActiveEditor();

    const activeDocId = activeTabObj?.docId;
    const editor = activeDocId ? editorInstances.current[activeDocId] : null;

    try {
      const clipText = await navigator?.clipboard?.readText?.();
      if (!clipText) {
        notifyPaneToast('Clipboard is empty');
        return;
      }

      if (editor?.chain) {
        editor.chain().focus().insertContent(String(clipText)).run();
      } else {
        document.execCommand('paste');
      }

      notifyPaneToast('Pasted without formatting');
      hideSelectionActionBar();
    } catch (err) {
      try {
        document.execCommand('paste');
        notifyPaneToast('Pasted without formatting');
        hideSelectionActionBar();
      } catch (error) {
        notifyPaneToast('Unable to paste without formatting');
      }
    }
  }, [activeTabObj?.docId, focusActiveEditor, hideSelectionActionBar, isActiveTabReadOnly, notifyPaneToast]);

  const handleSelectionActionAiQuick = useCallback(async (action = 'rephrase') => {
    if (isActiveTabReadOnly) return;

    const activeDocId = activeTabObj?.docId;
    if (!activeDocId) return;

    const payload = getSelectionPayload();
    const selectedText = String(payload?.text || '').trim();
    if (!selectedText) {
      notifyPaneToast('Select text first');
      return;
    }

    const editor = editorInstances.current[activeDocId];
    const selection = editor?.state?.selection;
    if (!editor?.view || !selection || selection.empty) {
      notifyPaneToast('Select text first');
      return;
    }

    const config = getSelectionQuickActionConfig(action);
    const model = resolveSelectionQuickActionOutputModel();
    const prompt = buildSelectionQuickActionPrompt({
      action,
      selectedText,
      surroundingContext: payload?.surroundingContext || ''
    });

    if (!model) {
      notifyPaneToast('No AI model configured');
      return;
    }

    notifyPaneToast(`AI: ${config.label}…`);

    let requestKey = null;
    try {
      requestKey = await apiGetNextRequestKeyForDoc({ docId: activeDocId });
    } catch (requestKeyError) {
      debug('Selection quick action request key allocation failed', requestKeyError);
    }

    const startedAt = Date.now();
    try {
      const response = await apiAiChatCompletions({
        model,
        messages: [
          {
            role: 'system',
            content: 'You are an editing assistant. Return only replacement text for the user-selected content.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: config.temperature,
        max_tokens: config.maxTokens,
        stream: false
      });

      await persistSelectionQuickActionRequestLog({
        docId: activeDocId,
        requestKey,
        model,
        response,
        durationMs: Date.now() - startedAt,
        projectId: currentProject?.id,
        userId: currentUserId,
        action,
        status: 'success'
      });

      const assistantRaw = extractAssistantTextFromOpenRouter(response);
      const replacement = normalizeSelectionQuickActionResponse(assistantRaw);
      if (!replacement) {
        notifyPaneToast('No replacement returned');
        return;
      }

      if (isSelectionQuickActionNoOp({
        selectedText,
        replacementText: replacement
      })) {
        notifyPaneToast('No change returned. Try a more specific instruction.');
        return;
      }

      const lengthCheck = coerceSelectionQuickActionLength({
        action,
        selectedText,
        replacementText: replacement
      });
      if (!lengthCheck?.ok) {
        notifyPaneToast('Unable to apply this edit. Try again.');
        return;
      }
      const replacementToApply = String(lengthCheck?.replacementText || replacement || '').trim();
      if (!replacementToApply) {
        notifyPaneToast('Unable to apply this edit. Try again.');
        return;
      }

      const { state } = editor;
      const from = Number(state?.selection?.from || 0);
      const to = Number(state?.selection?.to || 0);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
        notifyPaneToast('Unable to apply replacement to current selection');
        return;
      }

      const addMarkType = state.schema?.marks?.diffSuggestionAdd;
      const removeMarkType = state.schema?.marks?.diffSuggestionRemove;
      if (!addMarkType || !removeMarkType) {
        notifyPaneToast('Suggest mode marks are unavailable');
        return;
      }

      const suggestionIdentity = createSuggestionIdentity({});
      const removeAttrs = createSuggestionMarkAttrs({
        op: 'remove',
        annotationId: suggestionIdentity.annotationId,
        batchId: suggestionIdentity.batchId,
        source: 'ai_write',
        authorId: currentUserId,
        createdAt: Date.now(),
        status: 'pending'
      });
      const addAttrs = createSuggestionMarkAttrs({
        op: 'add',
        annotationId: suggestionIdentity.annotationId,
        batchId: suggestionIdentity.batchId,
        source: 'ai_write',
        authorId: currentUserId,
        createdAt: Date.now(),
        status: 'pending'
      });

      const tr = state.tr;
      tr.addMark(from, to, removeMarkType.create(removeAttrs));
      insertMarkedReplacement({
        tr,
        state,
        from,
        to,
        replacementText: replacementToApply,
        markType: addMarkType,
        markAttrs: addAttrs
      });
      editor.view.dispatch(tr.scrollIntoView());

      const nextJson = editor.getJSON?.();
      if (!nextJson || !isValidEditorJson(nextJson)) {
        notifyPaneToast('Applied in editor, but failed to capture updated content');
        return;
      }

      let cachedNextState = null;
      setEditorStates((prev) => {
        const existing = prev?.[activeDocId] || prev?.[Number(activeDocId)] || {};
        const nextState = {
          ...existing,
          content: nextJson,
          updated_at: new Date().toISOString(),
          hasEdits: false,
          has_edits: false,
          isSaving: false,
          isSaved: true,
          saveError: null
        };
        cachedNextState = nextState;
        return {
          ...prev,
          [activeDocId]: nextState
        };
      });
      if (cachedNextState) {
        cacheDocState(activeDocId, cachedNextState);
      }

      try {
        await saveDocContent(activeDocId, nextJson, currentUserId, null, currentProject?.id, {
          reason: 'selection-action',
          requireUserInteraction: true,
          userInteractionDetected: true
        });
      } catch (saveErr) {
        debug('Selection quick action save failed', saveErr);
      }

      refreshDiffSuggestionDock(activeDocId, false);
      hideSelectionActionBar();
      notifyPaneToast(`${config.label} applied`);
    } catch (err) {
      await persistSelectionQuickActionRequestLog({
        docId: activeDocId,
        requestKey,
        model,
        response: null,
        durationMs: Date.now() - startedAt,
        projectId: currentProject?.id,
        userId: currentUserId,
        action,
        status: 'failed',
        errorMessage: String(err?.message || '').trim() || 'Selection quick action failed'
      });
      const message = classifyAiChatError(err, {
        defaultMessage: `${config.label} failed`,
        networkMessage: 'Unable to reach AI service for selection action'
      });
      notifyPaneToast(message);
    }
  }, [
    activeTabObj?.docId,
    currentProject?.id,
    currentUserId,
    getSelectionPayload,
    hideSelectionActionBar,
    isActiveTabReadOnly,
    notifyPaneToast,
    refreshDiffSuggestionDock,
    setEditorStates
  ]);

  useEffect(() => {
    if (isActiveTabReadOnly) {
      tableMenu.close();
    }
  }, [isActiveTabReadOnly, tableMenu]);

  useEffect(() => {
    currentProjectIdRef.current = currentProject?.id ?? null;
  }, [currentProject?.id]);

  useEffect(() => {
    return () => {
      dismissLoadingToastForDoc();
    };
  }, [dismissLoadingToastForDoc]);

  useEffect(() => {
    setChatRetryPayload(null);
    setIsRetryingChat(false);
  }, [activeTabId]);

  useEffect(() => {
    if (!activeTabObj?.docId) {
      hideSelectionActionBar();
      return undefined;
    }

    let rafId = 0;
    const scheduleRefresh = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        refreshSelectionActionBar();
      });
    };

    const onSelectionChange = () => {
      scheduleRefresh();
    };

    const scrollEl = editorScrollRef.current;
    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('resize', scheduleRefresh);
    window.addEventListener('scroll', scheduleRefresh, true);
    scrollEl?.addEventListener('scroll', scheduleRefresh);

    scheduleRefresh();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('resize', scheduleRefresh);
      window.removeEventListener('scroll', scheduleRefresh, true);
      scrollEl?.removeEventListener('scroll', scheduleRefresh);
    };
  }, [activeTabObj?.docId, hideSelectionActionBar, refreshSelectionActionBar]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const sourceTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
    const paneActiveTabId = pane === 'right' ? rightActiveTabId : leftActiveTabId;
    const paneActiveTab = sourceTabs.find((entry) => String(entry?.id || '') === String(paneActiveTabId || '')) || null;
    const visibleDocId = Number(paneActiveTab?.docId || 0) || null;

    const maybeApply = (context) => {
      const indicator = context?.indicator && typeof context.indicator === 'object' ? context.indicator : null;
      const indicatorDocId = Number(indicator?.docId || context?.docId || 0);
      if (!indicator || !indicatorDocId || !visibleDocId || indicatorDocId !== visibleDocId) {
        setSelectionRegionIndicator((prev) => {
          if (!prev.visible) return prev;
          if (Number(prev?.docId || 0) === visibleDocId) {
            return { visible: false, docId: null, left: 0, top: 0, height: 0 };
          }
          return prev;
        });
        return;
      }
      applySelectionRegionIndicator({ ...indicator, docId: indicatorDocId });
    };

    maybeApply(window[LAST_SELECTION_CONTEXT_WINDOW_KEY] || null);

    const handleUpdate = (event) => {
      maybeApply(event?.detail || null);
    };

    window.addEventListener('ghost-selection-context-updated', handleUpdate);
    return () => window.removeEventListener('ghost-selection-context-updated', handleUpdate);
  }, [allTabs, applySelectionRegionIndicator, leftActiveTabId, pane, rightActiveTabId, tabs]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleDocContentUpdated = (event) => {
      const detail = event?.detail;
      const updatedDocId = String(detail?.docId || '').trim();
      if (!updatedDocId) return;

      const updatedContent = detail?.content;
      if (!updatedContent || typeof updatedContent !== 'object') return;

      const updatedAt = String(detail?.updated_at || '').trim() || new Date().toISOString();

      setEditorStates((prev) => {
        const existing = prev?.[updatedDocId] || prev?.[Number(updatedDocId)];
        if (!existing) return prev;
        return {
          ...prev,
          [updatedDocId]: {
            ...existing,
            content: updatedContent,
            updated_at: updatedAt,
            hasEdits: false,
            has_edits: false
          }
        };
      });

      const existing = editorStates[updatedDocId] || editorStates[Number(updatedDocId)] || {};
      cacheDocState(updatedDocId, {
        content: updatedContent,
        layout: existing?.layout ?? null,
        name: existing?.name ?? '',
        updated_at: updatedAt,
        hasEdits: false
      });
    };

    window.addEventListener(DOC_CONTENT_UPDATED_EVENT, handleDocContentUpdated);
    return () => window.removeEventListener(DOC_CONTENT_UPDATED_EVENT, handleDocContentUpdated);
  }, [editorStates]);

  // (Reverted experimental node-attr sync) Keep simple setContent behavior.

  const isDocReadOnly = useCallback((docId) => {
    const normalizedDocId = String(docId);
    if (normalizedDocId.startsWith('version-')) return true;
    if (isProjectReadOnly) return true;
    const tab = tabs.find(t => String(t.docId) === normalizedDocId);
    return Boolean(tab?.readOnly);
  }, [tabs, isProjectReadOnly]);

  const getVersionContext = (tab) => {
    const normalizedDocId = String(tab?.docId ?? '');
    const isVersionDoc = Boolean(tab?.isVersion) || normalizedDocId.startsWith('version-');
    if (!isVersionDoc) return null;
    return {
      sourceDocId: tab?.sourceDocId,
      versionId: tab?.versionId
    };
  };

  const resolveTabLoad = useCallback(async (tab) => {
    if (!tab) return { docId: null, __loadFailed: true };
    if (loadTabsForEditor) {
      return await Promise.resolve(loadTabsForEditor(tab));
    }

    const versionContext = getVersionContext(tab);
    if (versionContext) {
      const payload = await getVersionContent({
        projectId: currentProject.id,
        docId: versionContext.sourceDocId,
        versionId: versionContext.versionId
      });
      const versionDoc = payload?.doc || {};
      return {
        docId: tab.docId,
        content: versionDoc.content || '',
        layout: versionDoc.layout || null,
        name: versionDoc.name || tab.label || '',
        updated_at: versionDoc.updated_at || payload?.savedAt || null,
      };
    }

    return await loadDocForEditor(tab.docId);
  }, [currentProject?.id, loadTabsForEditor]);

  const maybeCreateAutoVersion = useCallback(async ({ docId, state, trigger }) => {
    if (!docId || !state || !currentProject?.id) return;
    if (isEditorContentEmpty(state.content)) {
      return;
    }
    if (isDocReadOnly(docId)) {
      debug('createVersion', { type: 'log' }, `Skipped read-only docId=${docId} trigger=${trigger}`);
      return;
    }
    if (versionSaveInProgressByDoc.current[docId]) {
      debug('createVersion', { type: 'log' }, `Skipped save-in-progress docId=${docId} trigger=${trigger}`);
      return;
    }

    const signature = createContentSignature(state.content);
    if (!signature) {
      debug('createVersion', { type: 'log' }, `Skipped missing-signature docId=${docId} trigger=${trigger}`);
      return;
    }

    if (lastVersionSignatureByDoc.current[docId] === signature) {
      debug('createVersion', { type: 'log' }, `Skipped no-content-change docId=${docId} trigger=${trigger} sig=${signature}`);
      return;
    }

    if (!versionExistenceCheckedByDoc.current[docId] && !versionExistenceInFlightByDoc.current[docId]) {
      versionExistenceInFlightByDoc.current[docId] = true;
      try {
        const versions = await getVersions({ projectId: currentProject.id, docId });
        hasExistingVersionByDoc.current[docId] = Array.isArray(versions) && versions.length > 0;
        if (Array.isArray(versions) && versions.length > 0) {
          const sorted = [...versions].sort((left, right) => {
            const leftTime = left?.savedAt ? new Date(left.savedAt).getTime() : 0;
            const rightTime = right?.savedAt ? new Date(right.savedAt).getTime() : 0;
            if (rightTime !== leftTime) return rightTime - leftTime;
            return String(right?.versionId || '').localeCompare(String(left?.versionId || ''));
          });
          const latest = sorted[0];
          const latestStats = latest?.metadata?.stats;
          if (latestStats && typeof latestStats === 'object') {
            autoVersionBaselineStatsByDoc.current[docId] = normalizeDocumentStats(latestStats);
          }

          const latestSignature = latest?.contentSignature || latest?.metadata?.version?.content_signature || null;
          if (latestSignature) {
            lastVersionSignatureByDoc.current[docId] = latestSignature;
          }
        }
        versionExistenceCheckedByDoc.current[docId] = true;
      } catch (err) {
        debug('createVersion', { type: 'warn' }, `Failed version-check docId=${docId} trigger=${trigger} error=${err?.message || err}`);
      } finally {
        versionExistenceInFlightByDoc.current[docId] = false;
      }
    }

    const now = Date.now();
    if (!autoVersionAnchorAtByDoc.current[docId]) {
      autoVersionAnchorAtByDoc.current[docId] = now;
    }
    // Only increment the auto-version save checks if the change exceeds configured offset.
    const currentStats = computeDocumentStats(state.content || null);
    const previousStats = autoVersionBaselineStatsByDoc.current[docId] || null;
    const totalOffset = Number(computeOffsetFromStats(previousStats, currentStats, { signatureChanged: true }) || 0);
    const imageOffset = Math.abs(Number(currentStats?.images || 0) - Number(previousStats?.images || 0));
    const imageChanged = imageOffset > 0;
    const forceOnImageChange = AUTO_VERSION_ON_IMAGE_CHANGE && imageChanged;
    const offsetTarget = Number(AUTO_VERSION_MIN_OFFSET || 0);
    const shouldCount = (AUTO_VERSION_MIN_OFFSET <= 0) || (totalOffset >= AUTO_VERSION_MIN_OFFSET);
    const nextCount = shouldCount ? (autoVersionSaveChecksByDoc.current[docId] || 0) + 1 : (autoVersionSaveChecksByDoc.current[docId] || 0);
    autoVersionSaveChecksByDoc.current[docId] = nextCount;

    const anchor = autoVersionAnchorAtByDoc.current[docId] || now;
    const elapsedMs = now - anchor;
    const elapsedMin = elapsedMs / 60000;
    const elapsedMinTarget = AUTO_VERSION_TIME_INTERVAL_MS / 60000;
    const elapsedMinLabel = `${elapsedMin.toFixed(1)} / ${elapsedMinTarget.toFixed(1)}`;
    const charOffsetLabel = `${totalOffset} / ${offsetTarget}`;
    const imageOffsetLabel = `${imageOffset}`;
    const reachedTime = elapsedMs >= AUTO_VERSION_TIME_INTERVAL_MS;
    const reachedOffset = offsetTarget <= 0 ? true : totalOffset >= offsetTarget;
    const forceFirstVersion = versionExistenceCheckedByDoc.current[docId] && hasExistingVersionByDoc.current[docId] === false;

    if (!forceFirstVersion && !(forceOnImageChange || reachedOffset || reachedTime)) {
      debug('createVersion', { type: 'log' }, `Skipped docId=${docId} trigger=${trigger} elapsedMin=${elapsedMinLabel} charOffset=${charOffsetLabel} imageOffset=${imageOffsetLabel} count=${nextCount}/${AUTO_VERSION_SAVE_INTERVAL} gate=image|offset|time`);
      return;
    }

    versionSaveInProgressByDoc.current[docId] = true;
    try {
      const tab = tabs.find(t => String(t.docId) === String(docId));
      const result = await createVersion({
        projectId: currentProject.id,
        docId,
        snapshot: {
          content: state.content ?? null,
          layout: state.layout ?? null,
          name: state.name || tab?.label || 'Untitled',
          updated_at: state.updated_at || new Date().toISOString(),
          stats: currentStats
        },
        trigger,
        signature,
        contentOffset: totalOffset
      });

      const reason = forceFirstVersion
        ? 'firstVersion'
        : (forceOnImageChange ? 'imageOffset' : (reachedOffset ? 'offset' : 'elapsedMin'));

      lastVersionSignatureByDoc.current[docId] = signature;
      autoVersionSaveChecksByDoc.current[docId] = 0;
      autoVersionAnchorAtByDoc.current[docId] = now;
      autoVersionBaselineStatsByDoc.current[docId] = normalizeDocumentStats(currentStats);
      hasExistingVersionByDoc.current[docId] = true;
      versionExistenceCheckedByDoc.current[docId] = true;
      onVersionCreated?.({ docId, trigger, versionId: result?.versionId || null });
      debug('createVersion', { type: 'log' }, `Saved docId=${docId} trigger=${trigger} reason=${reason} elapsedMin=${elapsedMinLabel} charOffset=${charOffsetLabel} imageOffset=${imageOffsetLabel} prunedVersions=${Number(result?.pruned || 0)} prunedImages=${Number(result?.prunedImages || 0)} versionId=${result?.versionId || 'n/a'}`);
    } catch (err) {
      debug('createVersion', { type: 'warn' }, `Failed docId=${docId} trigger=${trigger} sig=${signature || 'n/a'} error=${err?.message || err}`);
    } finally {
      versionSaveInProgressByDoc.current[docId] = false;
    }
  }, [
    AUTO_VERSION_MIN_OFFSET,
    AUTO_VERSION_ON_IMAGE_CHANGE,
    AUTO_VERSION_SAVE_INTERVAL,
    AUTO_VERSION_TIME_INTERVAL_MS,
    currentProject?.id,
    isDocReadOnly,
    onVersionCreated,
    tabs
  ]);

  const snapshotEditorContent = useCallback((docId) => {
    const editor = editorInstances.current[docId];
    if (!editor) return null;
    try {
      const json = editor.getJSON?.();
      const html = editor.getHTML?.() || '';
      if (!isValidEditorJson(json)) return null;
      return {
        json,
        html
      };
    } catch (err) {
      return null;
    }
  }, []);

  const markProgrammaticEditorUpdate = useCallback((rawDocId) => {
    const docId = String(rawDocId);
    suppressNextEditorChangeByDoc.current[docId] = Date.now() + 1000;
  }, []);

  const shouldSuppressEditorChange = useCallback((rawDocId) => {
    const docId = String(rawDocId);
    const expiresAt = suppressNextEditorChangeByDoc.current[docId] || 0;
    if (expiresAt <= 0) return false;
    if (Date.now() > expiresAt) {
      delete suppressNextEditorChangeByDoc.current[docId];
      return false;
    }
    delete suppressNextEditorChangeByDoc.current[docId];
    return true;
  }, []);

  const syncDocStateToCache = useCallback((docId) => {
    if (!docId) return;
    const state = editorStates[docId] || editorStates[Number(docId)];
    if (!state) return;
    const liveContent = snapshotEditorContent(docId);
    const normalizedStateContent = normalizeEditorContent(state.content) || state.content;
    cacheDocState(docId, {
      ...state,
      content: liveContent || normalizedStateContent
    });
  }, [editorStates, snapshotEditorContent]);

  const getLatestDoc = useCallback(async (rawDocId, reason = 'manual') => {
    const docId = String(rawDocId);
    if (!docId || docId.startsWith('version-')) {
      return { docId, applied: false, skipped: 'non-db-doc' };
    }

    const state = editorStates[docId] || editorStates[Number(docId)];
    if (!state) {
      return { docId, applied: false, skipped: 'state-missing' };
    }

    if (state.hasEdits || savingInProgress.current[docId]) {
      return { docId, applied: false, skipped: 'local-edits-or-saving' };
    }

    const latest = await loadDocContent(docId);
    if (!latest) {
      return { docId, applied: false, skipped: 'load-failed' };
    }

    const latestFingerprint = computeDocFingerprint(latest?.content_json || null);
    const latestSignature = createContentSignature(latest?.content || null);
    const stateFingerprint = state?.dbFingerprint || null;
    const stateSignature = createContentSignature(state?.content || null);
    const sameFingerprint = Boolean(latestFingerprint) && Boolean(stateFingerprint) && latestFingerprint === stateFingerprint;
    const sameSignature = Boolean(latestSignature) && Boolean(stateSignature) && latestSignature === stateSignature;

    if (sameFingerprint || sameSignature) {
      return { docId, applied: false, unchanged: true };
    }

    cacheDocState(docId, {
      content: latest.content,
      layout: latest.layout,
      name: latest.name,
      updated_at: latest.updated_at,
      dbFingerprint: latestFingerprint,
      hasEdits: false
    });

    setEditorStates(prev => {
      const prevState = prev[docId];
      if (!prevState || prevState.hasEdits) return prev;
      return {
        ...prev,
        [docId]: {
          ...prevState,
          content: latest.content,
          layout: latest.layout,
          name: latest.name,
          updated_at: latest.updated_at || prevState.updated_at,
          dbFingerprint: latestFingerprint,
          hasEdits: false
        }
      };
    });

    if (latest?.name) {
      setTabs(prev => prev.map(t => String(t.docId) === docId ? { ...t, label: latest.name } : t));
    }

    const editor = editorInstances.current[docId] || editorInstances.current[Number(docId)];
    if (editor) {
      const toSet = latest.content && latest.content.json ? latest.content.json : latest.content;
      if (isValidEditorJson(toSet)) {
        markProgrammaticEditorUpdate(docId);
        try { editor.commands.setContent(toSet, false); } catch (err) { try { editor.commands.setContent(toSet); } catch (e) {} }
      }
    }

    const shouldNotifyRemoteUpdate = ['window-focus', 'visibility-change', 'pageshow', 'manual-refresh'].includes(String(reason || '').toLowerCase());
    if (shouldNotifyRemoteUpdate) {
      notifyPaneToast('Updated remotely', {
        where: { docId }
      });
    }

    return { docId, applied: true };
  }, [editorStates, setTabs, markProgrammaticEditorUpdate, notifyPaneToast]);

  const saveDoc = useCallback(async (rawDocId, reason = 'manual') => {
    const docId = String(rawDocId);
    if (!docId || docId.startsWith('version-')) {
      return { docId, saved: false, skipped: 'non-db-doc' };
    }

    const state = editorStates[docId] || editorStates[Number(docId)];
    if (!state || !state.hasEdits) {
      return { docId, saved: false, skipped: 'no-edits' };
    }

    if (isDocReadOnly(docId)) {
      return { docId, saved: false, skipped: 'read-only' };
    }

    if (savingInProgress.current[docId]) {
      return { docId, saved: false, skipped: 'save-in-progress' };
    }

    const resolveContentForSave = (candidateContent) => {
      if (!isEditorContentEmpty(candidateContent)) {
        return { content: candidateContent, repairedFromEditor: false, empty: false };
      }

      const liveEditor = editorInstances.current[docId] || editorInstances.current[Number(docId)];
      if (!liveEditor) {
        return { content: candidateContent, repairedFromEditor: false, empty: true };
      }

      try {
        const liveJson = liveEditor.getJSON?.();
        const liveHtml = liveEditor.getHTML?.() || '';
        if (isValidEditorJson(liveJson)) {
          const wrapped = { json: liveJson, html: liveHtml };
          if (!isEditorContentEmpty(wrapped)) {
            return { content: wrapped, repairedFromEditor: true, empty: false };
          }
        }
      } catch (err) {
        // Fall through to empty-candidate behavior.
      }

      return { content: candidateContent, repairedFromEditor: false, empty: true };
    };

    const resolved = resolveContentForSave(state.content);
    const contentToSave = resolved.content;

    if (resolved.repairedFromEditor) {
      cacheDocState(docId, {
        ...(state || {}),
        content: contentToSave,
        dbFingerprint: state?.dbFingerprint ?? null,
        hasEdits: true
      });
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          content: contentToSave,
          hasEdits: true
        }
      }));
    }

    const allowIntentionalEmptySave = resolved.empty && wasRecentEditInteractionMatchedForDoc(docId);

    if (resolved.empty && !allowIntentionalEmptySave) {
      debug('saveDoc', { type: 'warn' }, `reason=${reason} docId=${docId} skipped=empty-content-candidate`);
      return { docId, saved: false, skipped: 'empty-content-candidate' };
    }

    savingInProgress.current[docId] = true;
    try {
      const result = await saveDocContent(docId, contentToSave, currentUserId, null, currentProject?.id, {
        reason,
        requireUserInteraction: true,
        userInteractionDetected: wasUserInteractionDetectedForDoc(docId),
        allowDestructiveEmptySave: allowIntentionalEmptySave
      });
      if (!result) throw new Error('Save failed: empty save response');

      const newUpdatedAt = result?.updated_at || new Date().toISOString();
      const newDbFingerprint = computeDocFingerprint(result?.content_json || null);

      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          hasEdits: false,
          updated_at: newUpdatedAt,
          dbFingerprint: newDbFingerprint
        }
      }));

      cacheDocState(docId, {
        ...(state || {}),
        content: contentToSave,
        updated_at: newUpdatedAt,
        dbFingerprint: newDbFingerprint,
        hasEdits: false
      });

      onSaved?.();
      void maybeCreateAutoVersion({
        docId,
        state: {
          ...state,
          content: contentToSave,
          updated_at: newUpdatedAt
        },
        trigger: reason
      });

      debug('saveDoc', { type: 'log' }, `reason=${reason} docId=${docId} saved=true`);
      return { docId, saved: true };
    } catch (err) {
      const saveMessage = err?.saveFailure?.message;
      if (saveMessage) {
        notifyPaneToast(saveMessage);
      } else if (err?.code === 'DESTRUCTIVE_SAVE_BLOCKED') {
        notifyPaneToast('Save blocked to prevent data loss. Your previous content is preserved.');
      } else if (err?.code === 'NO_USER_INTERACTION') {
        // Intentionally silent in UI; saveDocContent already logs the reason.
      }
      debug('saveDoc', { type: 'warn' }, `reason=${reason} docId=${docId} saved=false error=${err?.message || err}`);
      return { docId, saved: false, error: err };
    } finally {
      savingInProgress.current[docId] = false;
    }
  }, [editorStates, currentUserId, currentProject?.id, onSaved, isDocReadOnly, maybeCreateAutoVersion, notifyPaneToast, wasUserInteractionDetectedForDoc, wasRecentEditInteractionMatchedForDoc]);

  useImperativeHandle(ref, () => ({
    updateTitle: (docId, newTitle) => {
      setEditorStates(prev => ({
        ...prev,
        [docId]: prev[docId] ? { ...prev[docId], title: newTitle } : prev[docId]
      }));
    },
    getActiveEditor: () => {
      const activeTab = tabs.find(t => t.id === activeTabId);
      return activeTab ? editorInstances.current[activeTab.docId] : null;
    },
    getEditorStates: () => editorStates,
    getLatestDoc: (docId, reason = 'imperative') => getLatestDoc(docId, reason),
    saveDoc: (docId, reason = 'imperative') => saveDoc(docId, reason),
    updateDocContent: (docId, content, updated_at) => {
      // Update content from remote source (only if doc exists in state)
      if (editorStates[docId]) {
        const hasUnsavedEdits = editorStates[docId].hasEdits;
        cacheDocState(docId, {
          ...editorStates[docId],
          ...(hasUnsavedEdits ? {} : { content }),
          updated_at,
          dbFingerprint: hasUnsavedEdits ? editorStates[docId]?.dbFingerprint : null,
          hasEdits: hasUnsavedEdits
        });
        setEditorStates(prev => ({
          ...prev,
          [docId]: { 
            ...prev[docId], 
            ...(hasUnsavedEdits ? {} : { content }), // Only update content if no unsaved edits
            updated_at,
            dbFingerprint: hasUnsavedEdits ? prev[docId]?.dbFingerprint : null,
            hasEdits: hasUnsavedEdits // Preserve unsaved edits flag
          }
        }));
        // Only update the editor content if there are no unsaved edits
        // to avoid disrupting the user's typing experience
        if (!hasUnsavedEdits) {
          const editor = editorInstances.current[docId];
          if (editor && content) {
            const toSet = content && content.json ? content.json : content;
            if (!isValidEditorJson(toSet)) {
              debug('[EditorPanel] Invalid editor JSON; skipping setContent', { type: 'warn' }, content);
            } else {
              markProgrammaticEditorUpdate(docId);
              try { editor.commands.setContent(toSet, false); } catch (e) { try { editor.commands.setContent(toSet); } catch (err) { debug('[EditorPanel] setContent failed', { type: 'error' }, err); } }
            }
          }
        }
      }
    },
    updateMetadata: (docId, metadata) => {
      // Update local state immediately
      setEditorStates(prev => ({
        ...prev,
        [docId]: { ...prev[docId], ...metadata, hasMetadataEdits: true }
      }));

      // Debounce the save
      if (metadataSaveTimeouts.current[docId]) {
        clearTimeout(metadataSaveTimeouts.current[docId]);
      }
      
      metadataSaveTimeouts.current[docId] = setTimeout(async () => {
        // metadata save triggered
        if (isDocReadOnly(docId)) {
          setEditorStates(prev => ({
            ...prev,
            [docId]: { ...prev[docId], hasMetadataEdits: false }
          }));
          return;
        }
        try {
          await updateDocMetadata(docId, metadata);
          setEditorStates(prev => ({
            ...prev,
            ...prev,
            [docId]: { ...prev[docId], hasMetadataEdits: false }
          }));
          onSaved?.();
        } catch (err) {
          debug('[EditorPanel] Failed to save metadata:', { type: 'error' }, err);
        }
      }, 2000);
    },
    loadExternalDoc: (docId, payload) => {
      if (!docId || !payload) return;
      cacheDocState(docId, {
        content: payload.content ?? null,
        layout: payload.layout ?? null,
        name: payload.name ?? '',
        updated_at: payload.updated_at ?? null,
        dbFingerprint: null,
        hasEdits: false
      });
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          content: payload.content ?? null,
          layout: payload.layout ?? null,
          name: payload.name ?? '',
          updated_at: payload.updated_at ?? null,
          dbFingerprint: null,
          hasEdits: false,
          viewMode: 'rich',
          diffMode: getDefaultDiffModeValue()
        }
      }));
      loadedDocsRef.current.add(docId);
    }
  }), [editorStates, onSaved, tabs, activeTabId, getLatestDoc, saveDoc, markProgrammaticEditorUpdate, isDocReadOnly]);

  useEffect(() => {
    // Determine which tabs actually need loading (not already in editorStates
    // and not previously loaded during this session)
    const missingTabs = tabs.filter(tab => {
      const rawDocId = tab.docId;
      const cacheKey = String(rawDocId);
      if (rawDocId === null || rawDocId === undefined || rawDocId === '') return false;
      const versionContext = getVersionContext(tab);
      if (versionContext) {
        if (!currentProject?.id || !versionContext.sourceDocId || !versionContext.versionId) return false;
      }
      return !editorStates[rawDocId] && !loadedDocsRef.current.has(cacheKey);
    });
    if (missingTabs.length === 0) return;

    const activeDocId = tabs.find(t => t.id === activeTabId)?.docId;
    if (activeDocId !== null && activeDocId !== undefined) {
      const isActiveDocMissing = missingTabs.some(tab => String(tab.docId) === String(activeDocId));
      if (isActiveDocMissing) {
        const activeDocIdKey = String(activeDocId);
        pendingLoadVerificationByDoc.current[activeDocIdKey] = {
          docId: activeDocIdKey,
          startedAt: Date.now(),
          retries: 0
        };
        showLoadingToastForDoc(activeDocId);
      }
    }

    // Mark these as pending so we don't re-request them immediately
    missingTabs.forEach(t => loadedDocsRef.current.add(String(t.docId)));

    const loader = (mTabs) => Promise.all(
      mTabs.map(tab =>
        Promise.resolve(resolveTabLoad(tab))
          .catch(err => {
            debug('[EditorPanel] Failed to load doc:', { type: 'error' }, err);
            return { docId: tab.docId, __loadFailed: true };
          })
      )
    );

    loader(missingTabs).then(results => {
      const activeDocIdFromTabs = tabs.find(t => t.id === activeTabId)?.docId;
      const activeDocFailed = results.some(({ docId, __loadFailed }) => __loadFailed && String(docId) === String(activeDocIdFromTabs));

      setEditorStates(prev => {
        const updates = {};
        results.forEach(({ docId, content, layout, name, updated_at, db_fingerprint, __loadFailed }) => {
          if (__loadFailed) {
            loadedDocsRef.current.delete(String(docId));
            delete pendingLoadVerificationByDoc.current[String(docId)];
            return;
          }

          const docKey = String(docId);
          if (String(activeDocIdFromTabs) === docKey) {
            pendingLoadVerificationByDoc.current[docKey] = {
              ...(pendingLoadVerificationByDoc.current[docKey] || {}),
              docId: docKey,
              expectedContent: content,
              loadedAt: Date.now(),
              retries: 0
            };
          }

          cacheDocState(docId, { content, layout, name, updated_at, dbFingerprint: db_fingerprint || null, hasEdits: false });
          updates[docId] = {
            content,
            layout,
            name,
            updated_at,
            dbFingerprint: db_fingerprint || null,
            hasEdits: false,
            viewMode: 'rich',
            diffMode: getDefaultDiffModeValue()
          };
        });
        return { ...prev, ...updates };
      });

      if (activeDocFailed) {
        dismissLoadingToastForDoc(activeDocIdFromTabs);
        notifyPaneToast("Couldn't load document. Please try again.");
      }

      // Ensure loaded layout metadata is reflected in the DOM so Options panel
      // can read the current page layout immediately. Use a short timeout so
      // editors have a chance to mount. Apply the layout class on the
      // outer `.editor` wrapper rather than the inner `.document` element.
      setTimeout(() => {
        try {
          results.forEach(({ docId, layout, __loadFailed }) => {
            if (__loadFailed) return;
            const outer = document.getElementById(`ghost-${docId}`);
            if (!outer) return;
            const cls = layout || 'pageless';
            outer.classList.remove('letter', 'a4', 'pageless');
            outer.classList.add(cls);
          });
        } catch (err) {
          debug('[EditorPanel] apply loaded layouts to DOM failed', { type: 'warn' }, err);
        }
      }, 40);
    }).catch(err => {
      // In case of error, clear markers so future attempts can retry
      missingTabs.forEach(t => loadedDocsRef.current.delete(String(t.docId)));
      missingTabs.forEach(t => {
        const docIdKey = String(t.docId);
        delete pendingLoadVerificationByDoc.current[docIdKey];
      });
      if (activeDocId !== null && activeDocId !== undefined) {
        dismissLoadingToastForDoc(activeDocId);
      }
      debug('[EditorPanel] load missingTabs failed', { type: 'error' }, err);
    });

    // If tabs were removed (closed) we should clear loadedDocsRef entries for them
    // so reopening will attempt to load again. Keep this lightweight: remove
    // any loaded doc ids not present in current tabs.
    const currentDocIds = new Set(tabs.map(t => t.docId));
    for (const id of Array.from(loadedDocsRef.current)) {
      if (!currentDocIds.has(id)) loadedDocsRef.current.delete(id);
    }
    Object.keys(lastVersionSignatureByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete lastVersionSignatureByDoc.current[id];
      }
    });
    Object.keys(autoVersionSaveChecksByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete autoVersionSaveChecksByDoc.current[id];
      }
    });
    Object.keys(autoVersionAnchorAtByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete autoVersionAnchorAtByDoc.current[id];
      }
    });
    Object.keys(autoVersionBaselineStatsByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete autoVersionBaselineStatsByDoc.current[id];
      }
    });
    Object.keys(hasExistingVersionByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete hasExistingVersionByDoc.current[id];
      }
    });
    Object.keys(versionExistenceCheckedByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete versionExistenceCheckedByDoc.current[id];
      }
    });
    Object.keys(versionExistenceInFlightByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete versionExistenceInFlightByDoc.current[id];
      }
    });
    Object.keys(versionSaveInProgressByDoc.current).forEach(id => {
      if (!currentDocIds.has(id) && !currentDocIds.has(Number(id))) {
        delete versionSaveInProgressByDoc.current[id];
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, resolveTabLoad, activeTabId, notifyPaneToast, showLoadingToastForDoc, dismissLoadingToastForDoc]);

  useEffect(() => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    const activeDocId = String(activeTab?.docId || '').trim();
    const activeToastDocId = String(activeLoadingToastRef.current?.docId || '').trim();
    if (activeToastDocId && activeToastDocId !== activeDocId) {
      dismissLoadingToastForDoc(activeToastDocId);
    }
    if (!activeDocId) {
      dismissLoadingToastForDoc();
      return;
    }

    const pending = pendingLoadVerificationByDoc.current[activeDocId];
    if (!pending) return;

    const state = editorStates[activeDocId] || editorStates[Number(activeDocId)];
    if (!state?.content) return;

    let cancelled = false;
    let timer = null;
    const expectedContent = pending.expectedContent || state.content;
    const expectedSignature = createContentSignature(expectedContent || null);
    const expectedJson = expectedContent?.json || expectedContent;
    const expectedNodeCount = Array.isArray(expectedJson?.content) ? expectedJson.content.length : 0;

    const verifyLoaded = () => {
      if (cancelled) return;

      const entry = pendingLoadVerificationByDoc.current[activeDocId] || pending;
      const editor = editorInstances.current[activeDocId] || editorInstances.current[Number(activeDocId)];
      const maxAttempts = 8;

      // Once local edits exist, never force content reconciliation for this load cycle.
      const liveState = editorStates[activeDocId] || editorStates[Number(activeDocId)];
      if (liveState?.hasEdits) {
        delete pendingLoadVerificationByDoc.current[activeDocId];
        dismissLoadingToastForDoc(activeDocId);
        return;
      }

      if (!editor || !editor.view || !editor.commands) {
        const retries = Number(entry?.retries || 0);
        if (retries >= maxAttempts) {
          delete pendingLoadVerificationByDoc.current[activeDocId];
          dismissLoadingToastForDoc(activeDocId);
          notifyPaneToast("Couldn't load document. Please try again.");
          return;
        }
        pendingLoadVerificationByDoc.current[activeDocId] = {
          ...entry,
          retries: retries + 1
        };
        timer = setTimeout(verifyLoaded, 90);
        return;
      }

      // Some tests/mocks provide a minimal editor shape without getJSON. In that
      // case we cannot verify signature parity, so treat mount completion as loaded.
      if (typeof editor.getJSON !== 'function') {
        delete pendingLoadVerificationByDoc.current[activeDocId];
        dismissLoadingToastForDoc(activeDocId);
        return;
      }

      let editorSignature = null;
      let editorNodeCount = 0;
      try {
        const currentJson = editor.getJSON?.();
        editorNodeCount = Array.isArray(currentJson?.content) ? currentJson.content.length : 0;
        editorSignature = createContentSignature(currentJson ? { json: currentJson, html: '' } : null);
      } catch (err) {
        editorSignature = null;
        editorNodeCount = 0;
      }

      if (expectedSignature && editorSignature === expectedSignature) {
        delete pendingLoadVerificationByDoc.current[activeDocId];
        dismissLoadingToastForDoc(activeDocId);
        return;
      }

      // Accept successful mount when both sides have content even if serialization
      // differs (TipTap may normalize attrs/order and produce a different signature).
      const bothHaveVisibleContent = expectedNodeCount > 0 && editorNodeCount > 0;
      const bothAreEmpty = expectedNodeCount === 0 && editorNodeCount === 0;
      if (bothHaveVisibleContent || bothAreEmpty) {
        delete pendingLoadVerificationByDoc.current[activeDocId];
        dismissLoadingToastForDoc(activeDocId);
        return;
      }

      try {
        const retries = Number(entry?.retries || 0);
        if (retries === 0 && isValidEditorJson(expectedJson)) {
          markProgrammaticEditorUpdate(activeDocId);
          editor.commands.setContent(expectedJson, false);
        }
      } catch (err) {
        // fall through to bounded retry handling
      }

      const retries = Number(entry?.retries || 0);
      if (retries >= maxAttempts) {
        delete pendingLoadVerificationByDoc.current[activeDocId];
        dismissLoadingToastForDoc(activeDocId);
        debug('loadDoc.verify', { type: 'warn' }, `docId=${activeDocId} signature-mismatch timeout; not surfacing failure toast`);
        return;
      }

      pendingLoadVerificationByDoc.current[activeDocId] = {
        ...entry,
        retries: retries + 1
      };
      timer = setTimeout(verifyLoaded, 90);
    };

    verifyLoaded();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeTabId, tabs, editorStates, dismissLoadingToastForDoc, markProgrammaticEditorUpdate, notifyPaneToast]);

  useEffect(() => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    const versionContext = getVersionContext(activeTab);
    if (!activeTab || !versionContext) return;

    const docKey = String(activeTab.docId || '');
    if (!docKey || activeVersionLoadInFlight.current[docKey]) return;
    activeVersionLoadInFlight.current[docKey] = true;

    Promise.resolve(resolveTabLoad(activeTab))
      .then(result => {
        if (!result || result.__loadFailed || !result.docId) return;
        const { docId, content, layout, name, updated_at, db_fingerprint } = result;
        cacheDocState(docId, { content, layout, name, updated_at, dbFingerprint: db_fingerprint || null, hasEdits: false });
        setEditorStates(prev => ({
          ...prev,
          [docId]: {
            ...(prev[docId] || {}),
            content,
            layout,
            name,
            updated_at,
            dbFingerprint: db_fingerprint || null,
            hasEdits: false,
            viewMode: prev[docId]?.viewMode || 'rich',
            diffMode: prev[docId]?.diffMode || getDefaultDiffModeValue()
          }
        }));
      })
      .catch(() => {
        notifyPaneToast("Couldn't load document. Please try again.");
      })
      .finally(() => {
        activeVersionLoadInFlight.current[docKey] = false;
      });
  }, [activeTabId, tabs, resolveTabLoad, notifyPaneToast]);

  useEffect(() => {
    const currentDocIds = new Set((tabs || []).map(t => String(t.docId)));
    setEditorStates(prev => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([docId, state]) => {
        const normalizedDocId = String(docId);
        if (currentDocIds.has(normalizedDocId)) {
          next[docId] = state;
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [tabs]);

  // Notify parent when active editor changes
  useEffect(() => {
    if (activeTabId && onEditorChange) {
      const activeTab = tabs.find(t => t.id === activeTabId);
      if (activeTab) {
        const editor = editorInstances.current[activeTab.docId];
        // Only call onEditorChange if editor is fully initialized with a view
        if (editor && editor.view && editor.view.dom) {
          onEditorChange(editor);
        }
      }
    }
  }, [activeTabId, tabs, onEditorChange]);

  // Use `observeAndHideDropcursor` from helpers

  // Ensure any persistent insert indicators are removed immediately when a
  // drop or dragend happens anywhere in the document. Use capture phase so
  // this runs before other handlers and prevents a lingering visual cue.
  useEffect(() => {
    document.addEventListener('drop', clearInsertIndicators, true);
    document.addEventListener('dragend', clearInsertIndicators, true);
    document.addEventListener('dragstart', restoreHiddenIndicators, true);
    document.addEventListener('dragenter', restoreHiddenIndicators, true);
    document.addEventListener('dragover', restoreHiddenIndicators, true);

    return () => {
      try { document.removeEventListener('drop', clearInsertIndicators, true); } catch (e) {}
      try { document.removeEventListener('dragend', clearInsertIndicators, true); } catch (e) {}
      try { document.removeEventListener('dragstart', restoreHiddenIndicators, true); } catch (e) {}
      try { document.removeEventListener('dragenter', restoreHiddenIndicators, true); } catch (e) {}
      try { document.removeEventListener('dragover', restoreHiddenIndicators, true); } catch (e) {}
    };
  }, []);

  // When switching/opening an active tab, keep chat tabs pinned to latest message.
  useEffect(() => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    const isChatDoc = activeTab?.docType === 'chat';
    if (!isChatDoc) return;

    const panel = editorScrollRef.current || document.getElementById(editorPanelId);
    const activeDocId = activeTab?.docId;
    const activeState = activeDocId ? (editorStates[activeDocId] || editorStates[Number(activeDocId)]) : null;
    const activeJson = activeState?.content?.json || activeState?.content || null;
    const shouldPinLatest = shouldPinChatToBottom({
      isChatDoc,
      docJson: activeJson,
      minMessages: 2
    });

    if (panel) {
      panel.scrollTop = shouldPinLatest ? panel.scrollHeight : 0;
    }

    if (activeTab) {
      const docEl = document.getElementById(`ghost-${activeTab.docId}`);
      if (docEl) {
        docEl.scrollTop = shouldPinLatest ? docEl.scrollHeight : 0;
      }
    }
  }, [activeTabId, tabs, editorPanelId, editorStates]);

  const scrollChatToBottom = useCallback(() => {
    const panel = editorScrollRef.current || (typeof document !== 'undefined' ? document.getElementById(editorPanelId) : null);
    if (!panel) return;
    panel.scrollTop = panel.scrollHeight;
  }, [editorPanelId]);

  // On initial page/tab hydration, force chat docs to the latest message once content exists.
  useEffect(() => {
    if (!tabsRestored || !isActiveChatTab) return;
    const activeDocId = String(activeTabObj?.docId || '').trim();
    if (!activeDocId) return;

    const activeState = editorStates[activeDocId] || editorStates[Number(activeDocId)];
    if (!activeState?.content) return;
    const activeJson = activeState?.content?.json || activeState?.content || null;
    const messageCount = countChatMessagesInDocJson(activeJson);
    if (messageCount <= 1) {
      initialChatScrollDoneByDoc.current.add(activeDocId);
      return;
    }
    if (initialChatScrollDoneByDoc.current.has(activeDocId)) return;

    initialChatScrollDoneByDoc.current.add(activeDocId);

    const scrollToBottom = () => {
      const panel = editorScrollRef.current || (typeof document !== 'undefined' ? document.getElementById(editorPanelId) : null);
      if (panel) {
        panel.scrollTop = panel.scrollHeight;
      }
      if (typeof document !== 'undefined') {
        const docEl = document.getElementById(`ghost-${activeDocId}`);
        if (docEl) {
          docEl.scrollTop = docEl.scrollHeight;
        }
      }
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
      return;
    }
    const timer = setTimeout(scrollToBottom, 0);
    return () => clearTimeout(timer);
  }, [activeTabObj?.docId, editorPanelId, editorStates, isActiveChatTab, tabsRestored]);

  // Auto-save when switching tabs
  useEffect(() => {
    // Check if we switched away from a tab with unsaved changes
    const previousTabId = previousActiveTabId.current;
    if (previousTabId && previousTabId !== activeTabId) {
      const previousTab = tabs.find(t => t.id === previousTabId);
      if (previousTab && editorStates[previousTab.docId]?.hasEdits && !savingInProgress.current[previousTab.docId]) {
        const docId = previousTab.docId;
        if (isDocReadOnly(docId)) {
          setEditorStates(current => ({
            ...current,
            [docId]: {
              ...current[docId],
              hasEdits: false
            }
          }));
          return;
        }
        const state = editorStates[docId];
        const allowDestructiveEmptySave = isEditorContentEmpty(state?.content) && wasRecentEditInteractionMatchedForDoc(docId);
        // Auto-saving on tab switch
        savingInProgress.current[docId] = true;
        saveDocContent(docId, state.content, currentUserId, null, currentProject?.id, {
          reason: 'tab-switch',
          requireUserInteraction: true,
          userInteractionDetected: wasUserInteractionDetectedForDoc(docId),
          allowDestructiveEmptySave
        })
          .then(result => {
            if (!result) {
              throw new Error('Save failed: empty save response');
            }
            const newUpdatedAt = result?.updated_at || new Date().toISOString();
            const newDbFingerprint = computeDocFingerprint(result?.content_json || null);
            const updatedContent = result?.updatedContent;
            setEditorStates(current => ({
              ...current,
              [docId]: { 
                ...current[docId], 
                hasEdits: false, 
                updated_at: newUpdatedAt,
                dbFingerprint: newDbFingerprint,
                // content: updatedContent  // Don't update to avoid cursor loss
              }
            }));
            cacheDocState(docId, {
              ...(state || {}),
              content: state.content,
              updated_at: newUpdatedAt,
              dbFingerprint: newDbFingerprint,
              hasEdits: false
            });
            if (editorRef && editorRef.current && updatedContent?.json && !editorRef.current.isFocused) {
              // Clear existing data-chunk attributes from DOM before setting new content
              const editorElement = (editorRef.current && editorRef.current.view && editorRef.current.view.dom) ? editorRef.current.view.dom : null;
              if (editorElement) {
                const elementsWithChunks = editorElement.querySelectorAll('[data-chunk]');
                elementsWithChunks.forEach(el => el.removeAttribute('data-chunk'));
              }
              
              try {
                const toSet = updatedContent?.json;
                if (isValidEditorJson(toSet)) {
                  markProgrammaticEditorUpdate(docId);
                  if (editorRef.current?.commands?.setContent) editorRef.current.commands.setContent(toSet, false);
                  else editorRef.current.commands.setContent(toSet);
                } else {
                  debug('[EditorPanel] Invalid updatedContent from save; skipping setContent', { type: 'warn' }, updatedContent);
                }
              } catch (e) {
                try { editorRef.current.commands.setContent(updatedContent.json); } catch (err) {}
              }
            }
            onSaved?.();
            void maybeCreateAutoVersion({
              docId,
              state: {
                ...state,
                updated_at: newUpdatedAt
              },
              trigger: 'tab-switch'
            });
          })
          .catch(err => {
            if (err?.code === 'DESTRUCTIVE_SAVE_BLOCKED') {
              notifyPaneToast('Save blocked to prevent data loss. Your previous content is preserved.');
            } else if (err?.code === 'NO_USER_INTERACTION') {
              return;
            }
            debug('[EditorPanel] Failed to save on tab switch:', { type: 'error' }, err);
          })
          .finally(() => {
            savingInProgress.current[docId] = false;
          });
      }
    }
    
    // Update the previous active tab ID
    previousActiveTabId.current = activeTabId;
  }, [activeTabId, tabs, editorStates, currentUserId, onSaved, editorRef, currentProject?.id, isDocReadOnly, markProgrammaticEditorUpdate, maybeCreateAutoVersion, notifyPaneToast, wasUserInteractionDetectedForDoc, wasRecentEditInteractionMatchedForDoc]);

  useEffect(() => {
    const handleGlobalDragStart = () => {
      const activeTab = tabs.find(t => t.id === activeTabId);
      if (!activeTab?.docId) return;
      syncDocStateToCache(activeTab.docId);
    };

    document.addEventListener('dragstart', handleGlobalDragStart, true);
    return () => {
      try { document.removeEventListener('dragstart', handleGlobalDragStart, true); } catch (err) {}
    };
  }, [tabs, activeTabId, syncDocStateToCache]);

  useEffect(() => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    const docId = activeTab?.docId;
    if (!docId) return;
    if (previousTabRefreshId.current === activeTabId) return;
    previousTabRefreshId.current = activeTabId;

    void getLatestDoc(docId, 'tab-switch').then(result => {
      if (result?.skipped === 'state-missing') return;
      const state = result?.applied
        ? 'applied'
        : result?.unchanged
        ? 'unchanged'
        : `skipped:${result?.skipped || 'unknown'}`;
      debug('getLatestDoc', { type: 'log' }, `reason=tab-switch docId=${String(docId)} result=${state}`);
    });
  }, [activeTabId, tabs, getLatestDoc]);

  const flushUnsavedDocs = useCallback((reason = 'manual-flush') => {
    const unsavedDocs = Object.keys(editorStates).filter(docId => editorStates[docId]?.hasEdits);
    if (unsavedDocs.length === 0) return Promise.resolve([]);

    const promises = [];
    unsavedDocs.forEach(docId => {
      const state = editorStates[docId];
      if (!state || !state.hasEdits || isDocReadOnly(docId)) return;
      const allowDestructiveEmptySave = isEditorContentEmpty(state?.content) && wasRecentEditInteractionMatchedForDoc(docId);
      promises.push(
        saveDocContent(docId, state.content, currentUserId, null, currentProject?.id, {
          reason,
          requireUserInteraction: true,
          userInteractionDetected: wasUserInteractionDetectedForDoc(docId),
          allowDestructiveEmptySave
        }).catch((err) => {
          if (err?.code === 'NO_USER_INTERACTION') return null;
          throw err;
        })
      );
    });

    return Promise.all(promises);
  }, [editorStates, currentUserId, currentProject?.id, isDocReadOnly, wasUserInteractionDetectedForDoc, wasRecentEditInteractionMatchedForDoc]);

  useEffect(() => {
    const handleWindowBlur = () => {
      void flushUnsavedDocs('window-blur').catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      void flushUnsavedDocs('visibility-hidden').catch(() => {});
    };

    const handlePageHide = () => {
      void flushUnsavedDocs('pagehide').catch(() => {});
    };

    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flushUnsavedDocs]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const unsavedDocs = Object.keys(editorStates).filter(docId => editorStates[docId]?.hasEdits);
      if (unsavedDocs.length > 0) {
        event.preventDefault();
        event.returnValue = '';
        void flushUnsavedDocs('before-unload').catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [editorStates, flushUnsavedDocs]);

  const handleContentChange = (docId, newContent) => {
    const normalizedDocId = String(docId || '');
    const incomingSignature = createContentSignature(newContent?.json || newContent || null);
    const lastSavedSignature = lastSavedSignatureByDoc.current[normalizedDocId] || null;
    if (incomingSignature && lastSavedSignature && incomingSignature === lastSavedSignature) {
      const existingState = editorStates[docId] || {};
      cacheDocState(docId, {
        ...existingState,
        content: newContent,
        dbFingerprint: existingState?.dbFingerprint ?? null,
        hasEdits: false
      });
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          content: newContent,
          hasEdits: false
        }
      }));
      return;
    }

    if (shouldSuppressEditorChange(docId)) {
      cacheDocState(docId, {
        ...(editorStates[docId] || {}),
        content: newContent,
        dbFingerprint: editorStates[docId]?.dbFingerprint ?? null,
        hasEdits: false
      });
      setEditorStates(prev => ({
        ...prev,
        [docId]: { ...prev[docId], content: newContent, hasEdits: false }
      }));
      return;
    }

    const previousState = editorStates[docId];
    const now = Date.now();
    const interaction = lastUserInteractionByDoc.current[normalizedDocId] || null;
    const deltaMs = interaction?.at ? Math.max(0, now - Number(interaction.at)) : null;
    const matched = deltaMs !== null && deltaMs <= 12000;

    if (
      previousState &&
      !previousState.hasEdits &&
      isEditorContentEmpty(previousState.content) &&
      isEditorContentEmpty(newContent)
    ) {
      return;
    }

    if (
      previousState &&
      !previousState.hasEdits &&
      isEditorContentEmpty(newContent) &&
      !isEditorContentEmpty(previousState.content) &&
      !matched
    ) {
      return;
    }

    if (isDocReadOnly(docId)) {
      cacheDocState(docId, {
        ...(editorStates[docId] || {}),
        content: newContent,
        dbFingerprint: editorStates[docId]?.dbFingerprint ?? null,
        hasEdits: false
      });
      setEditorStates(prev => ({
        ...prev,
        [docId]: { ...prev[docId], content: newContent, hasEdits: false }
      }));
      return;
    }

    // newContent is { json, html } from RichEditor
    lastEditInteractionMatchByDoc.current[normalizedDocId] = {
      matched,
      deltaMs,
      interactionType: interaction?.type || null,
      interactionKey: interaction?.key || null
    };

    cacheDocState(docId, {
      ...(editorStates[docId] || {}),
      content: newContent,
      dbFingerprint: editorStates[docId]?.dbFingerprint ?? null,
      hasEdits: true
    });
    setEditorStates(prev => ({
      ...prev,
      [docId]: { ...prev[docId], content: newContent, hasEdits: true }
    }));

    // Detect removed images (compare previous content -> new content) and
    // mark them soft-deleted in the DB. Non-blocking and best-effort.
    try {
      const collectImageUrlsFromContent = (raw) => {
        const urls = new Set();
        const walk = (node) => {
          if (!node || typeof node !== 'object') return;
          if (node.type === 'image' && node.attrs && node.attrs.src) {
            urls.add(String(node.attrs.src));
          }
          if (Array.isArray(node.content)) {
            node.content.forEach(walk);
          }
          if (node.json && Array.isArray(node.json.content)) {
            node.json.content.forEach(walk);
          }
        };
        walk(raw);
        return Array.from(urls);
      };

      const prevUrls = collectImageUrlsFromContent(previousState?.content?.json || previousState?.content || {});
      const newUrls = collectImageUrlsFromContent(newContent?.json || newContent || {});
      const prevSet = new Set(prevUrls || []);
      const newSet = new Set(newUrls || []);
      const removed = Array.from(prevSet).filter(u => !newSet.has(u));
      if (removed.length > 0) {
        (async () => {
          try {
            const existing = await apiGetImagesForDoc(docId).catch(() => []);
            const urlToKey = new Map((existing || []).map(i => [String(i.url || ''), i.key]));
            for (const u of removed) {
              const key = urlToKey.get(u);
              if (key) {
                apiMarkImageDeleted(key).catch(() => null);
              }
            }
          } catch (err) {
            // swallow
          }
        })();
      }
    } catch (err) {
      // swallow detection errors
    }

    // Don't set up auto-save if a save is already in progress
    // if (savingInProgress.current[docId]) {
    //   return;
    // }

    if (autoSaveTimeouts.current[docId]) clearTimeout(autoSaveTimeouts.current[docId]);
    autoSaveTimeouts.current[docId] = setTimeout(async () => {
      // Auto-saving immediately after debounce
      setEditorStates(prev => {
        const state = prev[docId];
        if (!state || !state.hasEdits) return prev;
        if (isDocReadOnly(docId)) {
          return {
            ...prev,
            [docId]: {
              ...state,
              hasEdits: false
            }
          };
        }
        
        // Check if save already in progress or too recent
        const now = Date.now();
        const lastSave = lastSaveTime.current[docId] || 0;
        if (savingInProgress.current[docId] || (now - lastSave) < 0) {
          // Save already in progress or too recent; skipping save
          return prev;
        }

        const currentSignature = createContentSignature(state?.content || null);
        const previousSavedSignature = lastSavedSignatureByDoc.current[docId] || null;
        if (currentSignature && previousSavedSignature && currentSignature === previousSavedSignature) {
          cacheDocState(docId, {
            ...(state || {}),
            content: state.content,
            dbFingerprint: state?.dbFingerprint ?? null,
            hasEdits: false
          });
          return {
            ...prev,
            [docId]: {
              ...state,
              hasEdits: false
            }
          };
        }
        
        // Mark save as in progress and record time
        savingInProgress.current[docId] = true;
        lastSaveTime.current[docId] = now;

        let contentToSave = state.content;
        if (isEditorContentEmpty(contentToSave)) {
          const liveEditor = editorInstances.current[docId] || editorInstances.current[Number(docId)];
          if (liveEditor) {
            try {
              const liveJson = liveEditor.getJSON?.();
              const liveHtml = liveEditor.getHTML?.() || '';
              if (isValidEditorJson(liveJson)) {
                const wrapped = { json: liveJson, html: liveHtml };
                if (!isEditorContentEmpty(wrapped)) {
                  contentToSave = wrapped;
                }
              }
            } catch (err) {
              // keep state.content fallback
            }
          }
        }

        const allowIntentionalEmptySave = isEditorContentEmpty(contentToSave) && wasRecentEditInteractionMatchedForDoc(docId);

        if (isEditorContentEmpty(contentToSave) && !allowIntentionalEmptySave) {
          debug('saveDoc', { type: 'warn' }, `reason=autosave docId=${docId} skipped=empty-content-candidate`);
          savingInProgress.current[docId] = false;
          return prev;
        }
        
        // Trigger save with current content
        saveDocContent(docId, contentToSave, currentUserId, null, currentProject?.id, {
          reason: 'autosave',
          requireUserInteraction: true,
          userInteractionDetected: wasUserInteractionDetectedForDoc(docId),
          allowDestructiveEmptySave: allowIntentionalEmptySave
        })
          .then(result => {
            if (!result) {
              throw new Error('Save failed: empty save response');
            }
            const newUpdatedAt = result?.updated_at || new Date().toISOString();
            const newDbFingerprint = computeDocFingerprint(result?.content_json || null);
            const updatedContent = result?.updatedContent;
            
            setEditorStates(current => ({
              ...current,
              [docId]: { 
                ...current[docId], 
                hasEdits: false, 
                updated_at: newUpdatedAt,
                dbFingerprint: newDbFingerprint,
                // content: updatedContent  // Don't update to avoid cursor loss
              }
            }));
            cacheDocState(docId, {
              ...(state || {}),
              content: contentToSave,
              updated_at: newUpdatedAt,
              dbFingerprint: newDbFingerprint,
              hasEdits: false
            });
            lastSavedSignatureByDoc.current[docId] = createContentSignature(contentToSave || null);
            
            // Update the editor content with the new server-provided content
            if (editorRef && editorRef.current && updatedContent?.json && !editorRef.current.isFocused) {
              try {
                const toSet = updatedContent?.json;
                if (isValidEditorJson(toSet)) {
                  markProgrammaticEditorUpdate(docId);
                  editorRef.current.commands.setContent(toSet, false);
                } else {
                  debug('[EditorPanel] Invalid updatedContent from save; skipping setContent', { type: 'warn' }, updatedContent);
                }
              } catch (e) {
                try { editorRef.current.commands.setContent(updatedContent.json); } catch (err) {}
              }
            }
            
            onSaved?.();
            void maybeCreateAutoVersion({
              docId,
              state: {
                ...state,
                content: contentToSave,
                updated_at: newUpdatedAt
              },
              trigger: 'autosave'
            });
            void maybeHandleDocumentRename({
              docId,
              content: contentToSave,
              docType: tabs.find(tab => String(tab?.docId) === String(docId))?.docType || null,
              trigger: 'autosave'
            });
          })
          .catch(err => {
            const saveMessage = err?.saveFailure?.message;
            if (saveMessage) {
              notifyPaneToast(saveMessage);
            } else if (err?.code === 'DESTRUCTIVE_SAVE_BLOCKED') {
              notifyPaneToast('Save blocked to prevent data loss. Your previous content is preserved.');
            } else if (err?.code === 'NO_USER_INTERACTION') {
              return;
            }
            debug('[EditorPanel] Failed to save:', { type: 'error' }, err);
          })
          .finally(() => {
            // Release lock
            savingInProgress.current[docId] = false;
          });
        
        return prev; // Don't update state here, will update after save
      });
    }, 5000);
  };

  const handleBlur = async (docId) => {
    const state = editorStates[docId] || editorStates[Number(docId)];
    if (!state || !state.hasEdits || isDocReadOnly(docId)) return;

    // Briefly wait to see where focus moved; ignore blur if focus moved into the toolbar
    await new Promise(resolve => setTimeout(resolve, 50));
    try {
      const active = document.activeElement;
      const toolbarEl = document.getElementById(toolbarId);
      const editorInstance = editorInstances.current[docId];
      const editorEl = editorInstance && editorInstance.view ? editorInstance.view.dom : null;

      if ((toolbarEl && toolbarEl.contains(active)) || (editorEl && editorEl.contains(active))) {
        // Focus moved into the toolbar or back into the editor; treat as internal, don't save.
        return;
      }
    } catch (err) {
      // ignore and proceed to save
    }

    await saveDoc(docId, 'blur');
  };

  const resolveTextblockEdgePos = (view, pos, preferEnd = false) => {
    try {
      const docSize = view?.state?.doc?.content?.size || 0;
      const safePos = Math.max(0, Math.min(Number(pos) || 0, docSize));
      const $pos = view.state.doc.resolve(safePos);
      for (let depth = $pos.depth; depth >= 0; depth -= 1) {
        const node = $pos.node(depth);
        if (node?.isTextblock) {
          return preferEnd ? $pos.end(depth) : $pos.start(depth);
        }
      }
      return safePos;
    } catch (err) {
      return Number(pos) || 0;
    }
  };

  const resolveTextblockRange = (view, pos) => {
    try {
      const docSize = view?.state?.doc?.content?.size || 0;
      const safePos = Math.max(0, Math.min(Number(pos) || 0, docSize));
      const $pos = view.state.doc.resolve(safePos);
      for (let depth = $pos.depth; depth >= 0; depth -= 1) {
        const node = $pos.node(depth);
        if (node?.isTextblock) {
          return { from: $pos.start(depth), to: $pos.end(depth) };
        }
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  const placeCaretFromMarginClick = (clickPoint, editor, preferEnd = false) => {
    try {
      const view = editor?.view;
      const dom = view?.dom;
      if (!view || !dom) return false;

      const rect = dom.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return false;

      const y = Math.max(rect.top + 2, Math.min(rect.bottom - 2, Number(clickPoint?.clientY) || rect.top + 2));
      const edgeX = preferEnd ? rect.right - 2 : rect.left + 2;

      let mapped = view.posAtCoords({ left: edgeX, top: y });
      if (!mapped?.pos) {
        const centerX = rect.left + (rect.width / 2);
        mapped = view.posAtCoords({ left: centerX, top: y });
      }
      if (!mapped?.pos) return false;

      const textSelectionPos = resolveTextblockEdgePos(view, mapped.pos, preferEnd);
      if (editor?.chain && editor?.commands?.setTextSelection) {
        editor.chain().focus().setTextSelection(textSelectionPos).run();
        return true;
      }
      if (editor?.commands?.focus) editor.commands.focus();
      if (editor?.commands?.setTextSelection) {
        editor.commands.setTextSelection(textSelectionPos);
        return true;
      }
      if (view?.focus) {
        view.focus();
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  const selectTextblockFromMarginDoubleClick = (clickPoint, editor) => {
    try {
      const view = editor?.view;
      const dom = view?.dom;
      if (!view || !dom) return false;

      const rect = dom.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return false;

      const y = Math.max(rect.top + 2, Math.min(rect.bottom - 2, Number(clickPoint?.clientY) || rect.top + 2));
      const centerX = rect.left + (rect.width / 2);
      const mapped = view.posAtCoords({ left: centerX, top: y });
      if (!mapped?.pos) return false;

      const range = resolveTextblockRange(view, mapped.pos);
      if (!range || !(range.from < range.to)) return false;

      if (editor?.chain && editor?.commands?.setTextSelection) {
        editor.chain().focus().setTextSelection({ from: range.from, to: range.to }).run();
        return true;
      }
      if (editor?.commands?.focus) editor.commands.focus();
      if (editor?.commands?.setTextSelection) {
        editor.commands.setTextSelection({ from: range.from, to: range.to });
        return true;
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  const doFocusHandoff = (event, docId, mode = 'single') => {
    const editor = editorInstances.current[docId];
    if (!editor) return;
    // Only handle clicks inside this editor wrapper, and skip interactive overlays.
    try {
      const target = event?.target || event?.srcElement;
      const currentTarget = event?.currentTarget || null;
      // Ignore clicks on the toolbar or the options button specifically
      const toolbarEl = document.getElementById(toolbarId);
      if (target && toolbarEl && toolbarEl.contains(target)) return;
      try { if (target && target.closest && target.closest('#optionsButton')) return; } catch (e) {}
      try {
        if (target && target.closest && target.closest('.contextMenu, .modal, .modalOverlay, [data-tooltip-block-parent="true"]')) {
          return;
        }
      } catch (e) {}
      if (target && currentTarget && !currentTarget.contains(target)) return;

      const editorDom = editor?.view?.dom || null;
      const isMarginClick = Boolean(target && editorDom && !editorDom.contains(target));
      if (isMarginClick && editorDom) {
        const clickPoint = {
          clientX: Number(event?.clientX) || 0,
          clientY: Number(event?.clientY) || 0
        };
        if (mode === 'double') {
          const selected = selectTextblockFromMarginDoubleClick(clickPoint, editor);
          if (selected && typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
              selectTextblockFromMarginDoubleClick(clickPoint, editor);
            });
          }
          if (selected) return;
        }
        const rect = editorDom.getBoundingClientRect();
        const midX = rect.left + (rect.width / 2);
        const preferEnd = clickPoint.clientX > midX;
        const applied = placeCaretFromMarginClick(clickPoint, editor, preferEnd);
        if (applied) {
          // Re-apply on next frame so native click handling can't keep old caret
          // when the doc is already focused.
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
              placeCaretFromMarginClick(clickPoint, editor, preferEnd);
            });
          }
          return;
        }
      }

      // Let native caret placement win for direct clicks inside editable/table content.
      try {
        if (target && target.closest) {
          if (
            target.closest('[contenteditable="true"]') ||
            target.closest('.ProseMirror') ||
            target.closest('table,td,th')
          ) {
            return;
          }
        }
      } catch (e) {}
    } catch (err) {}

    // If selection already belongs to this editor (collapsed or range), do not force-focus.
    try {
      if (typeof window !== 'undefined' && window.getSelection) {
        const sel = window.getSelection();
        if (sel && editor && editor.view && editor.view.dom) {
          const anchor = sel.anchorNode;
          const focusNode = sel.focusNode;
          const containsAnchor = anchor && editor.view.dom.contains(anchor);
          const containsFocus = focusNode && editor.view.dom.contains(focusNode);
          if (containsAnchor || containsFocus) return; // keep selection
        }
      }
    } catch (err) {}

    // Prefer focusing the editor DOM so the browser preserves click position
    // (avoids moving the caret to the end). Fall back to the command if DOM
    // focus isn't available.
    try {
      if (editor && editor.view && editor.view.focus) editor.view.focus();
      else if (editor && editor.commands && editor.commands.focus) editor.commands.focus();
    } catch (err) {
      try { if (editor && editor.commands && editor.commands.focus) editor.commands.focus(); } catch (e) {}
    }
  };

  const handlePanelScrollMouseDownCapture = (event) => {
    if (isActiveChatTab || isActiveTabReadOnly) return;
    const activeDocId = activeTabObj?.docId;
    if (!activeDocId) return;

    const target = event?.target;
    if (!target) return;

    try {
      if (target.closest && target.closest('.editor, .ProseMirror, [contenteditable="true"]')) {
        return;
      }
      if (target.closest && target.closest('button, a, input, textarea, select, label, [role="button"], .contextMenu, .modal, .modalOverlay, [data-tooltip-block-parent="true"]')) {
        return;
      }
    } catch (err) {
      // no-op
    }

    doFocusHandoff(event, activeDocId);
  };

  const handlePanelScrollDoubleClickCapture = (event) => {
    if (isActiveChatTab || isActiveTabReadOnly) return;
    const activeDocId = activeTabObj?.docId;
    if (!activeDocId) return;

    const target = event?.target;
    if (!target) return;

    try {
      if (target.closest && target.closest('.editor, .ProseMirror, [contenteditable="true"]')) {
        return;
      }
      if (target.closest && target.closest('button, a, input, textarea, select, label, [role="button"], .contextMenu, .modal, .modalOverlay, [data-tooltip-block-parent="true"]')) {
        return;
      }
    } catch (err) {
      // no-op
    }

    doFocusHandoff(event, activeDocId, 'double');
  };

  const handleVersionDrop = useCallback(async (event) => {
    if (typeof onOpenVersion !== 'function') return false;
    let payload = null;
    try {
      const raw = event?.dataTransfer?.getData?.('application/x-ghost-version');
      if (!raw) return false;
      payload = JSON.parse(raw);
    } catch (err) {
      return false;
    }

    const versionId = String(payload?.versionId || '').trim();
    const sourceDocId = String(payload?.docId || '').trim();
    if (!versionId || !sourceDocId) return false;

    setActivePane?.(pane);
    await onOpenVersion({
      versionId,
      sourceDocId,
      sourceDocName: payload?.label || null,
      preferredPane: pane
    });
    return true;
  }, [onOpenVersion, pane, setActivePane]);

  const handleEditorDrop = async (e) => {
    try {
      const activeTab = tabs.find(t => t.id === activeTabId);
      if (activeTab?.docId) {
        syncDocStateToCache(activeTab.docId);
      }
      await helperHandleEditorDrop(e, {
        tabs,
        activeTabId,
        editorInstances,
        draggedId,
        pane,
        loadDocContent,
        removeChunkAttributes,
        dropInEditor,
        onVersionDrop: handleVersionDrop
      });
    } catch (err) {
      // swallow errors to keep component lightweight
    }
  };

  const getImageFilesFromDataTransfer = (dataTransfer) => {
    if (!dataTransfer?.files) return [];
    return Array.from(dataTransfer.files || []).filter(file => file && file.type?.startsWith('image/') && file.size > 0);
  };

  const getImageFilesFromClipboard = (clipboardData) => {
    if (!clipboardData?.items) return [];
    return Array.from(clipboardData.items)
      .map(item => (item.kind === 'file' ? item.getAsFile() : null))
      .filter(file => file && file.type?.startsWith('image/') && file.size > 0);
  };

  const insertImagesIntoEditor = async (docId, files, coords = {}) => {
    if (!docId || !files || files.length === 0) return;
    const editor = editorInstances.current[docId];
    if (!editor || !editor.view) return;
    const panelElement = editorScrollRef.current || (typeof document !== 'undefined' ? document.getElementById(editorPanelId) : null);
    const panelScrollTopBeforeInsert = typeof panelElement?.scrollTop === 'number' ? panelElement.scrollTop : null;

    const projectId = currentProjectIdRef.current;
    if (!projectId) {
      debug('[EditorPanel] Missing project context for image upload', { type: 'warn' });
      notifyPaneToast('Unable to upload image: missing project context.');
      return;
    }

    let insertPos = editor.state.selection?.to ?? 0;
    if (typeof coords.clientX === 'number' && typeof coords.clientY === 'number') {
      const coordsResult = editor.view.posAtCoords({ left: coords.clientX, top: coords.clientY });
      if (coordsResult?.pos != null) {
        insertPos = coordsResult.pos;
      }
    }

    for (const file of files) {
      try {
        const upload = await createImage({
          docId,
          file,
          projectId,
          uploadedBy: currentUserId
        });
        if (!upload?.url) continue;

        const attrs = {
          src: upload.url,
          alt: file.name || '',
          width: upload.width || null,
          loading: 'lazy'
        };

        if (editor.chain) {
          editor.chain().focus().insertContentAt(insertPos, {
            type: 'image',
            attrs
          }).run();
        } else if (editor.commands?.insertContentAt) {
          editor.commands.insertContentAt(insertPos, {
            type: 'image',
            attrs
          });
        }

        if (typeof panelScrollTopBeforeInsert === 'number' && panelElement) {
          panelElement.scrollTop = panelScrollTopBeforeInsert;
        }

        insertPos = editor.state.selection?.to ?? insertPos;
      } catch (err) {
        debug('[EditorPanel] Failed to insert image', { type: 'warn' }, err);
        notifyPaneToast('Unable to insert image.');
      }
    }

    if (typeof panelScrollTopBeforeInsert === 'number' && panelElement) {
      panelElement.scrollTop = panelScrollTopBeforeInsert;
    }
  };

  const tryHandleEditorImageDrop = (event, docId) => {
    const files = getImageFilesFromDataTransfer(event?.dataTransfer);
    if (files.length === 0) return false;
    clearInsertIndicators();
    try { document.body.classList.add('ghost-hide-dropcursor'); } catch (err) {}
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();
    try { event?.dataTransfer?.clearData?.(); } catch (err) {}
    void insertImagesIntoEditor(docId, files, { clientX: event.clientX, clientY: event.clientY })
      .finally(() => {
        clearInsertIndicators();
        setTimeout(() => {
          clearInsertIndicators();
          try { document.body.classList.remove('ghost-hide-dropcursor'); } catch (err) {}
        }, 120);
      });
    return true;
  };

  const tryHandleEditorImagePaste = (event, docId) => {
    const files = getImageFilesFromClipboard(event?.clipboardData);
    if (files.length === 0) return false;
    clearInsertIndicators();
    try { document.body.classList.add('ghost-hide-dropcursor'); } catch (err) {}
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.stopImmediatePropagation?.();
    void insertImagesIntoEditor(docId, files)
      .finally(() => {
        clearInsertIndicators();
        setTimeout(() => {
          clearInsertIndicators();
          try { document.body.classList.remove('ghost-hide-dropcursor'); } catch (err) {}
        }, 120);
      });
    return true;
  };

  const attachImageHandlers = (editor, docId) => {
    if (!editor?.view?.dom) return;
    if (editorImageHandlers.current[docId]) return;
    const dom = editor.view.dom;
    const dropHandler = (event) => { tryHandleEditorImageDrop(event, docId); };
    const pasteHandler = (event) => { tryHandleEditorImagePaste(event, docId); };
    dom.addEventListener('drop', dropHandler, true);
    dom.addEventListener('paste', pasteHandler, true);
    editorImageHandlers.current[docId] = { dom, dropHandler, pasteHandler };
  };

  const detachImageHandlers = (docId) => {
    const handlers = editorImageHandlers.current[docId];
    if (!handlers) return;
    try { handlers.dom.removeEventListener('drop', handlers.dropHandler, true); } catch (err) {}
    try { handlers.dom.removeEventListener('paste', handlers.pasteHandler, true); } catch (err) {}
    delete editorImageHandlers.current[docId];
  };

  const normalizePane = useCallback((paneName) => (paneName === 'right' ? 'right' : 'left'), []);
  const oppositePane = useCallback((paneName) => (paneName === 'right' ? 'left' : 'right'), []);

  const handleAppendChatMessage = useCallback(async ({ text, orchestration } = {}) => {
    const docId = activeTabObj?.docId;
    if (!docId || !currentUserId || !currentProject?.id) return;
    if (activeTabObj?.docType !== 'chat') return;

    const input = String(text || '').trim();
    if (!input) return;

    const requestDocKey = String(docId);
    if (chatRequestInFlightByDoc.current?.[requestDocKey]) {
      debug('chat.ai.request', { type: 'warn' }, `Skipping duplicate chat submit docId=${requestDocKey}`);
      return false;
    }

    chatRequestInFlightByDoc.current = {
      ...(chatRequestInFlightByDoc.current || {}),
      [requestDocKey]: true
    };

    setChatRetryPayload(null);

    const state = editorStates[docId];
    const isFirstChatTurn = countChatMessagesInDocJson(state?.content?.json || state?.content || null) === 0;
    const templateDocId = Number(orchestration?.templateDocId || 0);
    const linkedDocIds = buildChatLinkedDocIds({ orchestration, chatOrchestrationEnabled, maxLinkedDocs });
    const mode = normalizeChatMode(orchestration?.mode || 'Explore');
    const isWriteMode = mode === 'write';
    const writeSelectionContexts = Array.isArray(orchestration?.selectionContexts)
      ? orchestration.selectionContexts
      : [];
    const hasSelectionScopedRewritePrompt = isWriteMode
      && /\b(rewrite|rephrase|reword|polish|tighten|shorten|improve|edit|fix)\b/i.test(input)
      && !/\b(add|append|expand|extend|elaborate|include|introduce|invent|new info|new information|extra context|example|examples|research|fact check|facts?|citations?|source|sources)\b/i.test(input)
      && input.length <= 180;
    const scopedRewriteSelectionContext = hasSelectionScopedRewritePrompt
      ? (writeSelectionContexts.find((entry) => Number(entry?.docId || 0) > 0 && String(entry?.selectedText || '').trim().length > 0) || null)
      : null;
    const missingSelectionForScopedRewrite = hasSelectionScopedRewritePrompt
      && !scopedRewriteSelectionContext;
    const taskKey = getAiTaskKeyForChatMode(mode);
    const resolvedModel = getResolvedModelForTask({
      taskKey,
      phase: 'response',
      requestedModelId: orchestration?.model
    });
    const intentModel = getDefaultModelForTask(taskKey, 'intent');
    const fallbackModels = getFallbackModelsForTask({
      taskKey,
      phase: 'response',
      primaryModelId: resolvedModel
    });
    const toUserFacingModelLabel = (modelId) => {
      const normalizedId = String(modelId || '').trim();
      if (!normalizedId) return null;
      return getAiModelById(normalizedId)?.label || normalizedId;
    };
    const systemPrompt = buildChatSystemPrompt(mode);

    const baseNow = Date.now();
    const userTimestamp = Math.max(baseNow, Number(lastChatMessageTimestampRef.current || 0) + 1);
    const assistantTimestamp = Number(userTimestamp) + 1;
    lastChatMessageTimestampRef.current = assistantTimestamp;
    const pendingJson = buildChatComposerNextJson({
      content: state?.content,
      userText: input,
      assistantText: '',
      assistantTimestamp,
      assistantModel: toUserFacingModelLabel(resolvedModel),
      assistantStatusVariant: 'stage',
      assistantProgressText: buildAiStageStatusText({ activeStage: 'request' }),
      now: userTimestamp,
      orchestration
    });
    let latestRenderedChatJson = pendingJson;

    const applyContentLocally = (json, { hasEdits = false } = {}) => {
      const updatedAt = new Date().toISOString();
      const nextContent = { json, html: '' };
      latestRenderedChatJson = json;
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          content: nextContent,
          updated_at: updatedAt,
          hasEdits,
          has_edits: hasEdits
        }
      }));
      cacheDocState(docId, {
        content: nextContent,
        layout: editorStates[docId]?.layout ?? null,
        name: editorStates[docId]?.name ?? '',
        updated_at: updatedAt,
        hasEdits
      });
    };

    const scrollToChatBottom = () => {
      const panel = editorScrollRef.current || (typeof document !== 'undefined' ? document.getElementById(editorPanelId) : null);
      if (!panel) return;
      panel.scrollTop = panel.scrollHeight;
    };

    try {
      const pendingSaved = await saveDocContent(docId, pendingJson, currentUserId, null, currentProject.id);
      const pendingContent = pendingSaved?.updatedContent || { json: pendingJson, html: '' };
      latestRenderedChatJson = pendingContent?.json || pendingJson;
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          content: pendingContent,
          updated_at: pendingSaved?.updated_at || new Date().toISOString(),
          hasEdits: false,
          has_edits: false
        }
      }));
      cacheDocState(docId, {
        content: pendingContent,
        layout: editorStates[docId]?.layout ?? null,
        name: editorStates[docId]?.name ?? '',
        updated_at: pendingSaved?.updated_at || new Date().toISOString(),
        hasEdits: false
      });

      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => requestAnimationFrame(scrollToChatBottom));
      } else {
        setTimeout(scrollToChatBottom, 0);
      }
    } catch (err) {
      notifyPaneToast(err?.message || 'Unable to append chat message.');
      if (chatRequestInFlightByDoc.current?.[requestDocKey]) {
        const next = { ...(chatRequestInFlightByDoc.current || {}) };
        delete next[requestDocKey];
        chatRequestInFlightByDoc.current = next;
      }
      return false;
    }

    let assistantText = '';
    let latestStreamPreview = '';
    let lastStreamRenderAt = 0;
    let activeStage = null;
    let latestStageSummary = '';
    let latestStageTimelineText = '';
    let assistantResponseModel = String(resolvedModel || '').trim() || null;
    let assistantResponseModelLabel = toUserFacingModelLabel(assistantResponseModel);
    let responsePayload = null;
    let isRequestActive = true;
    const completedStages = [];

    const applyStageStatus = (stageEvent) => {
      if (!isRequestActive) return;
      if (!chatOrchestrationEnabled) return;
      const stageKey = String(stageEvent?.stage || '').trim().toLowerCase();
      if (!stageKey) return;

      if (activeStage && activeStage !== stageKey && !completedStages.includes(activeStage)) {
        completedStages.push(activeStage);
      }
      activeStage = stageKey;

      if (latestStreamPreview) return;
      const stageSummary = buildStageSummaryFromEvent(stageEvent, stageKey);
      latestStageSummary = stageSummary;

      const timelineText = buildAiStageStatusText({
        activeStage,
        completedStages,
        stageSummary
      });
      latestStageTimelineText = timelineText;
      const stageJson = replaceChatMessageTextByTimestamp({
        content: { json: latestRenderedChatJson, html: '' },
        messageTimestamp: assistantTimestamp,
        statusVariant: 'stage',
        progressText: timelineText,
        modelLabel: assistantResponseModelLabel
      });
      applyContentLocally(stageJson, { hasEdits: true });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(scrollToChatBottom);
      }
    };

    try {
      const responsePromise = generateAssistantResponse({
        docJson: pendingJson,
        userText: input,
        orchestration: {
          ...(orchestration || {}),
          projectId: currentProject.id,
          initiatorUserId: currentUserId || null,
          activeDocId: docId,
          activeDocType: String(activeTabObj?.docType || '').trim().toLowerCase() || null,
          linkedDocIds,
          templateDocId: Number.isFinite(templateDocId) && templateDocId > 0 ? templateDocId : null
        },
        model: resolvedModel,
        intentModel,
        fallbackModels,
        systemPrompt,
        maxHistoryMessages: 20,
        temperature: 0.3,
        maxTokens: 700,
        stream: true,
        onStage: applyStageStatus,
        onStreamText: (streamText) => {
          if (!isRequestActive) return;
          latestStreamPreview = String(streamText || '').trim();
          if (isWriteMode) {
            return;
          }
          const nowMs = Date.now();
          if (!latestStreamPreview || nowMs - lastStreamRenderAt < 240) return;
          lastStreamRenderAt = nowMs;

          const streamJson = replaceChatMessageTextByTimestamp({
            content: { json: latestRenderedChatJson, html: '' },
            messageTimestamp: assistantTimestamp,
            nextText: latestStreamPreview,
            statusVariant: 'stage',
            progressText: latestStageTimelineText || null,
            modelLabel: assistantResponseModelLabel
          });

          applyContentLocally(streamJson, { hasEdits: true });
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(scrollToChatBottom);
          }
        }
      });
      const timeoutPromise = new Promise((_, reject) => {
        window.setTimeout(() => {
          const timeoutError = new Error('AI response timed out.');
          timeoutError.code = 'AI_TIMEOUT';
          reject(timeoutError);
        }, CHAT_RESPONSE_TIMEOUT_MS);
      });
      const response = await Promise.race([responsePromise, timeoutPromise]);
      responsePayload = response;
      isRequestActive = false;
      assistantText = String(response?.text || '').trim();
      assistantResponseModel = String(response?.raw?.model || resolvedModel || '').trim() || assistantResponseModel;
      assistantResponseModelLabel = toUserFacingModelLabel(assistantResponseModel);
      debug('chat.ai.response', { type: 'log' }, {
        docId,
        mode,
        requestedModel: orchestration?.model || 'default',
        resolvedModel,
        assistantText,
        responseMeta: {
          id: response?.raw?.id || null,
          model: response?.raw?.model || null,
          created: response?.raw?.created || null
        }
      });
    } catch (err) {
      isRequestActive = false;
      const { fallbackText, toastMessage } = classifyAiChatError(err, latestStreamPreview);
      const fallbackJson = replaceChatMessageTextByTimestamp({
        content: { json: latestRenderedChatJson, html: '' },
        messageTimestamp: assistantTimestamp,
        nextText: fallbackText,
        statusVariant: null,
        progressText: null,
        modelLabel: assistantResponseModelLabel,
        requestKey: String(err?.requestKey || '').trim() || null
      });
      applyContentLocally(fallbackJson, { hasEdits: false });
      try {
        await saveDocContent(docId, fallbackJson, currentUserId, null, currentProject.id);
      } catch {
        // Keep local fallback content even if persistence fails.
      }
      notifyPaneToast(toastMessage, {
        level: 'error',
        verboseMessage: err?.message || null,
        context: {
          component: 'EditorPanel',
          action: 'handleAppendChatMessage.generateAssistantResponse',
          code: String(err?.code || '') || null
        },
        error: err
      });
      setChatRetryPayload({
        text: input,
        orchestration: orchestration && typeof orchestration === 'object' ? { ...orchestration } : {}
      });
      if (chatRequestInFlightByDoc.current?.[requestDocKey]) {
        const next = { ...(chatRequestInFlightByDoc.current || {}) };
        delete next[requestDocKey];
        chatRequestInFlightByDoc.current = next;
      }
      return false;
    }

    const finalAssistantText = assistantText || latestStreamPreview;
    const writeContract = isWriteMode
      ? parseAiWriteOperationsEnvelope(finalAssistantText)
      : null;
    const parsedWriteOps = Array.isArray(writeContract?.operations) ? writeContract.operations : [];
    let shouldFallbackToExplorePresentation = isWriteMode && (!writeContract?.valid || missingSelectionForScopedRewrite);
    let renderedAssistantText = finalAssistantText;
    let assistantArtifactText = null;
    let writeSummaryMeta = null;
    if (isWriteMode) {
      debug('chat.ai.writeContract', { type: writeContract?.valid ? 'log' : 'warn' }, {
        docId,
        valid: Boolean(writeContract?.valid),
        opCount: Array.isArray(writeContract?.operations) ? writeContract.operations.length : 0,
        errors: Array.isArray(writeContract?.errors) ? writeContract.errors : [],
        missingSelectionForScopedRewrite: Boolean(missingSelectionForScopedRewrite)
      });

      if (writeContract?.valid && !missingSelectionForScopedRewrite) {
        const writeOps = parsedWriteOps;
        const opDocIds = Array.from(new Set(
          writeOps
            .map((op) => Number(op?.target_doc_id || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        ));

        if (opDocIds.length > 1) {
          shouldFallbackToExplorePresentation = true;
          debug('chat.ai.writeApply', { type: 'warn' }, {
            docId,
            reason: 'multiple_target_docs_not_supported',
            opDocIds
          });
        } else {
          const selectionContexts = Array.isArray(orchestration?.selectionContexts)
            ? orchestration.selectionContexts
            : [];
          const selectionDocId = hasSelectionScopedRewritePrompt
            ? (Number(scopedRewriteSelectionContext?.docId || 0) || null)
            : (Number(selectionContexts?.[0]?.docId || 0) || null);
          const targetDocId = opDocIds[0] || Number(linkedDocIds?.[0] || 0) || selectionDocId || null;
          const allOpenTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
          const targetTab = allOpenTabs.find((tab) => String(tab?.docId || '') === String(targetDocId || '')) || null;
          const targetItem = targetDocId ? findItem(itemsMap, targetDocId, 'doc') : null;
          const targetDocType = String(targetTab?.docType || targetItem?.docType || '').trim().toLowerCase();
          const isTargetReadOnly = Boolean(targetTab?.readOnly) || isProjectReadOnly;

          if (!targetDocId || targetDocType === 'chat' || isTargetReadOnly) {
            shouldFallbackToExplorePresentation = true;
            debug('chat.ai.writeApply', { type: 'warn' }, {
              docId,
              reason: !targetDocId
                ? 'missing_target_doc'
                : targetDocType === 'chat'
                  ? 'invalid_target_chat_doc'
                  : 'target_read_only',
              targetDocId
            });
          } else {
            let targetDocJson = normalizeEditorContent(editorStates[targetDocId]?.content)?.json
              || normalizeEditorContent(editorStates[String(targetDocId)]?.content)?.json
              || null;

            if (!targetDocJson) {
              try {
                const loaded = await loadDocContent(targetDocId);
                targetDocJson = normalizeEditorContent(loaded?.content)?.json || null;
              } catch (error) {
                targetDocJson = null;
              }
            }

            if (!targetDocJson) {
              shouldFallbackToExplorePresentation = true;
              debug('chat.ai.writeApply', { type: 'warn' }, {
                docId,
                reason: 'target_doc_load_failed',
                targetDocId
              });
            } else {
              let resolvedWriteApply = null;

              if (hasSelectionScopedRewritePrompt) {
                const matchedSelection = selectionContexts.find((entry) => Number(entry?.docId || 0) === Number(targetDocId || 0)) || null;
                const selectedText = String(matchedSelection?.selectedText || '').trim();
                const replacementContent = buildWriteFallbackReplacementContent(writeOps);
                const replacementParagraphCount = replacementContent
                  ? replacementContent.split(/\n\s*\n/).map((part) => String(part || '').trim()).filter(Boolean).length
                  : 0;
                const maxScopedChars = Math.max(240, Math.round(selectedText.length * 3));
                const scopedSizeOk = Boolean(replacementContent)
                  && replacementContent.length <= maxScopedChars
                  && replacementParagraphCount <= 3;

                if (selectedText && scopedSizeOk) {
                  const fallbackApply = applyAiWriteSelectionFallbackToDocJson({
                    docJson: targetDocJson,
                    selectedText,
                    replacementContent,
                    authorId: currentUserId,
                    createdAt: Date.now()
                  });
                  if (fallbackApply?.changed) {
                    resolvedWriteApply = fallbackApply;
                    debug('chat.ai.writeApply', { type: 'log' }, {
                      docId,
                      reason: 'scoped_rewrite_selection_only_applied',
                      targetDocId
                    });
                  }
                }

                if (!resolvedWriteApply) {
                  resolvedWriteApply = {
                    changed: false,
                    failedOps: [{ reason: 'scoped_rewrite_guard_blocked' }]
                  };
                }
              } else {
                const writeApply = applyAiWriteOperationsToDocJson({
                  docJson: targetDocJson,
                  operations: writeOps,
                  authorId: currentUserId,
                  createdAt: Date.now()
                });

                resolvedWriteApply = writeApply;
                if (!writeApply?.changed) {
                  const matchedSelection = selectionContexts.find((entry) => Number(entry?.docId || 0) === Number(targetDocId || 0)) || null;
                  const selectedText = String(matchedSelection?.selectedText || '').trim();
                  const replacementContent = buildWriteFallbackReplacementContent(writeOps);

                  if (selectedText && replacementContent) {
                    const fallbackApply = applyAiWriteSelectionFallbackToDocJson({
                      docJson: targetDocJson,
                      selectedText,
                      replacementContent,
                      authorId: currentUserId,
                      createdAt: Date.now()
                    });
                    if (fallbackApply?.changed) {
                      resolvedWriteApply = fallbackApply;
                      debug('chat.ai.writeApply', { type: 'log' }, {
                        docId,
                        reason: 'selection_fallback_applied',
                        targetDocId
                      });
                    }
                  }
                }
              }

              if (!resolvedWriteApply?.changed) {
                shouldFallbackToExplorePresentation = true;
                debug('chat.ai.writeApply', { type: 'warn' }, {
                  docId,
                  reason: 'no_operations_applied',
                  targetDocId,
                  failedOps: resolvedWriteApply?.failedOps || []
                });
              } else {
                try {
                  const savedTarget = await saveDocContent(targetDocId, resolvedWriteApply.docJson, currentUserId, null, currentProject.id);
                  const targetUpdatedContent = savedTarget?.updatedContent || { json: resolvedWriteApply.docJson, html: '' };
                  setEditorStates((prev) => {
                    const existing = prev[targetDocId] || prev[String(targetDocId)];
                    if (!existing) return prev;
                    return {
                      ...prev,
                      [targetDocId]: {
                        ...existing,
                        content: targetUpdatedContent,
                        updated_at: savedTarget?.updated_at || new Date().toISOString(),
                        hasEdits: false,
                        has_edits: false
                      }
                    };
                  });
                  cacheDocState(targetDocId, {
                    content: targetUpdatedContent,
                    layout: editorStates[targetDocId]?.layout ?? editorStates[String(targetDocId)]?.layout ?? null,
                    name: editorStates[targetDocId]?.name ?? editorStates[String(targetDocId)]?.name ?? targetItem?.name ?? '',
                    updated_at: savedTarget?.updated_at || new Date().toISOString(),
                    hasEdits: false
                  });

                  if (typeof window !== 'undefined') {
                    try {
                      window.dispatchEvent(new CustomEvent(DOC_CONTENT_UPDATED_EVENT, {
                        detail: {
                          docId: targetDocId,
                          content: targetUpdatedContent,
                          updated_at: savedTarget?.updated_at || new Date().toISOString()
                        }
                      }));
                    } catch (eventErr) {}
                  }

                  refreshDiffSuggestionDock(targetDocId, isTargetReadOnly);

                  const appliedOps = Array.isArray(resolvedWriteApply.appliedOps) ? resolvedWriteApply.appliedOps : [];
                  const lastAppliedOp = appliedOps.length > 0 ? appliedOps[appliedOps.length - 1] : null;
                  writeSummaryMeta = {
                    docId: Number(targetDocId || 0) || null,
                    annotationId: String(lastAppliedOp?.annotationId || '').trim() || null,
                    updatedAt: Date.now()
                  };

                  const targetName = String(resolveDocDisplayName(targetDocId) || targetItem?.name || `Doc ${targetDocId}`).trim();
                  renderedAssistantText = `Edited ${targetName} +${Math.max(0, Number(resolvedWriteApply.addedChars || 0))} -${Math.max(0, Number(resolvedWriteApply.removedChars || 0))}`;
                  if (Array.isArray(resolvedWriteApply.failedOps) && resolvedWriteApply.failedOps.length > 0) {
                    renderedAssistantText += `\n\n${resolvedWriteApply.failedOps.length} operation(s) could not be applied.`;
                  }
                  assistantArtifactText = null;
                } catch (error) {
                  shouldFallbackToExplorePresentation = true;
                  debug('chat.ai.writeApply', { type: 'warn' }, {
                    docId,
                    reason: 'target_doc_save_failed',
                    targetDocId,
                    error: String(error?.message || error)
                  });
                }
              }
            }
          }
        }
      }

      if (shouldFallbackToExplorePresentation) {
        const selectionContexts = Array.isArray(orchestration?.selectionContexts)
          ? orchestration.selectionContexts
          : [];
        const fallbackSelection = selectionContexts.find((entry) => {
          return Number(entry?.docId || 0) > 0 && String(entry?.selectedText || '').trim().length > 0;
        }) || null;
        const fallbackTargetDocId = (missingSelectionForScopedRewrite || hasSelectionScopedRewritePrompt)
          ? null
          : (Number(fallbackSelection?.docId || linkedDocIds?.[0] || 0) || null);
        const fallbackSelectedText = String(fallbackSelection?.selectedText || '').trim();
        const extractedFallbackArtifact = extractExploreArtifact(finalAssistantText);
        const writeFallbackFromOps = buildWriteFallbackReplacementContent(parsedWriteOps);
        const fallbackReplacementText = String(
          writeFallbackFromOps
          || (extractedFallbackArtifact?.hasArtifact
            ? (extractedFallbackArtifact?.text || extractedFallbackArtifact?.artifactText || finalAssistantText)
            : finalAssistantText)
        ).trim();

        if (fallbackTargetDocId && fallbackSelectedText && fallbackReplacementText) {
          const allOpenTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
          const targetTab = allOpenTabs.find((tab) => String(tab?.docId || '') === String(fallbackTargetDocId || '')) || null;
          const targetItem = findItem(itemsMap, fallbackTargetDocId, 'doc');
          const targetDocType = String(targetTab?.docType || targetItem?.docType || '').trim().toLowerCase();
          const isTargetReadOnly = Boolean(targetTab?.readOnly) || isProjectReadOnly;

          if (targetDocType !== 'chat' && !isTargetReadOnly) {
            let targetDocJson = normalizeEditorContent(editorStates[fallbackTargetDocId]?.content)?.json
              || normalizeEditorContent(editorStates[String(fallbackTargetDocId)]?.content)?.json
              || null;

            if (!targetDocJson) {
              try {
                const loaded = await loadDocContent(fallbackTargetDocId);
                targetDocJson = normalizeEditorContent(loaded?.content)?.json || null;
              } catch (error) {
                targetDocJson = null;
              }
            }

            if (targetDocJson) {
              const fallbackApply = applyAiWriteSelectionFallbackToDocJson({
                docJson: targetDocJson,
                selectedText: fallbackSelectedText,
                replacementContent: fallbackReplacementText,
                authorId: currentUserId,
                createdAt: Date.now()
              });

              if (fallbackApply?.changed) {
                try {
                  const savedTarget = await saveDocContent(fallbackTargetDocId, fallbackApply.docJson, currentUserId, null, currentProject.id);
                  const targetUpdatedContent = savedTarget?.updatedContent || { json: fallbackApply.docJson, html: '' };
                  setEditorStates((prev) => {
                    const existing = prev[fallbackTargetDocId] || prev[String(fallbackTargetDocId)];
                    if (!existing) return prev;
                    return {
                      ...prev,
                      [fallbackTargetDocId]: {
                        ...existing,
                        content: targetUpdatedContent,
                        updated_at: savedTarget?.updated_at || new Date().toISOString(),
                        hasEdits: false,
                        has_edits: false
                      }
                    };
                  });
                  cacheDocState(fallbackTargetDocId, {
                    content: targetUpdatedContent,
                    layout: editorStates[fallbackTargetDocId]?.layout ?? editorStates[String(fallbackTargetDocId)]?.layout ?? null,
                    name: editorStates[fallbackTargetDocId]?.name ?? editorStates[String(fallbackTargetDocId)]?.name ?? targetItem?.name ?? '',
                    updated_at: savedTarget?.updated_at || new Date().toISOString(),
                    hasEdits: false
                  });
                  refreshDiffSuggestionDock(fallbackTargetDocId, false);

                  const appliedOps = Array.isArray(fallbackApply.appliedOps) ? fallbackApply.appliedOps : [];
                  const lastAppliedOp = appliedOps.length > 0 ? appliedOps[appliedOps.length - 1] : null;
                  writeSummaryMeta = {
                    docId: Number(fallbackTargetDocId || 0) || null,
                    annotationId: String(lastAppliedOp?.annotationId || '').trim() || null,
                    updatedAt: Date.now()
                  };

                  const targetName = String(resolveDocDisplayName(fallbackTargetDocId) || targetItem?.name || `Doc ${fallbackTargetDocId}`).trim();
                  renderedAssistantText = `Edited ${targetName} +${Math.max(0, Number(fallbackApply.addedChars || 0))} -${Math.max(0, Number(fallbackApply.removedChars || 0))}`;
                  assistantArtifactText = null;
                  shouldFallbackToExplorePresentation = false;
                  debug('chat.ai.writeApply', { type: 'log' }, {
                    docId,
                    reason: 'selection_plaintext_fallback_applied',
                    targetDocId: fallbackTargetDocId
                  });
                } catch (error) {
                  debug('chat.ai.writeApply', { type: 'warn' }, {
                    docId,
                    reason: 'selection_plaintext_fallback_save_failed',
                    targetDocId: fallbackTargetDocId,
                    error: String(error?.message || error)
                  });
                }
              }
            }
          }
        }
      }
    }

    if (!isWriteMode || shouldFallbackToExplorePresentation) {
      const extractedExploreArtifact = extractExploreArtifact(finalAssistantText);
      renderedAssistantText = (isWriteMode && shouldFallbackToExplorePresentation)
        ? ((missingSelectionForScopedRewrite || hasSelectionScopedRewritePrompt)
          ? 'Select the exact text you want rewritten, then run Rephrase again. No document changes were made.'
          : getWriteModeFallbackAssistantText(finalAssistantText))
        : (extractedExploreArtifact?.text || finalAssistantText);
      assistantArtifactText = extractedExploreArtifact?.artifactText || null;
    }

    const responseRequestKey = String(responsePayload?.requestKey || '').trim() || null;
    if (writeSummaryMeta?.docId) {
      if (responseRequestKey) {
        writeSummaryByRequestKeyRef.current = {
          ...(writeSummaryByRequestKeyRef.current || {}),
          [responseRequestKey]: writeSummaryMeta
        };
      }
      writeSummaryByTimestampRef.current = {
        ...(writeSummaryByTimestampRef.current || {}),
        [String(assistantTimestamp)]: writeSummaryMeta
      };
    }
    const isWriteChipSummary = isWriteMode && !shouldFallbackToExplorePresentation && /^Edited\s+/i.test(String(renderedAssistantText || '').trim());
    const finalJson = replaceChatMessageTextByTimestamp({
      content: { json: latestRenderedChatJson, html: '' },
      messageTimestamp: assistantTimestamp,
      nextText: renderedAssistantText,
      statusVariant: isWriteChipSummary ? 'chip' : null,
      progressText: null,
      modelLabel: assistantResponseModelLabel,
      requestKey: responseRequestKey,
      artifactText: assistantArtifactText
    });

    try {
      const saved = await saveDocContent(docId, finalJson, currentUserId, null, currentProject.id);
      const nextContent = saved?.updatedContent || { json: finalJson, html: '' };
      setEditorStates(prev => ({
        ...prev,
        [docId]: {
          ...prev[docId],
          content: nextContent,
          updated_at: saved?.updated_at || new Date().toISOString(),
          hasEdits: false,
          has_edits: false
        }
      }));
      cacheDocState(docId, {
        content: nextContent,
        layout: editorStates[docId]?.layout ?? null,
        name: editorStates[docId]?.name ?? '',
        updated_at: saved?.updated_at || new Date().toISOString(),
        hasEdits: false
      });

      const completionActivityMode = 'hidden';
      if (completionActivityMode !== 'hidden' && latestStageTimelineText) {
        const completionProgressText = completionActivityMode === 'collapsed'
          ? buildAiStageCompletionSummary({
            activeStage,
            completedStages,
            stageSummary: latestStageSummary
          })
          : latestStageTimelineText;
        const withCompletionProgressJson = replaceChatMessageTextByTimestamp({
          content: nextContent,
          messageTimestamp: assistantTimestamp,
          statusVariant: 'stage',
          progressText: completionProgressText,
          modelLabel: assistantResponseModelLabel
        });
        const localCompletionContent = { json: withCompletionProgressJson, html: '' };

        setEditorStates(prev => ({
          ...prev,
          [docId]: {
            ...prev[docId],
            content: localCompletionContent,
            hasEdits: false,
            has_edits: false
          }
        }));
        cacheDocState(docId, {
          content: localCompletionContent,
          layout: editorStates[docId]?.layout ?? null,
          name: editorStates[docId]?.name ?? '',
          updated_at: new Date().toISOString(),
          hasEdits: false
        });
      }
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => requestAnimationFrame(scrollToChatBottom));
      } else {
        setTimeout(scrollToChatBottom, 0);
      }

      setChatRetryPayload(null);

      if (isFirstChatTurn) {
        const initialName = resolveDocDisplayName(docId);
        const normalizedChatMode = normalizeRenameMode(chatRenameMode, 'chat');

        if (isUntitledDefaultName(initialName, 'chat') && normalizedChatMode !== 'off') {
          try {
            const suggestedChatName = await requestChatRenameSuggestion({
              model: assistantResponseModel || resolvedModel,
              userPrompt: input,
              assistantResponse: finalAssistantText
            });

            if (shouldApplySuggestedRename({
              currentName: resolveDocDisplayName(docId),
              docType: 'chat',
              renameMode: normalizedChatMode,
              suggestedName: suggestedChatName
            })) {
              if (normalizedChatMode === 'auto') {
                const renamed = await applyResolvedRename({ docId, nextName: suggestedChatName, docType: 'chat' });
                if (renamed) {
                  showAutoRenameToast({
                    docId,
                    nextName: sanitizeSuggestedRename(suggestedChatName),
                    previousName: initialName,
                    docType: 'chat'
                  });
                }
              } else if (normalizedChatMode === 'prompt') {
                const sanitized = sanitizeSuggestedRename(suggestedChatName);
                if (sanitized) {
                  showRenamePrompt({ docId, suggestedName: suggestedChatName, docType: 'chat' });
                }
              }
            }
          } catch (err) {
            debug('chat.renameSuggestion', { type: 'warn' }, err?.message || err);
          }
        }
      }

      return true;
    } catch (err) {
      notifyPaneToast(err?.message || 'Unable to append chat message.');
      setChatRetryPayload({
        text: input,
        orchestration: orchestration && typeof orchestration === 'object' ? { ...orchestration } : {}
      });
      return false;
    } finally {
      if (chatRequestInFlightByDoc.current?.[requestDocKey]) {
        const next = { ...(chatRequestInFlightByDoc.current || {}) };
        delete next[requestDocKey];
        chatRequestInFlightByDoc.current = next;
      }
    }
  }, [
    activeTabObj,
    allTabs,
    applyResolvedRename,
    chatOrchestrationEnabled,
    chatRenameMode,
    currentProject?.id,
    currentUserId,
    editorPanelId,
    editorStates,
    isProjectReadOnly,
    itemsMap,
    maxLinkedDocs,
    notifyPaneToast,
    refreshDiffSuggestionDock,
    resolveDocDisplayName,
    showAutoRenameToast,
    showRenamePrompt,
    tabs
  ]);

  const handleChatActionClick = useCallback((event) => {
    const actionNode = event?.target?.closest?.('[data-chat-action]');
    if (!actionNode) return;

    event.preventDefault();
    event.stopPropagation();

    const messageNode = actionNode.closest('[data-message]');
    if (!messageNode) return;

    const role = String(messageNode.getAttribute('data-role') || '').trim().toLowerCase();
    const bodyNode = messageNode.querySelector('[data-message-body]');
    const messageText = String(bodyNode?.innerText || bodyNode?.textContent || '').trim();
    const messageHtml = String(bodyNode?.innerHTML || '').trim();
    const action = String(actionNode.getAttribute('data-chat-action') || '').trim().toLowerCase();

    if (!action) return;

    if (action === 'copy') {
      if (!messageText && !messageHtml) return;
      const successMessage = role === 'user' ? 'Prompt copied' : 'Response copied';
      void copyToClipboard({ text: messageText, html: messageHtml }, successMessage);
      return;
    }

    if (action === 'rerun') {
      if (role !== 'user' || !messageText) return;
      void handleAppendChatMessage({
        text: messageText,
        orchestration: chatComposerOrchestration && typeof chatComposerOrchestration === 'object'
          ? { ...chatComposerOrchestration }
          : {}
      });
      return;
    }

    if (action === 'focus-suggestion') {
      const requestKey = String(messageNode.getAttribute('data-request-key') || '').trim();
      const timestamp = String(messageNode.getAttribute('data-timestamp') || '').trim();
      const fromRequest = requestKey ? writeSummaryByRequestKeyRef.current?.[requestKey] : null;
      const fromTimestamp = timestamp ? writeSummaryByTimestampRef.current?.[timestamp] : null;
      const summary = fromRequest || fromTimestamp || null;
      const targetDocId = Number(summary?.docId || 0);
      if (!targetDocId) {
        notifyPaneToast('Suggestion target not available');
        return;
      }

      const sourceTabs = Array.isArray(allTabs) && allTabs.length > 0 ? allTabs : tabs;
      const targetTab = sourceTabs.find((tab) => Number(tab?.docId || 0) === targetDocId) || null;
      if (Boolean(targetTab?.readOnly) || isProjectReadOnly) {
        notifyPaneToast('Target document is read-only');
        return;
      }

      onOpenProjectDocRef.current?.(targetDocId, isActiveChatTab ? 'new-tab-opposite' : 'this-tab');
      window.setTimeout(() => {
        const preferredAnnotationId = String(summary?.annotationId || '').trim() || null;
        const entries = collectDiffSuggestionEntries(targetDocId);
        if (!shouldAutoFocusChatSuggestion({ preferredAnnotationId, entries })) {
          return;
        }
        diffSuggestionNextFocusModeRef.current = 'range';
        refreshDiffSuggestionDock(targetDocId, false, {
          preferredAnnotationId
        });
      }, 140);
      return;
    }

    if (action === 'helpful' || action === 'unhelpful') {
      const helpfulButton = messageNode.querySelector('[data-chat-action="helpful"]');
      const unhelpfulButton = messageNode.querySelector('[data-chat-action="unhelpful"]');

      const isHelpfulSelected = helpfulButton?.classList?.contains('active');
      const isUnhelpfulSelected = unhelpfulButton?.classList?.contains('active');
      const selectingHelpful = action === 'helpful' && !isHelpfulSelected;
      const selectingUnhelpful = action === 'unhelpful' && !isUnhelpfulSelected;

      if (helpfulButton?.classList) {
        helpfulButton.classList.toggle('active', selectingHelpful);
      }
      if (unhelpfulButton?.classList) {
        unhelpfulButton.classList.toggle('active', selectingUnhelpful);
      }

      if (selectingHelpful) {
        notifyPaneToast('Marked as helpful');
      } else if (selectingUnhelpful) {
        notifyPaneToast('Marked as unhelpful');
      }

      const requestKey = String(messageNode.getAttribute('data-request-key') || '').trim();
      const activeDocId = Number(activeTabObj?.docId || selectedDocId || 0);
      const feedbackScore = selectingHelpful ? 1 : selectingUnhelpful ? -1 : null;
      if (requestKey && Number.isFinite(activeDocId) && activeDocId > 0 && Number.isFinite(Number(feedbackScore))) {
        void apiSetChatRequestFeedback({
          chatDocId: activeDocId,
          requestKey,
          feedbackScore
        }).catch((error) => {
          debug('[EditorPanel] set chat feedback failed', { type: 'warn' }, error?.message || error);
        });
      }
    }
  }, [activeTabObj?.docId, allTabs, chatComposerOrchestration, collectDiffSuggestionEntries, copyToClipboard, handleAppendChatMessage, isActiveChatTab, isProjectReadOnly, notifyPaneToast, refreshDiffSuggestionDock, selectedDocId, tabs]);

  const handleRetryLastChatMessage = useCallback(async () => {
    if (!isActiveChatTab) return;
    if (!chatRetryPayload?.text) return;
    if (isRetryingChat) return;

    setIsRetryingChat(true);
    try {
      await handleAppendChatMessage({
        text: chatRetryPayload.text,
        orchestration: chatRetryPayload.orchestration || {}
      });
    } finally {
      setIsRetryingChat(false);
    }
  }, [chatRetryPayload, handleAppendChatMessage, isActiveChatTab, isRetryingChat]);

  const onOpenProjectDoc = useCallback((docId, target = 'this-tab') => {
    if (!docId) return;

    const baseTabs = Array.isArray(allTabs) ? allTabs : (tabs || []);
    const thisPane = normalizePane(pane);
    const targetPane = target === 'new-tab-opposite' ? oppositePane(thisPane) : thisPane;
    if (target === 'new-tab-opposite') {
      setSplitScreen?.(true);
    }
    const docItem = findItem(itemsMap, docId, 'doc') || { id: docId, type: 'doc', name: '' };

    const getPaneTabs = (list, paneName) => list.filter(t => normalizePane(t.pane) === paneName);
    const getFallbackActiveId = (list, paneName, removedTabId = null) => {
      const paneTabs = getPaneTabs(list, paneName);
      if (paneTabs.length === 0) return null;
      if (!removedTabId) return paneTabs[0]?.id || null;
      const removedIndex = paneTabs.findIndex(t => t.id === removedTabId);
      const remaining = paneTabs.filter(t => t.id !== removedTabId);
      if (remaining.length === 0) return null;
      if (removedIndex > 0) return remaining[Math.min(removedIndex - 1, remaining.length - 1)]?.id || null;
      return remaining[0]?.id || null;
    };

    const setPaneActive = (paneName, tabId) => {
      if (paneName === 'right') setRightActiveTabId?.(tabId || null);
      else setLeftActiveTabId?.(tabId || null);
    };

    const withDocPayload = (tab, paneName) => ({
      ...tab,
      pane: paneName,
      docId: docItem.id,
      label: docItem.name || 'Untitled Doc',
      readOnly: Boolean(docItem.readOnly) || docItem.docType === 'chat',
      toolbarVariant: docItem.toolbarVariant || null,
      docType: docItem.docType || null
    });

    const existingTab = baseTabs.find(t => String(t.docId) === String(docId));

    if (target === 'this-tab') {
      const thisPaneTabs = getPaneTabs(baseTabs, thisPane);
      const replaceTargetTab = thisPaneTabs.find(t => t.id === activeTabId) || thisPaneTabs[0] || null;

      let nextTabs = baseTabs;
      let tabIdToActivate = null;
      let activeResetPane = null;
      let activeResetTabId = null;

      if (replaceTargetTab) {
        if (existingTab && existingTab.id !== replaceTargetTab.id) {
          const removedPane = normalizePane(existingTab.pane);
          nextTabs = nextTabs.filter(t => t.id !== existingTab.id);
          if (removedPane !== thisPane) {
            activeResetPane = removedPane;
            activeResetTabId = getFallbackActiveId(nextTabs, removedPane, existingTab.id);
          }
        }

        nextTabs = nextTabs.map(t => (
          t.id === replaceTargetTab.id
            ? withDocPayload(t, thisPane)
            : t
        ));
        tabIdToActivate = replaceTargetTab.id;
      } else if (existingTab) {
        const fromPane = normalizePane(existingTab.pane);
        nextTabs = nextTabs.map(t => (
          t.id === existingTab.id
            ? withDocPayload(t, thisPane)
            : t
        ));
        if (fromPane !== thisPane) {
          activeResetPane = fromPane;
          activeResetTabId = getFallbackActiveId(nextTabs, fromPane, existingTab.id);
        }
        tabIdToActivate = existingTab.id;
      } else {
        const newTab = { ...createTab(docItem), pane: thisPane };
        nextTabs = [...nextTabs, newTab];
        tabIdToActivate = newTab.id;
      }

      setTabs(nextTabs);
      if (activeResetPane) setPaneActive(activeResetPane, activeResetTabId);
      setPaneActive(thisPane, tabIdToActivate);
      setActivePane?.(thisPane);
      return;
    }

    let nextTabs = baseTabs;
    let tabIdToActivate = null;
    let sourcePaneReset = null;

    if (existingTab) {
      const sourcePane = normalizePane(existingTab.pane);
      if (sourcePane !== targetPane) {
        nextTabs = nextTabs.map(t => (
          t.id === existingTab.id
            ? { ...t, pane: targetPane }
            : t
        ));
        sourcePaneReset = {
          pane: sourcePane,
          tabId: getFallbackActiveId(nextTabs, sourcePane, existingTab.id)
        };
      }
      tabIdToActivate = existingTab.id;
    } else {
      const newTab = { ...createTab(docItem), pane: targetPane };
      nextTabs = [...nextTabs, newTab];
      tabIdToActivate = newTab.id;
    }

    setTabs(nextTabs);
    if (sourcePaneReset) setPaneActive(sourcePaneReset.pane, sourcePaneReset.tabId);
    setPaneActive(targetPane, tabIdToActivate);
    setActivePane?.(targetPane);
  }, [activeTabId, allTabs, tabs, pane, itemsMap, setTabs, setSplitScreen, setLeftActiveTabId, setRightActiveTabId, setActivePane, normalizePane, oppositePane]);

  useEffect(() => {
    onOpenProjectDocRef.current = onOpenProjectDoc;
  }, [onOpenProjectDoc]);

  const renderToolbar = () => {
    if (tabs.length === 0) return null;
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) return null;
    if (activeTab.docType === 'chat') return null;
    const state = editorStates[activeTab.docId];
    if (!state) return null;

    const docIsReadOnly = Boolean(activeTab.readOnly) || isProjectReadOnly;
    const toolbarVariant = activeTab.toolbarVariant || (docIsReadOnly ? 'readOnly' : 'default');

    const toolbarProps = {
      id: toolbarId,
      editor: editorInstances.current[activeTab.docId] || null,
      viewMode: state.viewMode,
      diffMode: state.diffMode || getDefaultDiffModeValue(),
      setViewMode: (mode) => {
        setEditorStates(prev => ({
          ...prev,
          [activeTab.docId]: { ...prev[activeTab.docId], viewMode: mode }
        }));
      },
      onDiffModeChange: (mode) => {
        const normalized = normalizeDiffModeValue(mode);
        setEditorStates(prev => ({
          ...prev,
          [activeTab.docId]: { ...prev[activeTab.docId], diffMode: normalized }
        }));
      },
      setContent: (content) => {
        setEditorStates(prev => ({
          ...prev,
          [activeTab.docId]: { ...prev[activeTab.docId], content }
        }));
      },
      projectId: currentProject?.id || null,
      itemsMap,
      onOpenProjectDoc
    };

    if (toolbarVariant === 'version') {
      return (
        <ReadOnlyToolBar
          {...toolbarProps}
          variant="version"
          onCompareVersion={() => debug('[EditorPanel] version compare action not implemented', { type: 'warn' })}
          onRestoreVersion={() => onRestoreVersion?.(activeTab)}
          onRestoreVersionCopy={() => onRestoreVersionCopy?.(activeTab)}
        />
      );
    }

    if (toolbarVariant === 'readOnly') {
      return <ReadOnlyToolBar {...toolbarProps} />;
    }

    return <ToolBar {...toolbarProps} />;
  };

  return (
    <div 
      id={editorPanelId} 
      className={panelClassName}
      onDragOver={e => e.preventDefault()}
      onDrop={handleEditorDrop}
      onClick={() => {
        setActivePane?.(pane);
        // If this pane has tabs but no active tab, activate the first one
        if (tabs.length > 0 && !activeTabId && onEditorChange) {
          const firstTab = tabs[0];
          const editor = editorInstances.current[firstTab.docId];
          if (editor && editor.view && editor.view.dom) {
            onEditorChange(editor);
          }
        }
      }}
    >
      {/* Render toolbar once for active tab */}
      {renderToolbar()}

      <div
        className={`editorPanelScroll ${isActiveChatTab ? 'chat' : ''}`.trim()}
        ref={editorScrollRef}
        onMouseDownCapture={handlePanelScrollMouseDownCapture}
        onDoubleClickCapture={handlePanelScrollDoubleClickCapture}
      >
      {tabs.length === 0 && (
        <EditorPlaceholder
          pane={pane}
          currentProject={currentProject}
          currentUserId={currentUserId}
          itemsMap={itemsMap}
          setItemsMap={setItemsMap}
          allTabs={allTabs}
          tabs={tabs}
          setTabs={setTabs}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          setLeftActiveTabId={setLeftActiveTabId}
          setRightActiveTabId={setRightActiveTabId}
          setActivePane={setActivePane}
          editorInstances={editorInstances}
          editorRef={editorRef}
          isLoading={isPaneLoading}
          isProjectReadOnly={isProjectReadOnly}
        />
      )}

      {tabs.map(tab => {
        const state = editorStates[tab.docId];
        if (!state) return null;

        const isActive =
          activeTabId === tab.id
          && (
            selectedDocId == null
            || String(tab.docId) === String(selectedDocId)
          );

        const isEmptyEditorState = isEditorContentEmpty(state.content?.json || state.content);

        const docReadOnly = Boolean(tab.readOnly) || isProjectReadOnly;

        // ensure a stable setEditorInstance callback per doc so its identity
        // doesn't change across renders (prevents useEffect re-runs in child)
        let setEditorInstanceFn = setEditorInstanceFns.current[tab.docId];
        if (!setEditorInstanceFn) {
          setEditorInstanceFn = (editor) => {
            // Only store/expose the editor instance once its DOM is mounted.
            if (editor) {
              if (editor.view && editor.view.dom) {
                if (editorInstances.current[tab.docId] !== editor) {
                  editorInstances.current[tab.docId] = editor;
                  forceUpdate({});
                  // Notify parent if this is the active tab
                  if (tab.id === activeTabId) {
                    onEditorChange?.(editor);
                  }
                }

                // Attach capture-phase handlers to the editor DOM to prevent
                // the browser from inserting plain text (the doc id) before
                // our programmatic insertion runs.
                try {
                    attachImageHandlers(editor, tab.docId);
                  } catch (err) {}
                  try {
                  try { console.debug('[editor-panel] attaching DOM handlers for', tab.docId); } catch (e) {}
                  attachEditorDomHandlers(editor, editorDomHandlers, tab.docId, handleEditorDrop);
                } catch (err) { try { console.error('[editor-panel] attachEditorDomHandlers error', err); } catch (e) {} }
              } else {
                // Editor exists but not yet mounted; ignore until mount
                return;
              }
            } else {
              // Clear instance when unmounted
              if (editorInstances.current[tab.docId]) {
                delete editorInstances.current[tab.docId];
                forceUpdate({});
                if (tab.id === activeTabId) onEditorChange?.(null);
              }
              // Remove any attached DOM handlers for this doc
                try { try { console.debug('[editor-panel] detaching DOM handlers for', tab.docId); } catch (e) {} detachEditorDomHandlers(editorDomHandlers, tab.docId); } catch (err) { try { console.error('[editor-panel] detachEditorDomHandlers error', err); } catch (e) {} }
                try { detachImageHandlers(tab.docId); } catch (err) {}
            }
          };
          setEditorInstanceFns.current[tab.docId] = setEditorInstanceFn;
        }

        return (
          <div
            key={`${tab.docId}-${tab.id}`}
            id={`ghost-${tab.docId}`}
            className={`editor ${state.layout || 'pageless'} ${tab.docType === 'chat' ? 'chat' : ''} ${isEmptyEditorState ? 'empty' : ''}`.trim()}
            style={{ display: isActive ? 'inline-block' : 'none' }}
            onClickCapture={tab.docType === 'chat' ? handleChatActionClick : undefined}
            onMouseMoveCapture={(event) => {
              if (tab.docType === 'chat') return;
              refreshDiffSuggestionInlineBar(event, tab.docId, docReadOnly);
            }}
            onTouchStart={e => {
              try {
                const t = e.touches && e.touches[0];
                if (t) touchInfo.current[tab.docId] = { startX: t.clientX, startY: t.clientY, moved: false };
              } catch (err) {}
            }}
            onContextMenu={isActiveTabReadOnly ? undefined : tableMenu.onContextMenu}
            onTouchMove={e => {
              try {
                const t = e.touches && e.touches[0];
                const info = touchInfo.current[tab.docId];
                if (t && info) {
                  if (Math.abs(t.clientX - info.startX) > 10 || Math.abs(t.clientY - info.startY) > 10) info.moved = true;
                }
              } catch (err) {}
            }}
            onTouchEnd={e => {
              try {
                // Record that a touch occurred so we can ignore the subsequent
                // synthetic mouse event that Chrome emits after touch.
                touchHandledAt.current[tab.docId] = Date.now();
                // Don't run focus handoff on touchend; let native tap/click
                // behavior place the caret. We still clear touch tracking.
                delete touchInfo.current[tab.docId];
              } catch (err) { delete touchInfo.current[tab.docId]; }
            }}
            onMouseUp={() => {
              // Intentionally no focus handoff on mouseup.
              // Let ProseMirror/browser native caret placement win,
              // especially inside table cells.
            }}
            onMouseDownCapture={e => {
              handleDiffSuggestionSelectFromEvent(e, tab.docId, docReadOnly);
              if (tab.docType === 'chat' || docReadOnly) return;
              clearSelectionContextForDoc(tab.docId);
              doFocusHandoff(e, tab.docId);
            }}
            onDoubleClickCapture={e => {
              if (tab.docType === 'chat' || docReadOnly) return;
              doFocusHandoff(e, tab.docId, 'double');
            }}
          >
            <RichEditor
              content={state.content?.json || state.content}
              onChange={newContent => handleContentChange(tab.docId, newContent)}
              onUserInteraction={interaction => markUserInteraction(tab.docId, interaction)}
              onBlur={() => handleBlur(tab.docId)}
              setEditorInstance={setEditorInstanceFn}
              htmlMode={state.viewMode === 'html'}
              diffMode={state.diffMode || getDefaultDiffModeValue()}
              readOnly={docReadOnly}
              projectId={currentProject?.id || null}
              itemsMap={itemsMap}
              onOpenProjectDoc={onOpenProjectDoc}
              currentUserId={currentUserId}
              docId={tab.docId}
              pane={pane}
            />
          </div>
        );
      })}
      {!isActiveTabReadOnly && (
        <ContextMenuTables
          editor={activeEditorInstance}
          anchorRef={tableMenu.anchorRef}
          isOpen={tableMenu.isOpen}
          diffMode={activeDiffMode}
          currentUserId={currentUserId}
          onClose={tableMenu.close}
        />
      )}
      </div>
      {isActiveChatTab && (
        <>
          {chatRetryPayload?.text && (
            <button
              type="button"
              className="textIconButton chatRetryFloating"
              onClick={handleRetryLastChatMessage}
              disabled={isRetryingChat}
              title="Retry last AI response"
            >
              <Icon icon={'arrow-clockwise'} className="icon" />
              <span>{isRetryingChat ? 'Retrying...' : 'Retry'}</span>
            </button>
          )}
          <ChatComposer
            docId={activeTabObj?.docId}
            currentUserId={currentUserId}
            onSubmit={handleAppendChatMessage}
            onOrchestrationChange={handleChatComposerOrchestrationChange}
            onFocusComposer={scrollChatToBottom}
            resolveSelectionReference={resolveSelectionReferenceForComposer}
            orchestrationEnabled={chatOrchestrationEnabled}
            linkedDocOptions={linkedDocChipOptions}
            maxLinkedDocs={maxLinkedDocs}
            resolveLinkedDocOption={resolveLinkedDocOption}
            draggedId={draggedId}
          />
        </>
      )}
      {selectionRegionIndicator.visible && (
        <div
          className="selectionRegionIndicator"
          style={{
            left: `${selectionRegionIndicator.left}px`,
            top: `${selectionRegionIndicator.top}px`,
            height: `${selectionRegionIndicator.height}px`
          }}
          aria-hidden="true"
        />
      )}
      {selectionActionBar.visible && (
        <div
          className="selectionActionBar"
          style={{ left: `${selectionActionBar.left}px`, top: `${selectionActionBar.top}px` }}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={handleSelectionActionCopy}
            data-tooltip="Copy"
            data-tooltip-placement="top"
            aria-label="Copy selection"
          >
            <Icon icon={'copy'} className="icon small copy" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={handleSelectionActionCut}
            disabled={isActiveTabReadOnly}
            data-tooltip="Cut"
            data-tooltip-placement="top"
            aria-label="Cut selection"
          >
            <Icon icon={'scissors'} className="icon small scissors" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={handleSelectionActionPaste}
            disabled={isActiveTabReadOnly}
            data-tooltip="Paste"
            data-tooltip-placement="top"
            aria-label="Paste"
          >
            <Icon icon={'clipboard-text'} className="icon small clipboard-text" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={handleSelectionActionPasteWithoutFormatting}
            disabled={isActiveTabReadOnly}
            data-tooltip="Paste without formatting"
            data-tooltip-placement="top"
            aria-label="Paste without formatting"
          >
            <Icon icon={'clipboard'} className="icon small clipboard" />
          </button>
          <div className="toolbar-divider" aria-hidden="true" />
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={() => handleSelectionActionAiQuick('rephrase')}
            disabled={isActiveTabReadOnly}
            data-tooltip="Rephrase"
            data-tooltip-placement="top"
            aria-label="Rephrase selection"
          >
            <Icon icon={'pen'} className="icon small pen" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={() => handleSelectionActionAiQuick('shorten')}
            disabled={isActiveTabReadOnly}
            data-tooltip="Shorten ~75%"
            data-tooltip-placement="top"
            aria-label="Shorten selection"
          >
            <Icon icon={'arrows-in-line-vertical'} className="icon small arrows-in-line-vertical" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={() => handleSelectionActionAiQuick('elaborate')}
            disabled={isActiveTabReadOnly}
            data-tooltip="Expand ~150%"
            data-tooltip-placement="top"
            aria-label="Expand selection"
          >
            <Icon icon={'arrows-out-line-vertical'} className="icon small arrows-out-line-vertical" />
          </button>
          <button
            type="button"
            className="iconButton small selectionActionButton"
            onClick={() => handleSelectionActionAiQuick('spelling')}
            disabled={isActiveTabReadOnly}
            data-tooltip="Spelling"
            data-tooltip-placement="top"
            aria-label="Spelling correction for selection"
          >
            <Icon icon={'seal-check'} className="icon small seal-check" />
          </button>
        </div>
      )}
      {diffSuggestionInlineBar.visible && (
        <div
          className="diffSuggestionActionBar"
          style={{ left: `${diffSuggestionInlineBar.left}px`, top: `${diffSuggestionInlineBar.top}px` }}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onMouseEnter={() => {
            diffSuggestionInlineHoverRef.current = true;
            if (diffSuggestionInlineHideTimeoutRef.current) {
              clearTimeout(diffSuggestionInlineHideTimeoutRef.current);
              diffSuggestionInlineHideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            diffSuggestionInlineHoverRef.current = false;
            if (diffSuggestionInlineHideTimeoutRef.current) {
              clearTimeout(diffSuggestionInlineHideTimeoutRef.current);
            }
            diffSuggestionInlineHideTimeoutRef.current = setTimeout(() => {
              diffSuggestionInlineHideTimeoutRef.current = null;
              if (!diffSuggestionInlineHoverRef.current) {
                hideDiffSuggestionInlineBar();
              }
            }, 180);
          }}
        >
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionKeep}
            data-tooltip="Keep"
            data-tooltip-placement="top"
            aria-label="Keep suggestion"
          >
            <Icon icon={'check'} className="icon small check" />
          </button>
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionUndo}
            data-tooltip="Undo"
            data-tooltip-placement="top"
            aria-label="Undo suggestion"
          >
            <Icon icon={'arrow-u-up-left'} className="icon small arrow-u-up-left" />
          </button>
        </div>
      )}
      {diffSuggestionDock.visible && diffSuggestionDock.entries.length > 0 && (
        <div
          className="diffSuggestionDock"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionCycleUp}
            data-tooltip="Previous suggestion"
            data-tooltip-placement="top"
            aria-label="Previous suggestion"
          >
            <Icon icon={'caret-up'} className="icon small caret-up" />
          </button>
          <div className="diffSuggestionCount" aria-live="polite">
            {`${diffSuggestionDock.currentIndex + 1} of ${diffSuggestionDock.entries.length}`}
          </div>
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionCycleDown}
            data-tooltip="Next suggestion"
            data-tooltip-placement="top"
            aria-label="Next suggestion"
          >
            <Icon icon={'caret-down'} className="icon small caret-down" />
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionKeepFromDock}
            disabled={!canResolveFocusedDockSuggestion}
            data-tooltip="Keep"
            data-tooltip-placement="top"
            aria-label="Keep focused suggestion"
          >
            <Icon icon={'check'} className="icon small check" />
          </button>
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionUndoFromDock}
            disabled={!canResolveFocusedDockSuggestion}
            data-tooltip="Undo"
            data-tooltip-placement="top"
            aria-label="Undo focused suggestion"
          >
            <Icon icon={'arrow-u-up-left'} className="icon small arrow-u-up-left" />
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionKeepAll}
            data-tooltip="Keep all"
            data-tooltip-placement="top"
            aria-label="Keep all suggestions"
          >
            <Icon icon={'checks'} className="icon small checks" />
          </button>
          <button
            type="button"
            className="iconButton small diffSuggestionActionButton"
            onClick={handleDiffSuggestionActionUndoAll}
            data-tooltip="Undo all"
            data-tooltip-placement="top"
            aria-label="Undo all suggestions"
          >
            <Icon icon={'arrow-u-up-left-all'} className="icon small arrow-u-up-left-all" />
          </button>
        </div>
      )}
    </div>
  );
});

export default EditorPanel;

